import {
  EButtonLocation,
  ECardinality,
  EStatementTypes,
  ESymbolCategory,
  EWFAssignmentType,
  EWFParamType,
  IAction,
  IActionDefinitionDto,
  IActionParameter,
  IConditionModel,
  IKTypeField,
  IProjectRole,
  ISymbol,
  IWFNodeAssignment,
  IWFParameter,
  IWFStep,
  IWorkFlow,
  IWorkFlowBaseInfo
} from "@kuika/kuika-cl-model";
import type { IInteractionParameter, IStepParameter, IWFLane } from "@kuika/kuika-cl-model/src/models/workflow";
import ELK from "elkjs/lib/elk.bundled.js";
import {
  MiMode,
  resolveMiMode
} from "../../components/design-tools/right-panel/workflow-panel/wf-utils/multi-instance-utils";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import { FormService } from "../../services/form-service";
import { ScreenService } from "../../services/screen-service";
import { WorkflowScreenService } from "../../services/workflow-screen-service";
import { WorkFlowService } from "../../services/workflow-service";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";
import { KMainFunctions } from "./main-functions";
import type { IAssignmentFailure, IAssignmentFailureSummary } from "./managed-screen-assignment-outcome";
import { classifyAssignmentFailure, summariseAssignmentFailures } from "./managed-screen-assignment-outcome";
import { findManagedScreenIdForStep } from "./managed-task-screen";
import { buildStepAction, resolveWorkflowAction } from "./workflow-action-resolver";
import {
  buildWorkflowConditionGroup,
  buildWorkflowConditionRow,
  buildWorkflowConditionRowWithOperand,
  buildWorkflowInstanceOperand
} from "./workflow-condition-symbol";
import {
  applyWorkflowFormOverrides,
  deriveTaskFormReadonlyDefault,
  describeFieldReferenceNames,
  mergeWorkflowFormOverrides,
  type WorkflowFormFieldOverride,
  type WorkflowFormLayoutOverride,
  type WorkflowFormOverrideApplication,
  type WorkflowFormOverrides,
  type WorkflowFormSectionOverride
} from "./workflow-form-metadata-overrides";
import { isBpmnModelerReady } from "./workflow-modeler-diagnostics";
import type { CatalogTypeResolution } from "./workflow-object-param-types";
import { resolveCatalogTypeName } from "./workflow-object-param-types";
import type {
  IOutputSourceAction,
  IOutputSourceField,
  OutputSourceResolution,
  OutputSourceStatementKind
} from "./workflow-output-source";
import { resolveOutputSource } from "./workflow-output-source";
import { normalizeWorkflowParamType } from "./workflow-param-types";
import type { IPhaseDeclaration, IPhaseLayoutPlan, IPhaseLayoutStep } from "./workflow-phase-layout";
import { planPhaseColumns } from "./workflow-phase-layout";
import type { CreatedScreenLike, StepAwaitingScreen, StepScreenLinkLike } from "./workflow-step-screen-mapping";
import {
  pairCreatedScreensWithSteps,
  screenNameMatchesStep,
  type StepScreenPairingEvidence
} from "./workflow-step-screen-mapping";
import {
  buildActionResultSymbol,
  buildFixedValueSymbol,
  buildPlatformDefaultSymbol,
  buildProcessInstanceSymbol,
  buildWorkflowParamRefSymbol
} from "./workflow-symbol-factory";

/** Runtime workflow objects may carry a legacy `workFlowName` field not present in the model. */
type IWorkFlowWithLegacy = IWorkFlow & { workFlowName?: string };

/** Result of ensuring the mandatory Start/End events exist (KOA-17362). */
export interface IEnsureStartEndResult {
  startId?: string;
  endId?: string;
  createdStart: boolean;
  createdEnd: boolean;
}

export interface IStepSummary {
  id: string;
  name: string;
  actionCount: number;
  type?: string;
  sourceId?: string;
  targetId?: string;
  /**
   * KOA-17362 §25 — the platform-managed task screen this step owns, when one is
   * assigned (`project.processStepScreenIds`). Present only for a step that HAS
   * one, so its absence is the honest "no screen yet".
   *
   * Two consumers: the orchestrator's `ASSIGN_SCREEN` backstop verifies its own
   * outcome against this instead of trusting a dispatch reply that may have timed
   * out mid-flight, and the LLM can see which tasks already have a screen instead
   * of re-running a heavy assignment to find out.
   */
  screenId?: string;
  screenName?: string;
  /**
   * KOA-18136 §49 — the parameters this step's DECISION BUTTONS assign, by name.
   *
   * Without it, "a task output no button sets" can only be computed from the SCRIPT of the
   * current turn, so a turn that edits an existing process reports a defect on a parameter a
   * button already assigns — a button the user may have added by hand in the designer. The
   * buttons are the model's own answer to "who writes this", and it was the one thing the
   * inventory did not expose. Present (possibly empty) for every step that has interactions.
   */
  interactionSets?: string[];
  /**
   * KOA-18136 §72 — the actions attached to this step, by the name the DSL refers to them by,
   * each with its mapped and unmapped inputs. Present (non-empty) only for a step that HAS an
   * action, so its absence is the honest "nothing attached" — `actionCount` alone could never say
   * WHICH action, which is what a report has to name.
   */
  actions?: IStepActionSummary[];
  /**
   * KOA-18593 — who gets a USER task, as the platform will assign it: the assignment type, the role
   * on a role assignment, and on a dynamic one the action that returns the assignees with its bound
   * and unbound inputs. Present on every user task, so "this task is assigned to nobody the spec
   * named" is answerable from the process as it stands and not only from this turn's script.
   */
  assignment?: IStepAssignmentSummary;
  /**
   * KOA-18136 §50 — the workflow parameters this step MAPS AS OUTPUTS, by name.
   *
   * `MAP_OUTPUT` is the only thing that produces a parameter (other than a process input), so
   * without this the question "does anything in this process produce the value a gateway tests?"
   * can only be answered from the current turn's script — and a gateway whose parameter nothing
   * produces has a branch that can never be taken. Present (possibly empty) for every step.
   */
  outputParameterNames?: string[];
  /**
   * KOA-18136 — whether that screen's NAME is the one the platform generates from
   * THIS step's name.
   *
   * The link itself proves membership of the process, not identity: it is written by a
   * pairing that falls back to position when a name cannot be derived, and live session
   * 379e6653 shipped a whole rotated set. Anything that acts on the STEP's behalf — the
   * requirement a task screen is built from — needs identity, and this is the evidence
   * for it. `false` never means "wrong", it means "not shown"; a caller that cannot act
   * on an unproven pair must say so instead of proceeding.
   */
  screenPairedByName?: boolean;
  /**
   * KOA-18593 — the labels of a user task's decision buttons, so a script that stopped part-way can
   * be continued from what the process already holds: the buttons standing are not sent again.
   */
  interactionLabels?: string[];
  /**
   * KOA-18593 — on a path leaving a gateway: whether it is the gateway's default path, and its
   * condition as the script wrote it (`StockResult == "Yeterli Stok"`). A path's condition and its
   * default flag are part of what a stopped script already built, and the only place the author can
   * read them back is here.
   */
  isDefault?: boolean;
  condition?: string;
  /** KOA-18593 — on a lane: the role it is assigned to, when it has one. */
  role?: ILaneRoleSummary;
}

/** A task's stored button, as far as LIST reads it. */
interface ILabelledInteraction {
  label?: unknown;
}

/** A path's stored decision entry, as far as LIST reads it. */
interface IStoredPathDecision {
  id?: string;
  condition?: IConditionModel | null;
}

/** A lane's role, as the script names it (`role:<id> roleName:"<name>"`). */
export interface ILaneRoleSummary {
  id: string;
  name: string;
}

/** The stored operator names, back in the DSL's own spelling. */
const DSL_CONDITION_OPERATORS: Record<string, string> = {
  isEqualTo: "==",
  isNotEqualTo: "!=",
  isGreaterThan: ">",
  isGreaterOrEqual: ">=",
  isLessThan: "<",
  isLessOrEqual: "<="
};

/** One stored condition row as `<parameter> <operator> "<value>"`, or undefined when it names nothing. */
function describeConditionRow(row: IConditionModel | undefined): string | undefined {
  const left: string = String(row?.field1?.description ?? row?.field1?.label ?? "").trim();
  if (left.length === 0) return undefined;
  const operatorName: string = String(row?.conditionOperator ?? "").trim();
  const operator: string = DSL_CONDITION_OPERATORS[operatorName] ?? operatorName;
  const fixed: unknown = row?.field2?.fixedValue ?? row?.field2?.description;
  const right: string = fixed === undefined || fixed === null ? "" : ` "${String(fixed)}"`;
  return `${left} ${operator}${right}`;
}

/** A path's stored condition, joined the way the script joins it, or undefined when it has none. */
export function describeStoredPathCondition(condition: IConditionModel | null | undefined): string | undefined {
  const rows: IConditionModel[] = condition?.conditionSet?.conditions ?? [];
  const described: string[] = rows
    .map((row: IConditionModel): string | undefined => describeConditionRow(row))
    .filter((text: string | undefined): text is string => text !== undefined);
  if (described.length === 0) return undefined;
  return described.join(condition?.isOrGroup === true ? " OR " : " AND ");
}

export interface IWfParamSummary {
  id: string;
  name: string;
  dataType: string;
  parameterType?: number;
  isInput: boolean;
  isOutput: boolean;
  isBusinessObject: boolean;
  defaultValue: string | number | boolean | null;
}

interface LaneEdgeRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface LaneRowNode {
  id: string;
  x: number;
  width: number;
  lane: number;
}

/**
 * Pool body height for a swimlane auto-layout. In MULTI-LANE mode every node's Y
 * is PINNED to its lane band (ELK's vertical spread is discarded — see the
 * node-move step), so the pool body must EXACTLY tile the summed lane bands;
 * using the ELK-derived height when it is larger leaves a dead empty strip below
 * the last lane (KOA-17362: ELK pool 541 vs 2×220-band span 440 → ~101px empty
 * band the user sees under "Müşteri Hizmetleri"). In SINGLE/no-lane mode the one
 * band spans the ELK-sized pool, so the ELK height is correct (and equals the
 * band span there anyway). Pure + side-effect-free for unit testing.
 */
export function poolBodyHeight(multiLane: boolean, laneBandSpan: number, elkPoolHeight: number): number {
  return multiLane ? laneBandSpan : elkPoolHeight;
}

interface Point2DLite {
  x: number;
  y: number;
}

/**
 * The point at HALF the arc-length of an edge polyline — the natural home for a
 * sequence-flow label. Auto-layout re-routes edges but `updateWaypoints` does
 * NOT move the edge's external label shape, so a labeled flow (e.g. a gateway's
 * "Diğer Durumlar" default path) keeps its STALE label position, which can land
 * on top of a node (KOA-17362: the gateway→END default label overlapped the END
 * event). Centering the label here keeps it on its own path and off the
 * endpoints. Pure + side-effect-free for unit testing.
 */
export function polylineMidpoint(points: readonly Point2DLite[]): Point2DLite {
  if (points.length === 0) return { x: 0, y: 0 };
  if (points.length === 1) return { x: points[0].x, y: points[0].y };
  const segLen: number[] = [];
  let total = 0;
  for (let i = 0; i + 1 < points.length; i++) {
    const d: number = Math.hypot(points[i + 1].x - points[i].x, points[i + 1].y - points[i].y);
    segLen.push(d);
    total += d;
  }
  if (total === 0) return { x: points[0].x, y: points[0].y };
  let half: number = total / 2;
  for (let i = 0; i < segLen.length; i++) {
    if (half <= segLen[i]) {
      const t: number = segLen[i] === 0 ? 0 : half / segLen[i];
      return {
        x: points[i].x + (points[i + 1].x - points[i].x) * t,
        y: points[i].y + (points[i + 1].y - points[i].y) * t
      };
    }
    half -= segLen[i];
  }
  const last = points[points.length - 1];
  return { x: last.x, y: last.y };
}

/**
 * Resolve horizontal collisions WITHIN each lane row of a swimlane auto-layout.
 * After ELK lays the flow out (X = flow order) we pin every node's Y to its
 * lane band — so two same-lane nodes ELK placed in the SAME layer (≈ same X,
 * different ELK Y) would land on the SAME (x,y) and overlap (KOA-17362: İK lane
 * had 8 nodes and several stacked invisibly). This spreads same-lane nodes left
 * to right so their boxes never overlap: per lane, sort by x, and push any node
 * that starts before the previous node's right edge + gap. Nodes in DIFFERENT
 * lanes never collide (distinct band Y). Returns the adjusted X per node id;
 * lane membership and Y are untouched. Exported for testing.
 */
export function resolveLaneRowCollisions(nodes: readonly LaneRowNode[], gap: number): Map<string, number> {
  const result: Map<string, number> = new Map<string, number>();
  const byLane: Map<number, LaneRowNode[]> = new Map<number, LaneRowNode[]>();
  for (const n of nodes) {
    const arr: LaneRowNode[] = byLane.get(n.lane) ?? [];
    arr.push(n);
    byLane.set(n.lane, arr);
  }
  for (const laneNodes of byLane.values()) {
    const sorted: LaneRowNode[] = [...laneNodes].sort((a, b) => a.x - b.x || a.id.localeCompare(b.id));
    let prevRight: number = -Infinity;
    for (const n of sorted) {
      const x: number = n.x < prevRight + gap ? prevRight + gap : n.x;
      result.set(n.id, x);
      prevRight = x + n.width;
    }
  }
  return result;
}

/**
 * Orthogonal route for a FORWARD lane edge in a swimlane auto-layout: exit the
 * source's right edge, run to the midpoint X, drop/rise vertically, then enter
 * the target's left edge. Returns null for a back-edge (target not to the right
 * of the source) so the caller can fall back to bpmn's layoutConnection. This
 * avoids the absurd around-the-diagram detours bpmn produces for long
 * cross-lane edges (KOA-17362). Exported for testing.
 */
export function computeForwardLaneEdgeWaypoints(
  src: LaneEdgeRect,
  tgt: LaneEdgeRect
): { x: number; y: number }[] | null {
  const sx: number = src.x + src.width;
  const sy: number = src.y + src.height / 2;
  const tx: number = tgt.x;
  const ty: number = tgt.y + tgt.height / 2;
  if (tx < sx - 1) return null; // back-edge / loop — let bpmn route it
  if (Math.abs(sy - ty) < 1) {
    return [
      { x: sx, y: sy },
      { x: tx, y: ty }
    ];
  }
  const midX: number = (sx + tx) / 2;
  return [
    { x: sx, y: sy },
    { x: midX, y: sy },
    { x: midX, y: ty },
    { x: tx, y: ty }
  ];
}

interface Point2D {
  x: number;
  y: number;
}

/**
 * Liang–Barsky segment-vs-AABB intersection test. Returns true if the open
 * segment p1→p2 passes through the rectangle's interior (the rect deflated by
 * `inset` on every side). A segment that only grazes the boundary (or runs
 * exactly along an edge) returns false. Used by the auto-layout
 * instrumentation to count sequence-flow polylines that cross a node box
 * (`[LAYOUT-EDGE-OVERLAP]`) so rule 3 — no arrow crosses any element
 * (KOA-17362) — can be measured deterministically rather than eyeballed off a
 * screenshot. Exported for testing.
 */
export function segmentIntersectsRect(p1: Point2D, p2: Point2D, rect: LaneEdgeRect, inset: number = 0): boolean {
  const xmin: number = rect.x + inset;
  const ymin: number = rect.y + inset;
  const xmax: number = rect.x + rect.width - inset;
  const ymax: number = rect.y + rect.height - inset;
  if (xmin >= xmax || ymin >= ymax) return false;
  const dx: number = p2.x - p1.x;
  const dy: number = p2.y - p1.y;
  let t0: number = 0;
  let t1: number = 1;
  const p: number[] = [-dx, dx, -dy, dy];
  const q: number[] = [p1.x - xmin, xmax - p1.x, p1.y - ymin, ymax - p1.y];
  for (let i = 0; i < 4; i++) {
    if (p[i] === 0) {
      // Segment parallel to this boundary — wholly outside the slab if q < 0.
      if (q[i] < 0) return false;
    } else {
      const r: number = q[i] / p[i];
      if (p[i] < 0) {
        if (r > t1) return false;
        if (r > t0) t0 = r;
      } else {
        if (r < t0) return false;
        if (r < t1) t1 = r;
      }
    }
  }
  return t0 < t1;
}

/** Drop consecutive duplicate points (within 0.5px) from a polyline. Pure. */
function dedupePoints(pts: readonly Point2D[]): Point2D[] {
  const out: Point2D[] = [];
  for (const p of pts) {
    const last: Point2D | undefined = out[out.length - 1];
    if (!last || Math.abs(last.x - p.x) > 0.5 || Math.abs(last.y - p.y) > 0.5) out.push({ x: p.x, y: p.y });
  }
  return out;
}

/** True if any segment of the polyline passes through any obstacle box. Pure. */
function polylineCrossesAny(pts: readonly Point2D[], obstacles: readonly LaneEdgeRect[], inset: number): boolean {
  for (let i = 0; i + 1 < pts.length; i++) {
    for (const ob of obstacles) {
      if (segmentIntersectsRect(pts[i], pts[i + 1], ob, inset)) return true;
    }
  }
  return false;
}

/**
 * Correct-by-construction orthogonal router for a FORWARD swimlane edge
 * (KOA-17362). The cramped 120px bands of the old layout left no room to route
 * around a node, so `computeForwardLaneEdgeWaypoints` cut straight through them
 * at node-centre Y. With the bands grown to leave a clear corridor between node
 * rows and a clear gap between columns, an edge can be routed entirely through
 * empty space: exit the source's RIGHT edge → short stub to a clear vertical
 * channel (`exitX`, in the column gap right of the source) → run vertically to
 * a clear horizontal corridor (`corridorY`, an inter-band gap) → run
 * horizontally clear of every node → drop into a clear vertical channel left of
 * the target (`entryX`) → enter the target's LEFT edge.
 *
 * The caller supplies CANDIDATE channels (corridor Ys ordered by proximity to
 * the edge midline; exit/entry Xs staggered per edge so siblings don't share a
 * line). This function tries each combination and returns the FIRST polyline
 * that crosses no obstacle — so a returned route is GUARANTEED node-clean.
 * Handles BOTH forward edges and back-edges/loops (target left of source): it
 * always exits the source's RIGHT edge into a clear column gap, runs the long
 * leg along a clear corridor in EITHER direction, and docks into the target's
 * LEFT edge — so a loop simply runs its corridor leg right-to-left. Returns
 * null only when no clean combination exists, so the caller can fall back to
 * bpmn's layoutConnection. Exported for testing.
 */
export function routeChannelEdge(
  src: LaneEdgeRect,
  tgt: LaneEdgeRect,
  obstacles: readonly LaneEdgeRect[],
  corridorYs: readonly number[],
  exitXs: readonly number[],
  entryXs: readonly number[]
): Point2D[] | null {
  const sx: number = src.x + src.width;
  const sy: number = src.y + src.height / 2;
  const tx: number = tgt.x;
  const ty: number = tgt.y + tgt.height / 2;
  const INSET: number = 2;
  // Clean same-row case: a straight shot with nothing in the way (forward only;
  // a backward straight line at the node row would cut through it).
  if (Math.abs(sy - ty) < 1 && tx > sx) {
    const straight: Point2D[] = [
      { x: sx, y: sy },
      { x: tx, y: ty }
    ];
    if (!polylineCrossesAny(straight, obstacles, INSET)) return straight;
  }
  // General channel route: try candidates, return the first node-clean one.
  // The corridor leg may run left→right (forward edge) or right→left (back-edge
  // / loop); the obstacle check guarantees whichever it picks is node-clean.
  for (const corridorY of corridorYs) {
    for (const exitX of exitXs) {
      if (exitX <= sx) continue; // stub must leave the source rightward
      for (const entryX of entryXs) {
        if (entryX >= tx) continue; // dock just left of the target's left edge
        const wp: Point2D[] = [
          { x: sx, y: sy },
          { x: exitX, y: sy },
          { x: exitX, y: corridorY },
          { x: entryX, y: corridorY },
          { x: entryX, y: ty },
          { x: tx, y: ty }
        ];
        const clean: Point2D[] = dedupePoints(wp);
        if (clean.length >= 2 && !polylineCrossesAny(clean, obstacles, INSET)) return clean;
      }
    }
  }
  return null;
}

/**
 * Decide what to write for a multi-instance step's instance count when the MI
 * command runs. Returns the number to write, or null to PRESERVE the existing
 * value. A mode-only `MI <ref> PMI` (no count:) must not reset a manually-set
 * count (KOA-17362): write the provided count when given; otherwise seed a
 * default of 1 only if the step has no count yet; otherwise keep the existing
 * one. Exported for testing.
 */
export function resolveMiInstanceCountWrite(
  providedCount: number | null | undefined,
  hasExistingCount: boolean
): number | null {
  if (providedCount !== undefined && providedCount !== null) return providedCount;
  if (!hasExistingCount) return 1;
  return null;
}

/** Pull a human-readable fixed default out of a parameter's symbol for LIST output. */
function summarizeParamDefault(sym?: ISymbol): string | number | boolean | null {
  if (!sym) return null;
  const fixed: unknown = sym.fixedValue;
  if (typeof fixed === "string" || typeof fixed === "number" || typeof fixed === "boolean") return fixed;
  if (typeof sym.description === "string" && sym.description.length > 0) return sym.description;
  return null;
}

interface IListStepsInput {
  workflowId?: string;
}

interface ISelectStepInput {
  stepId?: string;
  workflowId?: string;
}

function getWorkflows(sd: IScreenDesignContextState): IWorkFlowBaseInfo[] {
  return sd.workFlowList || [];
}

function getBpmnModules() {
  const modeler = (window as any).bpmnModeler;
  if (!modeler || typeof modeler.get !== "function") {
    throw new Error("BPMN Modeler is not ready or not found.");
  }
  return {
    modeling: modeler.get("modeling"),
    elementRegistry: modeler.get("elementRegistry"),
    elementFactory: modeler.get("elementFactory"),
    bpmnFactory: modeler.get("bpmnFactory"),
    autoPlace: modeler.get("autoPlace", false)
  };
}

function getWp(sdInstance: any): any {
  return sdInstance?.workflowProvider || (window as any).getSdInstance?.()?.workflowProvider;
}

/**
 * Depth of the enclosing DSL batch — who owns persistence right now (KOA-18136).
 *
 * These handlers have TWO callers with opposite persistence needs:
 *   - a STANDALONE MCP tool (`ui_workflow_create_internal_parameter`, …) — one mutation,
 *     one turn; nothing else will ever save it, so the handler must persist itself.
 *   - the DSL EXECUTOR — up to a hundred mutations in one script. A per-handler save
 *     there means a hundred overlapping whole-blob PUTs of the SAME KWorkFlow row, and
 *     since KOA-18100 added `[Timestamp] RowVersion` those collide into 409
 *     (`KConflictException`) instead of silently last-write-wins. The 409 storm is what
 *     collapsed the tail of every process build.
 *
 * A counter, not a boolean: nested/re-entrant batches must not clear the flag early.
 */
let dslBatchDepth: number = 0;

/** The only provider capability the persist gate needs. */
interface IWorkflowSaver {
  saveWorkFlow?: (wf: unknown, changeReason?: number) => Promise<boolean> | boolean;
}

/** Executor-only: take ownership of persistence for the duration of one script. */
export function beginWorkflowPersistBatch(): void {
  dslBatchDepth++;
}

/** Executor-only: hand persistence back to the handlers. */
export function endWorkflowPersistBatch(): void {
  if (dslBatchDepth > 0) dslBatchDepth--;
}

/**
 * Persist unless a DSL batch owns it. Standalone tool calls keep their existing
 * fire-and-forget save byte-for-byte; inside a script this is a no-op and the executor's
 * two deliberate persist points (before ASSIGN_SCREEN, after the script) do the writing.
 */
function persistUnlessBatched(wp: IWorkflowSaver | undefined, wf: unknown, changeReason?: number): void {
  if (dslBatchDepth > 0) return;
  if (!wp || typeof wp.saveWorkFlow !== "function" || !wf) return;
  wp.saveWorkFlow(wf, changeReason);
}

/** BPMN element type of a node (`bpmn:UserTask`, `bpmn:Task`, …), or undefined. */
function bpmnElementTypeOf(nodeId: string): string | undefined {
  try {
    const { elementRegistry } = getBpmnModules();
    const element = elementRegistry?.get(nodeId) as { type?: string } | undefined;
    return typeof element?.type === "string" ? element.type : undefined;
  } catch {
    // Modeler absent. Every graph command already aborts atomically before this
    // point (see `waitForBpmnModelerReady`), so this is not a normal path.
    return undefined;
  }
}

/** True when the node is a HUMAN step (`bpmn:UserTask`), per the live diagram. */
function isUserTaskNode(nodeId: string): boolean {
  return bpmnElementTypeOf(nodeId) === "bpmn:UserTask";
}

/**
 * KOA-17362 — the refusal a `STEP_ACTION` aimed at a user task gets.
 *
 * A user task and a system step are DIFFERENT hosts in the generated code, and the
 * difference is decided purely by the node type: the generator picks the
 * screen-interaction template for a user task and the plain step template for a
 * system step. On a user task the attached actions run as PRE-actions (before the
 * task waits for the human) and — decisively — the block that maps an action's
 * RESULT back into workflow parameters is emitted as EMPTY for that template. So an
 * action placed here can never hand anything to the rest of the process.
 *
 * Accepting the write anyway is what made live smoke 13 unfixable from the outside:
 * the DSL reported `Action 'CreateReturnRequest' added to system step natively`, LIST
 * reported `actionCount: 1`, four MAP_INPUTs reported success, and the row's id was
 * nonetheless discarded — so every later step acted on a record it could not find,
 * while every layer said the build was clean.
 */
function userTaskActionRefusal(actionLabel: string, nodeId: string, stepName: string): string {
  return (
    `Cannot attach '${actionLabel}' as this step's action: "${stepName}" (${nodeId}) is a USER TASK ` +
    `(bpmn:UserTask), not a system step. A user task waits for a person; an action attached to it runs ` +
    `as a PRE-action and its RESULT is discarded — the generated code emits no output mapping for a user ` +
    `task, so nothing this action returns can reach the rest of the process (a MAP_OUTPUT ... FROM on it ` +
    `would silently produce nothing).\n\n` +
    `If this step should run AUTOMATICALLY, make it a system step instead:\n` +
    `  DELETE ${nodeId}\n` +
    `  SYSTEM @step "${stepName}" after:<previous node>\n` +
    `  STEP_ACTION @step "${actionLabel}"\n` +
    `If you genuinely want pre-logic to run before the person opens this task, use ` +
    `PRE_ACTION ${nodeId} "${actionLabel}" instead — and do not MAP_OUTPUT its result.`
  );
}

/**
 * Returns the id of an existing task/system step with the EXACT same name, if
 * any. Used to block duplicate TASK/SYSTEM creates from DSL scripts — bpmn.js
 * would otherwise silently auto-suffix (" 1", " 2") leaving the original
 * untouched and creating orphan duplicates when the LLM thinks it's replacing.
 */
export function findExistingTaskByName(name: string | undefined): { id: string; type: string; name: string } | null {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return null;
  try {
    const { elementRegistry } = getBpmnModules();
    if (!elementRegistry) return null;
    const matches = elementRegistry.filter((e: any) => {
      if (e.type !== "bpmn:UserTask" && e.type !== "bpmn:Task") return false;
      const n = e.businessObject?.name || "";
      return n === trimmed;
    });
    if (!matches.length) return null;
    const m = matches[0];
    return { id: m.id, type: m.type, name: m.businessObject?.name || "" };
  } catch {
    return null;
  }
}

/**
 * Returns the id of an existing gateway with the EXACT same name AND bpmn type,
 * if any. Used to REUSE (not duplicate) a named gateway when a DSL script is
 * re-run — e.g. an agent that re-submits the whole CREATE script after a
 * partial failure would otherwise spawn a second "Karar?" gateway each time
 * (TASK has a duplicate guard but gateways did not), piling up orphan gateways
 * (KOA-17362). Only NAMED gateways are matched — an unnamed/auto-named gateway
 * (`input.name` falsy) is always created fresh, since two distinct decision
 * points legitimately share the generic "Exclusive" placeholder.
 */
export function findExistingGatewayByName(
  name: string | undefined,
  bpmnType: string
): { id: string; type: string; name: string } | null {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return null;
  try {
    const { elementRegistry } = getBpmnModules();
    if (!elementRegistry) return null;
    const matches = elementRegistry.filter((e: any) => {
      if (e.type !== bpmnType) return false;
      const n = e.businessObject?.name || "";
      return n === trimmed;
    });
    if (!matches.length) return null;
    const m = matches[0];
    return { id: m.id, type: m.type, name: m.businessObject?.name || "" };
  } catch {
    return null;
  }
}

/**
 * Collect the workflow's parameters from a UNION of every live reference —
 * `getValues(sd).selectedWf`, the provider's `getSelectedWf()`, and the window
 * instance's provider — deduped by name. `forceSyncAndFindNode` (called by the
 * step handlers) can swap `sd.selectedWf` for the provider's not-yet-saved copy,
 * so a single-ref `sd.selectedWf.parameters` read misses a PARAM created earlier
 * in the SAME script. The union is stable within a script, so a same-script
 * parameter resolves reliably. Shared by CONDITION/PATH-WHEN, MAP_INPUT, and
 * MAP_OUTPUT — all of which must see a just-created PARAM.
 */
function collectWorkflowParamsUnion(sdInstance: any, sd: any): any[] {
  const byName = new Map<string, any>();
  const addFrom = (wfRef: any): void => {
    const list = wfRef?.parameters;
    if (!Array.isArray(list)) return;
    for (const p of list) {
      if (p && typeof p.name === "string" && !byName.has(p.name)) byName.set(p.name, p);
    }
  };
  addFrom(sd?.selectedWf);
  try {
    const wp = getWp(sdInstance);
    addFrom(wp?.getSelectedWf ? wp.getSelectedWf() : null);
  } catch {
    /* ignore */
  }
  try {
    addFrom((window as any).getSdInstance?.()?.workflowProvider?.getSelectedWf?.());
  } catch {
    /* ignore */
  }
  return [...byName.values()];
}

/**
 * KOA-18136 §37 — the reconcile a LOOKUP or a MUTATION is allowed to run.
 *
 * Every caller in this file is reading the model or has just changed the canvas; none
 * of them persists. What they need is the ADDITIVE direction — bring in what the canvas
 * has and the model lacks — and nothing more. The full reconcile also DELETES model
 * nodes the current canvas read does not show, which on a canvas that cannot be read
 * (mid-teardown, unmounted, a foreign view) meant a lookup deleting the very step it
 * was looking for; live session be14b8f5 persisted 8 of 14 created steps that way while
 * the commands failed "Step not found." / "Task not found.".
 *
 * Subtraction stays where it belongs: the deliberate save points, against a canvas that
 * was actually read (see the provider's readability gate). There is deliberately NO
 * fallback to the subtractive variant — a provider without the adopt-only entry point
 * gets no sync rather than a destructive one.
 */
function adoptCanvasIntoModel(wp: any): void {
  if (!wp) return;
  try {
    if (typeof wp.adoptDiagramNodesIntoWorkflow === "function") wp.adoptDiagramNodesIntoWorkflow();
  } catch {
    /* a lookup must never fail on its own bookkeeping */
  }
}

/**
 * Sync the side-car from the live canvas, then find a node by id.
 *
 * The reconcile is `adoptCanvasIntoModel` — the ADOPT-ONLY variant, and neither of the
 * two variants that write (KOA-18136 §37). This helper runs on nearly every DSL command
 * — MAP_INPUT, MAP_OUTPUT, STEP_ACTION, INTERACTION, PHASE_ASSIGN, lane resolution — so
 * whatever it does happens dozens of times per script:
 *
 *   • `manualSyncWorkflow` would fire an un-awaited `saveWorkFlow` per lookup: DOZENS of
 *     overlapping whole-blob PUTs of the SAME KWorkFlow row inside one script;
 *   • `quickSyncBeforeSave` would run the SUBTRACTIVE half per lookup: a lookup deleting
 *     the very node it is looking for whenever the canvas read is not trustworthy.
 *
 * That was always wasteful; KOA-18100 made it fatal. `KWorkFlow` gained a
 * `[Timestamp] RowVersion`, so `WorkFlowService.Update` now answers a stale-token
 * write with `KConflictException` (409) instead of silently last-write-wins. The
 * self-inflicted write storm therefore became a 409 storm mid-script: saves start
 * failing while `syncWorkflowWithDiagram` keeps rewriting `selectedWf.nodes`, the
 * canvas and side-car diverge, and the tail of the script collapses ("Step not
 * found." / "Task not found." / "BPMN Modeler is not ready" for LINT + LAYOUT).
 *
 * A lookup must not write. Persistence is now explicit and deliberate (see the
 * executor's persist points), not a side effect of reading. `manualSyncWorkflow`
 * keeps its sync+save semantics untouched for any manual/UI caller.
 */
function forceSyncAndFindNode(sdInstance: any, nodeId: string): any {
  const sd = getValues(sdInstance) as any;
  const wp = getWp(sdInstance);
  const workflow = sd.selectedWf;

  if (wp) {
    try {
      if (typeof wp.processPendingChanges === "function") wp.processPendingChanges();
      adoptCanvasIntoModel(wp);
    } catch (e) {}
  }

  if (wp && typeof wp.findNodeRecursively === "function") {
    for (let i = 1; i <= 5; i++) {
      const found = wp.findNodeRecursively(nodeId, i, workflow);
      if (found) return found;
    }
  }

  let found: any = null;
  const searchInNodes = (nodes: any) => {
    if (!nodes) return;
    if (nodes.steps) found = found || nodes.steps.find((s: any) => s.id === nodeId);
    if (nodes.events) found = found || nodes.events.find((e: any) => e.id === nodeId);
    if (nodes.gateways) found = found || nodes.gateways.find((g: any) => g.id === nodeId);
    if (nodes.decisions) found = found || nodes.decisions.find((d: any) => d.id === nodeId);
    if (nodes.forwards) found = found || nodes.forwards.find((f: any) => f.id === nodeId);
    if (!found && nodes.steps) {
      for (const step of nodes.steps) {
        if (step.subprocess?.steps) searchInNodes(step.subprocess);
      }
    }
  };
  searchInNodes(workflow.nodes);
  return found;
}

/**
 * KOA-18136 §50 — the parameter NAMES a step maps as its outputs.
 *
 * `MAP_OUTPUT` stores the parameter's ID, so the names come from the workflow's parameter list.
 * Returns `undefined` for a step that maps none — its ABSENCE means "this step produces nothing"
 * while `[]` would mean "it has output entries that resolve to no parameter", and only the
 * second is a defect worth seeing.
 *
 * Pure and exported for testing: the inventory itself reads the live bpmn registry.
 */
export function outputParameterNamesForStep(
  step: { outputParameters?: { id?: string }[] } | undefined | null,
  parameters: IWFParameter[] | undefined
): string[] | undefined {
  const outputs = step?.outputParameters;
  if (!Array.isArray(outputs) || outputs.length === 0) return undefined;
  const nameByParamId: Map<string, string> = new Map<string, string>();
  for (const parameter of parameters || []) {
    if (parameter && parameter.id && parameter.name) nameByParamId.set(String(parameter.id), String(parameter.name));
  }
  const produced: Set<string> = new Set<string>();
  for (const output of outputs) {
    const name: string | undefined = output?.id ? nameByParamId.get(String(output.id)) : undefined;
    if (name !== undefined) produced.add(name);
  }
  return [...produced];
}

/**
 * KOA-18136 §49 — the parameter NAMES a step's decision buttons assign.
 *
 * An interaction stores the parameter's ID, so the names come from the workflow's parameter
 * list. Returns `undefined` for a step with no interactions — the caller leaves the field off
 * entirely, so its ABSENCE means "this step has no buttons" while `[]` would mean "buttons that
 * assign nothing", and those are different answers to "who writes this output".
 *
 * Pure and exported for testing: the inventory itself reads the live bpmn registry.
 */
export function interactionSetNamesForStep(
  step: { userInteractions?: { parameters?: { id?: string }[] }[] } | undefined | null,
  parameters: IWFParameter[] | undefined
): string[] | undefined {
  const interactions = step?.userInteractions;
  if (!Array.isArray(interactions) || interactions.length === 0) return undefined;
  const nameByParamId: Map<string, string> = new Map<string, string>();
  for (const parameter of parameters || []) {
    if (parameter && parameter.id && parameter.name) nameByParamId.set(String(parameter.id), String(parameter.name));
  }
  const assigned: Set<string> = new Set<string>();
  for (const interaction of interactions) {
    for (const parameter of interaction?.parameters || []) {
      const name: string | undefined = parameter?.id ? nameByParamId.get(String(parameter.id)) : undefined;
      if (name !== undefined) assigned.add(name);
    }
  }
  return [...assigned];
}

/**
 * KOA-18136 §72 — WHICH action a step runs, and which of its inputs are mapped.
 *
 * The inventory used to answer "how many" (`actionCount`) and nothing else, so the two questions
 * every report actually asks — *which* action does this step run, and does that action still have
 * an empty input — were answerable ONLY from the script of the current turn. Live `de9283fa` is
 * what that costs: the 163-line script's answer never came back (the transport gave up at the
 * heavy-tool ceiling while the browser applied every line), and with the script's per-line answer
 * gone there was no second way to learn that six freshly created actions were attached to steps.
 * The turn reported all of them as "used by nothing".
 *
 * The step object already holds it all: each attached action carries its definition id and its
 * input parameters, and an input is MAPPED exactly when it carries a symbol (what `MAP_INPUT`
 * writes). So this is the same move §49 and §50 already made twice — expose what the model
 * already knows, so a fresh listing is a full substitute for a lost answer rather than a headcount.
 *
 * `name` is the definition's own name because that is the string the DSL references
 * (`STEP_ACTION @step "CreateX"`); the label is the fallback for an action whose definition is not
 * in this snapshot. Pure and exported for testing.
 */
/** KOA-18593 — a user task's assignment, as LIST reports it (see `IStepSummary.assignment`). */
export interface IStepAssignmentSummary {
  /** `Role` / `Everyone` / `Dynamic` / `External` / `None`. */
  type: string;
  /** Role assignment: the role's name. */
  role?: string;
  /** Dynamic assignment: the action that returns who gets the task, with its inputs. */
  action?: IStepActionSummary;
  /** Dynamic assignment: what its rows name — `User` or `Role`. */
  returns?: string;
}

const ASSIGNMENT_TYPE_NAMES: ReadonlyMap<EWFAssignmentType, string> = new Map<EWFAssignmentType, string>([
  [EWFAssignmentType.none, "None"],
  [EWFAssignmentType.role, "Role"],
  [EWFAssignmentType.everyone, "Everyone"],
  [EWFAssignmentType.dynamic, "Dynamic"],
  [EWFAssignmentType.external, "External"]
]);

/** KOA-18593 — a user task's assignment for LIST, or `undefined` when the step carries none. */
export function stepAssignmentSummary(
  step: IWFStep | undefined | null,
  actionDefinitions: IActionDefinitionDto[] | undefined
): IStepAssignmentSummary | undefined {
  const assignment: IWFNodeAssignment | undefined = step?.assignment;
  if (assignment === undefined || assignment.assignmentType === undefined) return undefined;
  const summary: IStepAssignmentSummary = { type: ASSIGNMENT_TYPE_NAMES.get(assignment.assignmentType) ?? "None" };
  const role: string = String(assignment.roleAssignmentParameter?.name ?? "").trim();
  if (assignment.assignmentType === EWFAssignmentType.role && role.length > 0) summary.role = role;
  if (assignment.assignmentType === EWFAssignmentType.dynamic) {
    const action: IStepActionSummary[] | undefined = stepActionSummaries(
      { actions: assignment.dynamicAssignmentAction ? [assignment.dynamicAssignmentAction] : [] },
      actionDefinitions
    );
    if (action !== undefined) summary.action = action[0];
    summary.returns = assignment.dynamicAssignmentActionReturnType ?? "User";
  }
  return summary;
}

export interface IStepActionSummary {
  /** The name `STEP_ACTION` refers to this action by. */
  name: string;
  /** Input parameters carrying a bound source. */
  mappedInputs: string[];
  /** Input parameters with no source — each one ships EMPTY at runtime. */
  unmappedInputs: string[];
}

export function stepActionSummaries(
  step: { actions?: unknown } | undefined | null,
  actionDefinitions: IActionDefinitionDto[] | undefined
): IStepActionSummary[] | undefined {
  const attached: any[] = Array.isArray(step?.actions) ? (step!.actions as any[]) : [];
  if (attached.length === 0) return undefined;
  const defs: IActionDefinitionDto[] = Array.isArray(actionDefinitions) ? actionDefinitions : [];
  const out: IStepActionSummary[] = [];
  for (const attachedAction of attached) {
    const definitionId: string = String(attachedAction?.actionDefinitionId ?? "");
    const def: IActionDefinitionDto | undefined = defs.find(
      (d: IActionDefinitionDto): boolean => d.id === definitionId
    );
    const name: string = String(def?.name || attachedAction?.label || def?.displayName || "").trim();
    if (name.length === 0) continue;
    const params: any[] = Array.isArray(attachedAction?.parameters) ? attachedAction.parameters : [];
    const mappedInputs: string[] = [];
    const unmappedInputs: string[] = [];
    for (const param of params) {
      const paramName: string = String(param?.name ?? "").trim();
      if (paramName.length === 0) continue;
      const bound: boolean = param?.symbol !== undefined && param?.symbol !== null && param?.symbol !== "";
      if (bound) mappedInputs.push(paramName);
      else unmappedInputs.push(paramName);
    }
    out.push({ name, mappedInputs, unmappedInputs });
  }
  return out.length > 0 ? out : undefined;
}

export function listDesignerWorkflowSteps(
  sdInstance: ScreenDesignInstance,
  input: IListStepsInput = {}
):
  | {
      success: true;
      workflowId: string;
      workflowName: string;
      count: number;
      steps: IStepSummary[];
      parameters: IWfParamSummary[];
    }
  | { error: string } {
  const sd = getValues(sdInstance);
  const workflows = getWorkflows(sd);
  const workflow: IWorkFlow | undefined =
    input.workflowId != null
      ? (workflows.find((candidate: IWorkFlowBaseInfo) => String(candidate.id) === String(input.workflowId)) as
          | IWorkFlow
          | undefined)
      : sd.selectedWf;

  if (!workflow) return { error: "No workflow is active. Open a workflow first or provide workflowId." };

  let allElements: IStepSummary[] = [];
  const wp = getWp(sdInstance);

  try {
    const { elementRegistry } = getBpmnModules();
    if (elementRegistry) {
      const shapesAndConnections = elementRegistry
        .getAll()
        .filter((e: any) => e.type !== "label" && e.type !== "bpmn:Process" && e.type !== "bpmn:Participant");

      const decisions: IStoredPathDecision[] = Array.isArray(workflow.nodes?.decisions)
        ? (workflow.nodes?.decisions as unknown as IStoredPathDecision[])
        : [];
      const lanes: IWFLane[] = Array.isArray(workflow.nodes?.lanes) ? (workflow.nodes?.lanes as IWFLane[]) : [];
      allElements = shapesAndConnections.map((e: any): IStepSummary => {
        const summary: IStepSummary = {
          id: e.id,
          name: e.businessObject?.name || e.id,
          type: e.type,
          actionCount: 0,
          sourceId: e.source?.id,
          targetId: e.target?.id
        };
        // KOA-18593 — what a path out of a gateway carries: its default flag and its condition.
        if (e.type === "bpmn:SequenceFlow" && String(e.source?.type ?? "").includes("Gateway")) {
          summary.isDefault = e.source?.businessObject?.default?.id === e.id;
          const stored: IStoredPathDecision | undefined = decisions.find(
            (d: IStoredPathDecision): boolean => d?.id === e.id
          );
          const condition: string | undefined = describeStoredPathCondition(stored?.condition);
          if (condition !== undefined) summary.condition = condition;
        }
        // KOA-18593 — and the role a lane is assigned to.
        if (e.type === "bpmn:Lane") {
          const lane: IWFLane | undefined = lanes.find((l: IWFLane): boolean => l?.id === e.id);
          const role: IProjectRole | undefined = lane?.assignment?.roleAssignmentParameter;
          if (role?.id) summary.role = { id: String(role.id), name: String(role.name ?? "") };
        }
        return summary;
      });

      const wfSteps =
        wp && wp.findAllStepsRecursively
          ? wp.findAllStepsRecursively(workflow, () => true)
          : sd.findAllStepsRecursively?.(workflow, () => true) || [];
      // KOA-17362 §25 — surface the managed task screen each step owns, so
      // assignment is observable from STATE rather than only from an
      // `ASSIGN_SCREEN` reply that may have timed out mid-flight.
      const screenNameById: Map<string, string> = new Map<string, string>();
      for (const screen of sd.screenList || []) {
        const screenId: string = String((screen as { id?: unknown })?.id ?? "");
        const screenName: string = String((screen as { name?: unknown })?.name ?? "");
        if (screenId.length > 0 && screenName.length > 0) screenNameById.set(screenId, screenName);
      }
      allElements.forEach((el) => {
        const s = wfSteps.find((ws: any) => ws.id === el.id);
        if (s) el.actionCount = s.actions?.length || 0;
        // §49 — the names the step's buttons assign, so "no button writes this" can be answered
        // from the EXISTING process and not only from the current turn's script.
        const assignedByButtons: string[] | undefined = interactionSetNamesForStep(s, workflow.parameters);
        if (assignedByButtons !== undefined) el.interactionSets = assignedByButtons;
        // §50 — what this step produces, so an unproducible gateway condition is detectable
        // from the EXISTING process and not only from this turn's script.
        const produced: string[] | undefined = outputParameterNamesForStep(s, workflow.parameters);
        if (produced !== undefined) el.outputParameterNames = produced;
        // §72 — WHICH action and WHICH of its inputs are still empty. `actionCount` above is a
        // headcount; a report that has to name the action (or notice an unmapped input on a
        // process built in an earlier turn) cannot be written from a number.
        const attachedSummaries: IStepActionSummary[] | undefined = stepActionSummaries(s, sd?.actionDefinitions);
        if (attachedSummaries !== undefined) el.actions = attachedSummaries;
        // KOA-18593 — and who gets a user task, so an assignment the spec asked for can be checked
        // against the process as it stands.
        if (el.type === "bpmn:UserTask") {
          const assigned: IStepAssignmentSummary | undefined = stepAssignmentSummary(s, sd?.actionDefinitions);
          if (assigned !== undefined) el.assignment = assigned;
          // KOA-18593 — the task's buttons by label, so a stopped script is continued without them.
          const labels: string[] = (Array.isArray(s?.userInteractions) ? s.userInteractions : [])
            .map((interaction: ILabelledInteraction): string => String(interaction?.label ?? "").trim())
            .filter((label: string): boolean => label.length > 0);
          if (labels.length > 0) el.interactionLabels = labels;
        }
        const assignedScreenId: string | null = findManagedScreenIdForStep(sd.project, String(workflow.id), el.id);
        if (assignedScreenId !== null) {
          el.screenId = assignedScreenId;
          const assignedScreenName: string | undefined = screenNameById.get(assignedScreenId);
          if (assignedScreenName !== undefined) {
            el.screenName = assignedScreenName;
            el.screenPairedByName = screenNameMatchesStep(String(el.name ?? ""), assignedScreenName);
          }
        }
      });

      const parameters: IWfParamSummary[] = Array.isArray(workflow.parameters)
        ? workflow.parameters.map((p: IWFParameter): IWfParamSummary => ({
            id: String(p.id),
            name: p.name,
            dataType: String(p.dataType),
            parameterType: p.parameterType,
            isInput: !!p.isInput,
            isOutput: !!p.isOutput,
            isBusinessObject: !!p.isBusinessObject,
            defaultValue: summarizeParamDefault(p.defaultValue)
          }))
        : [];

      return {
        success: true,
        workflowId: String(workflow.id),
        workflowName: workflow.name || (workflow as any).workFlowName || "",
        count: allElements.length,
        steps: allElements,
        parameters
      };
    }
  } catch (e) {}

  return { error: "Could not read diagram state." };
}

export async function selectDesignerWorkflowStep(
  sdInstance: ScreenDesignInstance,
  input: ISelectStepInput
): Promise<any> {
  const { stepId, workflowId } = input || {};
  if (!stepId) return { error: "stepId is required" };

  const sd = getValues(sdInstance);
  const wp = getWp(sdInstance);

  if (workflowId && String(sd.selectedWf?.id) !== String(workflowId)) {
    if (!wp?.setSelectedWf) return { error: `Workflow "${workflowId}" is not active. Open it first.` };
    await wp.setSelectedWf(String(workflowId));
  }

  const workflow = workflowId
    ? (getWorkflows(sd).find((c) => String(c.id) === String(workflowId)) as IWorkFlow | undefined)
    : sd.selectedWf;

  if (!workflow) return { error: "No workflow is active." };

  const step = forceSyncAndFindNode(sdInstance, stepId);
  if (!step) return { error: `Element "${stepId}" not found in workflow.` };

  if (wp && wp.setSelectedWfStep) wp.setSelectedWfStep(step.id);

  return {
    success: true,
    workflowId: String(workflow.id),
    workflowName: workflow.name || (workflow as any).workFlowName || "",
    stepId: String(step.id),
    stepName: step.name || step.id,
    message: `Selected element "${step.name || step.id}".`
  };
}

function insertBpmnNode(
  sdInstance: any,
  nodeType: string,
  name: string,
  afterNodeId?: string,
  beforeNodeId?: string,
  laneId?: string
) {
  const { modeling, elementRegistry, elementFactory, bpmnFactory } = getBpmnModules();

  let afterElement = afterNodeId ? elementRegistry.get(afterNodeId) : null;
  let beforeElement = beforeNodeId ? elementRegistry.get(beforeNodeId) : null;

  if (!afterElement && !beforeElement) {
    const startEvents = elementRegistry.filter((e: any) => e.type === "bpmn:StartEvent");
    if (startEvents.length > 0) afterElement = startEvents[0];
  }

  if (afterElement && !beforeElement && !afterElement.type.includes("Gateway")) {
    const outgoingFlows = afterElement.outgoing?.filter((c: any) => c.type === "bpmn:SequenceFlow");
    if (outgoingFlows && outgoingFlows.length === 1) {
      beforeElement = outgoingFlows[0].target;
    }
  }

  const processShape = elementRegistry.filter((e: any) => e.type === "bpmn:Participant")[0];
  const allLanes = elementRegistry.filter((e: any) => e.type === "bpmn:Lane");
  allLanes.sort((a: any, b: any) => (a.y || 0) - (b.y || 0));

  let parentShape = processShape;
  const explicitLane = laneId ? elementRegistry.get(laneId) : null;

  let targetX = processShape ? processShape.x + 150 : 150;
  let targetY = 150;

  if (explicitLane) {
    parentShape = explicitLane;
    targetY = explicitLane.y + 40;
    if (afterElement) {
      targetX = afterElement.x + (afterElement.width || 100) + 60;
    } else {
      targetX = explicitLane.x + 100;
    }
  } else if (afterElement) {
    if (afterElement.parent && afterElement.parent.type === "bpmn:Lane") {
      parentShape = afterElement.parent;
    } else if (allLanes.length > 0) {
      parentShape = allLanes[0];
    }
    targetX = afterElement.x + (afterElement.width || 100) + 60;
    targetY = afterElement.y;
  } else if (beforeElement) {
    if (beforeElement.parent && beforeElement.parent.type === "bpmn:Lane") {
      parentShape = beforeElement.parent;
    }
    targetX = beforeElement.x - 160;
    targetY = beforeElement.y;
  }

  const businessObject = bpmnFactory.create(nodeType, { name: name });
  const newShapeDef = elementFactory.createShape({ type: nodeType, businessObject });
  const ew = newShapeDef.width || 100;
  const eh = newShapeDef.height || 80;

  const flowNodes = elementRegistry.filter(
    (e: any) =>
      e.type !== "bpmn:SequenceFlow" &&
      e.type !== "bpmn:Participant" &&
      e.type !== "bpmn:Lane" &&
      e.type !== "bpmn:Process" &&
      e.type !== "bpmn:BoundaryEvent"
  );

  const isOccupied = (tx: number, ty: number) => {
    const marginX = 30;
    const marginY = 30;
    return flowNodes.some((e: any) => {
      const ex = e.x;
      const ey = e.y;
      const ew2 = e.width || 100;
      const eh2 = e.height || 80;
      return tx < ex + ew2 + marginX && tx + ew + marginX > ex && ty < ey + eh2 + marginY && ty + eh + marginY > ey;
    });
  };

  let attempts = 0;
  let currentX = targetX;
  let currentY = targetY;

  while (isOccupied(currentX, currentY) && attempts < 500) {
    if (parentShape && parentShape.type === "bpmn:Lane") {
      if (currentY + 120 + eh < parentShape.y + parentShape.height) {
        currentY += 120;
      } else {
        currentY = targetY;
        currentX += 160;
      }
    } else {
      currentX += 160;
    }
    attempts++;
  }

  targetX = currentX;
  targetY = currentY;

  if (processShape && targetX + ew + 100 > processShape.x + processShape.width) {
    const newWidth = targetX + ew + 100 - processShape.x;
    modeling.resizeShape(processShape, {
      x: processShape.x,
      y: processShape.y,
      width: newWidth,
      height: processShape.height
    });
    allLanes.forEach((lane: any) => {
      modeling.resizeShape(lane, {
        x: processShape.x + 30,
        y: lane.y,
        width: newWidth - 30,
        height: lane.height
      });
    });
  }

  const createdShape = modeling.createShape(newShapeDef, { x: targetX, y: targetY }, parentShape);

  if (afterElement && beforeElement) {
    setTimeout(() => {
      const existingConnection = afterElement.outgoing?.find((c: any) => c.target.id === beforeElement.id);
      if (existingConnection) modeling.removeConnection(existingConnection);

      modeling.connect(afterElement, createdShape, { type: "bpmn:SequenceFlow" });
      if (!(nodeType.includes("Gateway") && beforeElement.type === "bpmn:EndEvent")) {
        modeling.connect(createdShape, beforeElement, { type: "bpmn:SequenceFlow" });
      }
    }, 50);
  } else if (afterElement) {
    setTimeout(() => {
      modeling.connect(afterElement, createdShape, { type: "bpmn:SequenceFlow" });
    }, 50);
  } else if (beforeElement) {
    setTimeout(() => {
      modeling.connect(createdShape, beforeElement, { type: "bpmn:SequenceFlow" });
    }, 50);
  }

  setTimeout(() => {
    if (!nodeType.includes("Gateway") && !nodeType.includes("Event")) {
      const out = createdShape.outgoing?.filter((c: any) => c.type === "bpmn:SequenceFlow");
      if (!out || out.length === 0) {
        const endEvent = elementRegistry.filter((e: any) => e.type === "bpmn:EndEvent")[0];
        if (endEvent) {
          modeling.connect(createdShape, endEvent, { type: "bpmn:SequenceFlow" });
        }
      }
    }

    const wp = getWp(sdInstance);
    if (wp) {
      if (typeof wp.processPendingChanges === "function") wp.processPendingChanges();
      adoptCanvasIntoModel(wp);
    }
  }, 100);

  return createdShape;
}

/**
 * Create a BPMN node without any auto-wiring.  Used by the DSL executor
 * for nodes that don't have `after:` / `before:` specified — they will be
 * connected later via PATH / CONNECT commands.
 *
 * Unlike `insertBpmnNode`, this function:
 * - Does NOT connect the node to StartEvent
 * - Does NOT auto-detect beforeElement
 * - Does NOT auto-connect to EndEvent
 * - Does NOT insert between existing flows
 *
 * It only places the shape in the correct pool/lane and returns it.
 */
function createIsolatedBpmnNode(sdInstance: any, nodeType: string, name: string, laneId?: string) {
  const { modeling, elementRegistry, elementFactory, bpmnFactory } = getBpmnModules();

  const processShape = elementRegistry.filter((e: any) => e.type === "bpmn:Participant")[0];
  const allLanes = elementRegistry.filter((e: any) => e.type === "bpmn:Lane");
  allLanes.sort((a: any, b: any) => (a.y || 0) - (b.y || 0));

  let parentShape = processShape;
  const explicitLane = laneId ? elementRegistry.get(laneId) : null;

  let targetX = processShape ? processShape.x + 400 : 400;
  let targetY = 150;

  if (explicitLane) {
    parentShape = explicitLane;
    targetX = explicitLane.x + 100;
    targetY = explicitLane.y + 40;
  } else if (allLanes.length > 0) {
    parentShape = allLanes[0];
    targetX = allLanes[0].x + 100;
    targetY = allLanes[0].y + 40;
  }

  const businessObject = bpmnFactory.create(nodeType, { name });
  const newShapeDef = elementFactory.createShape({ type: nodeType, businessObject });
  const ew = newShapeDef.width || 100;
  const eh = newShapeDef.height || 80;

  // Avoid overlapping with existing nodes
  const flowNodes = elementRegistry.filter(
    (e: any) =>
      e.type !== "bpmn:SequenceFlow" &&
      e.type !== "bpmn:Participant" &&
      e.type !== "bpmn:Lane" &&
      e.type !== "bpmn:Process" &&
      e.type !== "bpmn:BoundaryEvent"
  );

  const isOccupied = (tx: number, ty: number) => {
    const marginX = 30;
    const marginY = 30;
    return flowNodes.some((e: any) => {
      const ex = e.x;
      const ey = e.y;
      const ew2 = e.width || 100;
      const eh2 = e.height || 80;
      return tx < ex + ew2 + marginX && tx + ew + marginX > ex && ty < ey + eh2 + marginY && ty + eh + marginY > ey;
    });
  };

  let attempts = 0;
  while (isOccupied(targetX, targetY) && attempts < 500) {
    if (parentShape && parentShape.type === "bpmn:Lane") {
      if (targetY + 120 + eh < parentShape.y + parentShape.height) {
        targetY += 120;
      } else {
        targetY = parentShape.y + 40;
        targetX += 160;
      }
    } else {
      targetX += 160;
    }
    attempts++;
  }

  // Expand pool if needed
  if (processShape && targetX + ew + 100 > processShape.x + processShape.width) {
    const newWidth = targetX + ew + 100 - processShape.x;
    modeling.resizeShape(processShape, {
      x: processShape.x,
      y: processShape.y,
      width: newWidth,
      height: processShape.height
    });
    allLanes.forEach((lane: any) => {
      modeling.resizeShape(lane, {
        x: processShape.x + 30,
        y: lane.y,
        width: newWidth - 30,
        height: lane.height
      });
    });
  }

  const createdShape = modeling.createShape(newShapeDef, { x: targetX, y: targetY }, parentShape);

  // Sync workflow provider
  setTimeout(() => {
    const wp = getWp(sdInstance);
    if (wp) {
      if (typeof wp.processPendingChanges === "function") wp.processPendingChanges();
      adoptCanvasIntoModel(wp);
    }
  }, 50);

  return createdShape;
}

/**
 * A kuika workflow MUST have a Start and an End event (lint rule
 * `kuika/start-end-required`, "Do not DELETE StartEvent_1 or EndEvent_1"). If
 * one was deleted, the diagram is invalid AND the well-known `StartEvent_1` /
 * `EndEvent_1` aliases dangle, so `CONNECT X -> EndEvent_1` fails with "Source
 * or Target node not found" and the workflow can never be repaired via the DSL
 * (KOA-17362: "süreci incele ... eksikleri düzelt" on a workflow whose END was
 * missing left both branch tails unwired and no End event). This restores a
 * missing Start/End (additive only — no-op when both exist, never auto-wired;
 * the caller wires it). Returns the resolved ids so the executor can map the
 * well-known aliases to them. Only runs on an initialized diagram (has a pool).
 */
export function workflowEnsureStartEndEventsAction(sdInstance: ScreenDesignInstance): IEnsureStartEndResult {
  const { elementRegistry } = getBpmnModules();
  const participant = elementRegistry.filter((e: any) => e.type === "bpmn:Participant")[0];
  const process = elementRegistry.filter((e: any) => e.type === "bpmn:Process")[0];
  if (!participant && !process) return { createdStart: false, createdEnd: false };

  const startEvents = elementRegistry.filter((e: any) => e.type === "bpmn:StartEvent");
  const endEvents = elementRegistry.filter((e: any) => e.type === "bpmn:EndEvent");

  let startId: string | undefined = startEvents[0]?.id;
  let endId: string | undefined = endEvents[0]?.id;
  let createdStart: boolean = false;
  let createdEnd: boolean = false;

  if (startEvents.length === 0) {
    const created = createIsolatedBpmnNode(sdInstance, "bpmn:StartEvent", "");
    startId = created?.id;
    createdStart = true;
  }
  if (endEvents.length === 0) {
    const created = createIsolatedBpmnNode(sdInstance, "bpmn:EndEvent", "");
    endId = created?.id;
    createdEnd = true;
  }

  return { startId, endId, createdStart, createdEnd };
}

/** What a node-creation action answers: an outcome, or a reason it refused. */
interface NodeCreationResult {
  success?: boolean;
  message?: string;
  error?: string;
}

/**
 * KOA-18136 §32/§35 — SAME NAME, SAME TYPE IS A MATCH, NOT A FAILURE.
 *
 * Creating a node used to fail with "already exists" and leave the command's
 * ALIAS UNBOUND, which is what turns a half-finished build into a total loss.
 * Live 2026-08-17 (session d36d78e2): a 112-line script outlived the 90s
 * transport deadline having applied EVERY line; the builder could not know that,
 * re-sent the construction script, and its second line — one system step — was
 * refused. The alias went unbound and the next 15 field mappings, the action
 * attach, both decision buttons, the gateway and the phase assignment all failed
 * with "Unresolved alias". The gateways were left with a default branch and no
 * condition, and the builder then spent 21 turns moving that default back and
 * forth. Reusing makes a re-run IDEMPOTENT, which is exactly what any recovery
 * script has to be.
 *
 * A same-name node of a DIFFERENT type is still refused: a user task cannot
 * silently stand in for a system step, and reusing it would attach the script's
 * work to the wrong kind of step.
 *
 * §35 — this decision is SHARED by all three creation paths. It used to live
 * inside the isolated-node path only, so the two paths the DSL actually uses for
 * `TASK` and `SYSTEM` kept the old refusal. Same defect, same day, one fix.
 */
function judgeExistingNamedNode(name: string, bpmnType: string): NodeCreationResult | null {
  const existing = findExistingTaskByName(name);
  if (!existing) return null;
  if (existing.type === bpmnType) {
    return { success: true, message: `Task reused with ID: ${existing.id}` };
  }
  return {
    error:
      `A "${existing.type}" named "${existing.name}" already exists (id: ${existing.id}), ` +
      `but this command asks for a "${bpmnType}". Same name, different type is a conflict, not a match. ` +
      `To replace it, DELETE ${existing.id} FIRST in this same script, then create with the same name. ` +
      `Do NOT silently create a duplicate — bpmn.js would auto-suffix it ("${existing.name} 1") and leave the original orphaned.`
  };
}

export function workflowCreateIsolatedNodeAction(sdInstance: ScreenDesignInstance, input: any): NodeCreationResult {
  const typeMap: Record<string, string> = {
    UserTask: "bpmn:UserTask",
    Task: "bpmn:Task",
    ExclusiveGateway: "bpmn:ExclusiveGateway",
    InclusiveGateway: "bpmn:InclusiveGateway",
    ParallelGateway: "bpmn:ParallelGateway",
    SubProcess: "bpmn:SubProcess",
    CallActivity: "bpmn:CallActivity"
  };

  const bpmnType = typeMap[input.nodeType];
  if (!bpmnType) return { error: `Unknown node type: ${input.nodeType}` };

  if (bpmnType === "bpmn:UserTask" || bpmnType === "bpmn:Task") {
    const verdict: NodeCreationResult | null = judgeExistingNamedNode(input.name, bpmnType);
    if (verdict) return verdict;
  }

  // Reuse a same-name gateway instead of spawning a duplicate (idempotent re-run).
  if (bpmnType.includes("Gateway")) {
    const existingGateway = findExistingGatewayByName(input.name, bpmnType);
    if (existingGateway) {
      return { success: true, message: `Gateway reused with ID: ${existingGateway.id}` };
    }
  }

  const shape = createIsolatedBpmnNode(sdInstance, bpmnType, input.name || "", input.laneId);

  // For user tasks in lanes, copy lane assignment
  if ((bpmnType === "bpmn:UserTask" || bpmnType === "bpmn:Task") && input.laneId) {
    setTimeout(() => {
      const step = forceSyncAndFindNode(sdInstance, shape.id);
      if (step) {
        const laneNode = forceSyncAndFindNode(sdInstance, input.laneId);
        if (laneNode && laneNode.assignment) {
          step.assignment = JSON.parse(JSON.stringify(laneNode.assignment));
          step.useLaneAssignment = true;
          const wp = getWp(sdInstance);
          if (wp && wp.updateWfStep) wp.updateWfStep(step);
        }
      }
    }, 100);
  }

  return { success: true, message: `Isolated node created with ID: ${shape.id}` };
}

export function workflowCreateUserTaskAction(sdInstance: ScreenDesignInstance, input: any): NodeCreationResult {
  // §35 — shares the reuse verdict with the other two creation paths.
  const verdict: NodeCreationResult | null = judgeExistingNamedNode(input.taskName, "bpmn:UserTask");
  if (verdict) return verdict;
  const shape = insertBpmnNode(
    sdInstance,
    "bpmn:UserTask",
    input.taskName,
    input.afterNodeId,
    input.beforeNodeId,
    input.laneId
  );

  setTimeout(() => {
    const step = forceSyncAndFindNode(sdInstance, shape.id);
    if (step && input.laneId) {
      const laneNode = forceSyncAndFindNode(sdInstance, input.laneId);
      if (laneNode && laneNode.assignment) {
        step.assignment = JSON.parse(JSON.stringify(laneNode.assignment));
        step.useLaneAssignment = true;
        const wp = getWp(sdInstance);
        if (wp && wp.updateWfStep) wp.updateWfStep(step);
      }
    }
  }, 100);

  return { success: true, message: `User task created with ID: ${shape.id}` };
}

export function workflowCreateSystemStepAction(sdInstance: ScreenDesignInstance, input: any): NodeCreationResult {
  // §35 — shares the reuse verdict with the other two creation paths. This is the
  // path the live failure went through: `SYSTEM @persist "…"` on a re-sent script.
  const verdict: NodeCreationResult | null = judgeExistingNamedNode(input.stepName, "bpmn:Task");
  if (verdict) return verdict;
  const shape = insertBpmnNode(
    sdInstance,
    "bpmn:Task",
    input.stepName,
    input.afterNodeId,
    input.beforeNodeId,
    input.laneId
  );

  setTimeout(() => {
    const step = forceSyncAndFindNode(sdInstance, shape.id);
    if (step && input.laneId) {
      const laneNode = forceSyncAndFindNode(sdInstance, input.laneId);
      if (laneNode && laneNode.assignment) {
        step.assignment = JSON.parse(JSON.stringify(laneNode.assignment));
        step.useLaneAssignment = true;
        const wp = getWp(sdInstance);
        if (wp && wp.updateWfStep) wp.updateWfStep(step);
      }
    }
  }, 100);

  return { success: true, message: `System step created with ID: ${shape.id}` };
}

export function workflowUpdateUserTaskNameAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry } = getBpmnModules();
  const element = elementRegistry.get(input.taskId);
  if (!element) return { error: `Element ${input.taskId} not found.` };
  modeling.updateProperties(element, { name: input.newName });
  return { success: true, message: `Updated name to ${input.newName}` };
}

export function workflowUpdateSystemStepNameAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry } = getBpmnModules();
  const element = elementRegistry.get(input.stepId);
  if (!element) return { error: `Element ${input.stepId} not found.` };
  modeling.updateProperties(element, { name: input.newName });
  return { success: true, message: `Updated name to ${input.newName}` };
}

export function workflowUpdateStepPropertiesAction(sdInstance: ScreenDesignInstance, input: any) {
  const step = forceSyncAndFindNode(sdInstance, input.stepId);
  if (!step) return { error: `Step with ID ${input.stepId} not found.` };

  if (input.description !== undefined) step.description = input.description;
  if (input.sendNotification !== undefined) step.sendNotification = input.sendNotification;
  if (input.dueIn !== undefined) step.dueIn = { dueUnit: input.dueIn.dueUnit, dueValue: input.dueIn.dueValue };

  if (input.nodeAssignment !== undefined) {
    if (!step.nodeAssignment) step.nodeAssignment = {};
    step.nodeAssignment.assignmentType = input.nodeAssignment.assignmentType;
    if (input.nodeAssignment.assignmentType === 2 && input.nodeAssignment.roleId) {
      if (!isWritableRoleId(input.nodeAssignment.roleId)) {
        return {
          error:
            `Role id "${String(input.nodeAssignment.roleId)}" is not a role id — it must be 8-4-4-4-12 hex. ` +
            "Refused: an unreadable id makes the whole process definition fail to load."
        };
      }
      step.nodeAssignment.roleAssignmentParameter = {
        id: input.nodeAssignment.roleId,
        name: "Assigned Role",
        roleType: 1
      };
    } else if (input.nodeAssignment.assignmentType === 3) {
      step.nodeAssignment.roleAssignmentParameter = undefined;
    }

    if (input.nodeAssignment.laneId) {
      const { modeling, elementRegistry } = getBpmnModules();
      const bpmnElement = elementRegistry.get(input.stepId);
      const targetLane = elementRegistry.get(input.nodeAssignment.laneId);

      if (bpmnElement && targetLane && bpmnElement.parent !== targetLane) {
        modeling.moveElements([bpmnElement], { x: 0, y: 0 }, targetLane);
      }
    }
  }

  const wp = getWp(sdInstance);
  if (wp && wp.updateWfStep) wp.updateWfStep(step);
  return { success: true, message: `Properties updated successfully for step ${input.stepId}.` };
}

/**
 * A workflow's Start and End events are mandatory (lint rule
 * `kuika/start-end-required`) and must never be deletable via the DSL/agent.
 * Returns a user-facing error message when the given element type is a
 * required event, or undefined when the element may be deleted. Exported for
 * testing. (KOA-17362: deleting the End event corrupted the whole diagram.)
 */
export function requiredEventDeleteError(elementType: string): string | undefined {
  if (elementType === "bpmn:StartEvent" || elementType === "bpmn:EndEvent") {
    const which: string = elementType === "bpmn:StartEvent" ? "start" : "end";
    return `The ${which} event is a required element of every workflow and cannot be deleted. If you want to change where the flow ends, re-route the connections instead of removing the ${which} event.`;
  }
  return undefined;
}

export function workflowDeleteElementAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry } = getBpmnModules();
  const element = elementRegistry.get(input.elementId) as any;
  if (!element) return { error: `Element ${input.elementId} not found.` };

  // A workflow MUST keep its Start and End events (lint rule
  // `kuika/start-end-required`). Deleting one is never a valid edit — it leaves
  // the diagram structurally invalid, and an agent that "succeeded" at deleting
  // the End then panics on the lint error and cascade-deletes the rest of the
  // diagram trying to recover (KOA-17362: "süreç sonu öğesini sil" wiped the
  // whole design across 14 turns). Refuse here so the request is rejected with
  // a clear, user-facing reason instead of corrupting the workflow.
  const elementType: string = typeof element.type === "string" ? element.type : element.businessObject?.$type || "";
  const requiredEventError: string | undefined = requiredEventDeleteError(elementType);
  if (requiredEventError) return { error: requiredEventError };

  // A SequenceFlow (connection) is removed directly.
  const isConnection: boolean =
    Array.isArray(element.waypoints) || (typeof element.type === "string" && element.type.includes("SequenceFlow"));
  if (isConnection) {
    modeling.removeConnection(element);
    return { success: true, message: `Element ${input.elementId} deleted.` };
  }

  // For a flow NODE, remove its connected sequence flows FIRST, then the bare
  // shape — so bpmn-js does NOT auto-heal (reconnect predecessor→successor with
  // a fresh flow). That silent heal left a spurious bypass arrow when a caller
  // replaced a mid-flow step and re-wired the new node afterward (KOA-17362:
  // "remove 'Yönetici Onayı', put 'İK Onayı'" left a START→Gateway arrow). With
  // no heal, an explicit re-wire is clean, and a bare delete surfaces the gap
  // via LINT instead of silently bridging it.
  const connections: any[] = [...(element.incoming || []), ...(element.outgoing || [])];
  for (const conn of connections) {
    try {
      modeling.removeConnection(conn);
    } catch {
      // Already removed (e.g. shared with another just-deleted element).
    }
  }
  if (typeof modeling.removeShape === "function") {
    modeling.removeShape(element);
  } else {
    modeling.removeElements([element]);
  }

  return { success: true, message: `Element ${input.elementId} deleted.` };
}

export function workflowAddGatewayAction(sdInstance: ScreenDesignInstance, input: any) {
  const typeMap: Record<string, string> = {
    Exclusive: "bpmn:ExclusiveGateway",
    Inclusive: "bpmn:InclusiveGateway",
    Parallel: "bpmn:ParallelGateway"
  };
  const bpmnType = typeMap[input.gatewayType];
  if (!bpmnType) return { error: `Invalid gateway type: ${input.gatewayType}` };
  // Reuse a same-name gateway instead of spawning a duplicate (idempotent re-run).
  const existingGateway = findExistingGatewayByName(input.name, bpmnType);
  if (existingGateway) {
    return { success: true, message: `${input.gatewayType} Gateway reused with ID: ${existingGateway.id}` };
  }
  const shape = insertBpmnNode(
    sdInstance,
    bpmnType,
    input.name || input.gatewayType,
    input.afterNodeId,
    input.beforeNodeId
  );
  return { success: true, message: `${input.gatewayType} Gateway created with ID: ${shape.id}` };
}

export function workflowCreateGatewayPathAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry } = getBpmnModules();
  const source = elementRegistry.get(input.sourceNodeId);
  const target = elementRegistry.get(input.targetNodeId);

  if (!source || !target) return { error: "Source or Target node not found in diagram." };

  if (!source.type.includes("Gateway")) {
    return { error: "You cannot branch directly from a Task or Event. Please use a Gateway for branching." };
  }

  let connection = source.outgoing?.find((c: any) => c.target.id === target.id);

  if (!connection) {
    connection = modeling.connect(source, target, { type: "bpmn:SequenceFlow" });
  }

  if (input.pathName && connection) {
    modeling.updateProperties(connection, { name: input.pathName });
  }

  return { success: true, message: `Path handled with ID: ${connection?.id || "unknown"}` };
}

/**
 * Direct connect: creates a SequenceFlow between any two nodes (no gateway
 * restriction).  Before connecting, removes auto-generated flows:
 * - If source has an outgoing flow to EndEvent, removes it (task auto-connect cleanup)
 * - If target has an incoming flow from StartEvent, removes it (task auto-connect cleanup)
 * This prevents spurious paths that insertBpmnNode creates for unanchored nodes.
 */
export function workflowDirectConnectAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry } = getBpmnModules();
  const source = elementRegistry.get(input.sourceNodeId);
  const target = elementRegistry.get(input.targetNodeId);

  if (!source || !target) return { error: "Source or Target node not found in diagram." };

  // Already connected? Return existing flow
  const existing = source.outgoing?.find((c: any) => c.type === "bpmn:SequenceFlow" && c.target?.id === target.id);
  if (existing) {
    return { success: true, message: `Already connected with ID: ${existing.id}` };
  }

  // Clean up auto-generated outgoing flow from source to EndEvent only if
  // the source is a Task (not Gateway) AND the target is NOT an EndEvent.
  // insertBpmnNode auto-connects fresh tasks to EndEvent when they have no
  // outgoing; before wiring source to a new intended target we remove that
  // auto-artifact. If user explicitly connects source -> EndEvent, skip.
  if (!source.type.includes("Gateway") && target.type !== "bpmn:EndEvent") {
    const autoEndFlow = source.outgoing?.find(
      (c: any) => c.type === "bpmn:SequenceFlow" && c.target?.type === "bpmn:EndEvent"
    );
    if (autoEndFlow) {
      modeling.removeConnection(autoEndFlow);
    }
  }

  // NOTE: Previous versions removed target.incoming from StartEvent as a
  // cleanup for insertBpmnNode's auto-wiring. Removed because it also
  // deleted legitimate Start -> FirstTask flows when a CONNECT targeted a
  // task that happens to be first in the chain (e.g. loop-back edges).

  const connection = modeling.connect(source, target, { type: "bpmn:SequenceFlow" });

  // Clear auto-generated "Path N" name that bpmn.js assigns to gateway outgoing flows
  if (connection && connection.businessObject?.name && /^Path \d+$/.test(connection.businessObject.name)) {
    modeling.updateProperties(connection, { name: undefined });
  }

  return { success: true, message: `Connected with ID: ${connection?.id || "unknown"}` };
}

export function workflowSetDefaultPathAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry } = getBpmnModules();
  const gateway = elementRegistry.get(input.gatewayId);

  if (!gateway) {
    return {
      error:
        `Gateway '${input.gatewayId}' not found in diagram. ` +
        `Call LIST to get the current gateway IDs, then retry with the real ID. ` +
        `Tip: the correct gateway is the one whose outgoing SequenceFlow is '${input.pathId}' — check the LIST response's flow entries.`
    };
  }

  let path = elementRegistry.get(input.pathId);

  // Tolerate a TARGET node id as the second argument (e.g.
  // `DEFAULT gw -> @escalate` where @escalate is a task, not a flow):
  // resolve it to the gateway's outgoing SequenceFlow that points at that
  // target. LLMs frequently pass the target node instead of the flow id.
  const isSequenceFlow = (el: { type?: string } | undefined): boolean => el?.type === "bpmn:SequenceFlow";
  if (path && !isSequenceFlow(path as { type?: string })) {
    const targetId = input.pathId;
    const resolvedFlow = (
      (gateway as { outgoing?: { id: string; type: string; target?: { id?: string } }[] }).outgoing || []
    ).find((c) => c.type === "bpmn:SequenceFlow" && c.target?.id === targetId);
    if (resolvedFlow) {
      path = elementRegistry.get(resolvedFlow.id);
      input.pathId = resolvedFlow.id;
    }
  }

  if (!path) {
    return {
      error: `Path (SequenceFlow) '${input.pathId}' not found in diagram. Call LIST to verify the correct flow ID.`
    };
  }
  if (!path.type || path.type !== "bpmn:SequenceFlow") {
    return {
      error: `'${input.pathId}' is not a SequenceFlow (it is ${path.type}). DEFAULT's second argument must be a SequenceFlow ID (e.g. Flow_xxx) or the target node the default branch routes to.`
    };
  }
  // Sanity-check: path's source should match the gateway
  const pathSourceId = (path as { source?: { id?: string } }).source?.id;
  if (pathSourceId && pathSourceId !== input.gatewayId) {
    return {
      error:
        `Path '${input.pathId}' belongs to gateway '${pathSourceId}', not '${input.gatewayId}'. ` +
        `Use \`DEFAULT ${pathSourceId} ${input.pathId}\` instead.`
    };
  }

  modeling.updateProperties(gateway, { default: path.businessObject });

  const wp = getWp(sdInstance);
  if (wp && wp.updateDiagramElement) {
    wp.updateDiagramElement(input.gatewayId, [{ name: "default", value: input.pathId, isValueIdToGetElement: true }]);
  }

  // Mark the decision as default in the workflow JSON AND reset isDefault on
  // all OTHER outgoing flows of the same gateway. Without this reset, the
  // lint rule sees stale isDefault=true on a previously-defaulted flow and
  // mistakenly treats it as default — masking missing conditions.
  const sd = getValues(sdInstance) as any;
  const workflow = sd.selectedWf;
  const siblingFlowIds = new Set(
    ((gateway as { outgoing?: { id: string }[] }).outgoing || [])
      .map((c) => c.id)
      .filter((flowId) => flowId !== input.pathId)
  );
  const clearSiblings = (decisions: unknown[]): void => {
    if (!Array.isArray(decisions)) return;
    for (const d of decisions as { id?: string; isDefault?: boolean }[]) {
      if (d && d.id && siblingFlowIds.has(d.id)) d.isDefault = false;
    }
  };
  if (workflow && workflow.nodes && workflow.nodes.decisions) {
    clearSiblings(workflow.nodes.decisions);
    const decision = workflow.nodes.decisions.find((d: any) => d && d.id === input.pathId);
    if (decision) {
      decision.isDefault = true;
    }
  }
  // Also sync across the other workflow references the lint rule may read
  // from (workflowProvider.getSelectedWf / window.__wfSelected).
  try {
    const windowInst = (window as any).getSdInstance?.();
    const wpWf = windowInst?.workflowProvider?.getSelectedWf?.();
    if (wpWf?.nodes?.decisions) {
      clearSiblings(wpWf.nodes.decisions);
      const d = wpWf.nodes.decisions.find((x: any) => x && x.id === input.pathId);
      if (d) d.isDefault = true;
    }
  } catch {
    /* ignore */
  }
  try {
    const w2 = (window as any).__wfSelected;
    if (w2?.nodes?.decisions) {
      clearSiblings(w2.nodes.decisions);
      const d = w2.nodes.decisions.find((x: any) => x && x.id === input.pathId);
      if (d) d.isDefault = true;
    }
  } catch {
    /* ignore */
  }

  // Optional: also LABEL the default branch in one shot. "default dalı 'Diğer
  // Durumlar' olsun" means mark a branch default AND name it — without this the
  // agent had to chain a separate UPDATE_PATH_NAME and often skipped it
  // (KOA-17362). `path` is the resolved SequenceFlow.
  if (input.pathName && path) {
    modeling.updateProperties(path, { name: input.pathName });
  }

  const namedNote: string = input.pathName ? ` and labeled "${input.pathName}"` : "";
  return { success: true, message: `Path ${input.pathId} set as default${namedNote}.` };
}

export function workflowInitializePathConditionAction(sdInstance: ScreenDesignInstance, input: any) {
  let decision = forceSyncAndFindNode(sdInstance, input.pathId);

  // Fallback: newly created SequenceFlows may not have synced to the workflow
  // JSON yet.  If the flow exists in the BPMN elementRegistry, manually inject
  // a decisions entry so the condition can be set on it.
  // CRITICAL: The Kuika lint rule "gateway-conditions" reads from
  // wf.nodes.decisions[] (NOT forwards[]), so the entry MUST go there.
  if (!decision) {
    try {
      const { elementRegistry } = getBpmnModules();
      const element = elementRegistry.get(input.pathId);
      if (element && element.type === "bpmn:SequenceFlow") {
        // CRITICAL: The lint rule reads from workflowProvider.getSelectedWf(),
        // which may be a DIFFERENT object reference than getValues(sd).selectedWf.
        // We must inject the decision entry into BOTH references.
        const sd2 = getValues(sdInstance) as any;
        const wp = getWp(sdInstance);
        const wpWf = wp?.getSelectedWf ? wp.getSelectedWf() : null;

        // Collect ALL workflow references the lint rule might read from
        const workflowRefs: any[] = [];
        const addRef = (ref: any) => {
          if (ref && !workflowRefs.includes(ref)) workflowRefs.push(ref);
        };
        addRef(sd2.selectedWf);
        addRef(wpWf);
        // Exact path lint rule uses: window.getSdInstance().workflowProvider.getSelectedWf()
        try {
          const windowInst = (window as any).getSdInstance?.();
          addRef(windowInst?.workflowProvider?.getSelectedWf?.());
        } catch {
          /* ignore */
        }
        addRef((window as any).__wfSelected);

        const sourceElement = element.source;
        const isDefault = sourceElement?.businessObject?.default?.id === input.pathId;

        for (const workflow of workflowRefs) {
          if (!workflow || !workflow.nodes) continue;
          if (!workflow.nodes.decisions) workflow.nodes.decisions = [];
          const existing = workflow.nodes.decisions.find((d: any) => d && d.id === input.pathId);
          if (existing && !decision) {
            decision = existing;
          } else if (!existing) {
            const newDecision = {
              id: input.pathId,
              name: element.businessObject?.name || "",
              sourceRef: element.source?.id,
              targetRef: element.target?.id,
              isDefault: isDefault,
              internalIndex: workflow.nodes.decisions.length,
              condition: null
            };
            workflow.nodes.decisions.push(newDecision);
            if (!decision) decision = newDecision;
          }
        }
      }
    } catch {
      // Ignore — will fall through to error below
    }
  }

  if (!decision) return { error: `Path with ID ${input.pathId} not found.` };

  const sd = getValues(sdInstance) as any;

  // Collect parameters from EVERY workflow reference, not just `sd.selectedWf`.
  // A `PARAM INTERNAL` created earlier in the SAME script pushes onto whichever
  // selectedWf ref was live at that moment; the `forceSyncAndFindNode` at the top
  // of THIS call can swap `sd.selectedWf` for the workflow-provider's copy, which
  // may not yet carry that just-pushed param (async save) — so a single-ref read
  // intermittently reports "NO parameters yet" and the CONDITION/PATH fails even
  // though the PARAM line succeeded (KOA-17362: turn-3 cascade → delete/recreate
  // churn; the same script shape worked on a later turn purely by timing).
  const allParams: any[] = collectWorkflowParamsUnion(sdInstance, sd);

  for (const c of input.conditions || []) {
    const exists = allParams.find((p: any) => p.name === c.propertyName);
    if (!exists) {
      const available = allParams.map((p: any) => `${p.name}:${p.dataType}`).join(", ");
      const availableText = available ? `Existing parameters: ${available}.` : "The workflow has NO parameters yet.";
      return {
        error:
          `Parameter '${c.propertyName}' not found in workflow. ${availableText} ` +
          `Fix options: (1) Add \`PARAM INTERNAL ${c.propertyName} Boolean = false\` as an earlier line in THIS script, ` +
          `then this CONDITION/PATH will work. ` +
          `(2) Reuse an existing parameter from the list above. ` +
          `(3) If this is the fallback branch, use \`DEFAULT <gatewayId> <pathId>\` instead of CONDITION — DEFAULT needs no parameter. ` +
          `Do NOT retry the same command unchanged — pick one of (1), (2), or (3).`
      };
    }
  }

  const mappedConditions: IConditionModel[] = (input.conditions || []).map((c: any): IConditionModel =>
    buildWorkflowConditionRow(
      c,
      allParams.find((p: any) => p.name === c.propertyName)
    )
  );

  decision.condition = buildWorkflowConditionGroup(mappedConditions, input.isOrGroup === true);

  // Sync the condition to ALL workflow references the lint rule might read from.
  // The lint rule (gateway-conditions.js) calls getWF() which tries:
  //   1. window.getSdInstance().workflowProvider.getSelectedWf()
  //   2. window.__wfSelected
  // These may be different object references than sd.selectedWf.
  const syncToRef = (wfRef: any) => {
    if (!wfRef || wfRef === sd.selectedWf || !wfRef.nodes) return;
    if (!wfRef.nodes.decisions) wfRef.nodes.decisions = [];
    const existing = wfRef.nodes.decisions.find((d: any) => d && d.id === input.pathId);
    if (existing) {
      existing.condition = decision.condition;
      existing.isDefault = decision.isDefault;
    } else {
      wfRef.nodes.decisions.push({ ...decision });
    }
  };

  const wp = getWp(sdInstance);
  // Ref 1: workflowProvider.getSelectedWf()
  syncToRef(wp?.getSelectedWf ? wp.getSelectedWf() : null);
  // Ref 2: window.getSdInstance().workflowProvider.getSelectedWf() (exact path lint uses)
  try {
    const windowInst = (window as any).getSdInstance?.();
    const windowWp = windowInst?.workflowProvider;
    syncToRef(windowWp?.getSelectedWf ? windowWp.getSelectedWf() : null);
  } catch {
    /* ignore */
  }
  // Ref 3: window.__wfSelected (lint fallback)
  syncToRef((window as any).__wfSelected);

  persistUnlessBatched(wp, sd.selectedWf, 26);

  try {
    const g: any = (globalThis as any).__myapp_LINT_CTX;
    const modeler = g?.modeler;
    if (modeler && typeof modeler.get === "function") {
      const linting = modeler.get("linting");
      const eventBus = modeler.get("eventBus");
      const elementRegistry = modeler.get("elementRegistry");

      if (linting && typeof linting.update == "function") {
        linting.update();
      }

      if (eventBus && elementRegistry && typeof elementRegistry.getAll === "function") {
        const all = elementRegistry.getAll();
        eventBus.fire("elements.changed", { elements: all });
      }
    }
  } catch {}

  return { success: true, message: `Condition initialized for path ${input.pathId}.` };
}

export function workflowConfigureMultiInstanceAction(sdInstance: ScreenDesignInstance, input: any) {
  const step = forceSyncAndFindNode(sdInstance, input.stepId);
  if (!step) return { error: `Step ${input.stepId} not found.` };

  const { modeling, elementRegistry, bpmnFactory } = getBpmnModules();
  const element = elementRegistry.get(input.stepId);
  if (!element) return { error: `BPMN Element ${input.stepId} not found.` };

  let loopCharacteristics = undefined;
  if (input.mode === "PMI")
    loopCharacteristics = bpmnFactory.create("bpmn:MultiInstanceLoopCharacteristics", { isSequential: false });
  else if (input.mode === "SMI")
    loopCharacteristics = bpmnFactory.create("bpmn:MultiInstanceLoopCharacteristics", { isSequential: true });

  modeling.updateProperties(element, { loopCharacteristics });

  const updateExtra = (key: string, value: any) => {
    if (!step.extraParameters) step.extraParameters = [];
    const hit = step.extraParameters.find((p: any) => p?.key === key);
    if (hit) hit.value = JSON.stringify(value);
    else step.extraParameters.push({ key, value: JSON.stringify(value) });
  };

  if (input.mode === "None") {
    const keysToRemove = ["mi.mode", "mi.collectionSource", "mi.instanceCount", "mi.expansionMode", "mi.ordering"];
    step.extraParameters = step.extraParameters?.filter((p: any) => !keysToRemove.includes(p.key)) || [];
  } else {
    // PRESERVE existing MI sub-settings the command doesn't restate. `MI <ref>
    // PMI` (mode only, no count:/collection:) must NOT reset a manually-set
    // instance count or collection source (KOA-17362 — count 15 was being
    // clobbered back to 1). Only (re)write a sub-setting when it is explicitly
    // provided, or when the step has none yet.
    const readExtra = (key: string): any | undefined => {
      const hit = step.extraParameters?.find((p: any) => p?.key === key);
      if (!hit?.value) return undefined;
      try {
        return JSON.parse(hit.value);
      } catch {
        return undefined;
      }
    };

    updateExtra("mi.mode", input.mode);

    const existingCollectionSource = readExtra("mi.collectionSource");
    const collectionSource = input.collectionSource ?? existingCollectionSource ?? "Manual";
    updateExtra("mi.collectionSource", collectionSource);

    if (collectionSource === "Assignment" && input.expansionMode) {
      updateExtra("mi.expansionMode", input.expansionMode);
    } else if (collectionSource !== "Assignment") {
      const hasExistingCount = readExtra("mi.instanceCount") !== undefined;
      const countToWrite = resolveMiInstanceCountWrite(input.instanceCount, hasExistingCount);
      // null → preserve the existing (possibly manually-set) instance count.
      if (countToWrite !== null) {
        updateExtra("mi.instanceCount", {
          kind: "symbol",
          value: {
            category: ESymbolCategory.fixed,
            fixedValue: countToWrite,
            label: "Fixed Value",
            description: String(countToWrite),
            isFixed: true
          }
        });
      }
    }
    if (input.mode === "SMI" && input.ordering) updateExtra("mi.ordering", input.ordering);
  }

  const wp = getWp(sdInstance);
  if (wp && wp.updateWfStep) wp.updateWfStep(step);
  return { success: true, message: `Multi-Instance mode set to ${input.mode} for step ${input.stepId}.` };
}

export function workflowInitializeMiCancelConditionAction(sdInstance: ScreenDesignInstance, input: any) {
  const step = forceSyncAndFindNode(sdInstance, input.stepId);
  if (!step) return { error: `Step ${input.stepId} not found.` };

  // The diagram marker decides, the stored mi.mode (in either spelling a saved process carries) only
  // when there is no diagram — the rule the MI panels and the code generator follow. Parsing the stored
  // value alone refused a parallel step whose mi.mode the panel had stored as `false`.
  const mode: MiMode | undefined = resolveMiMode(step);
  if (!mode) return { error: "Step is not configured as PMI or SMI." };

  const conditionKey = mode === "PMI" ? "mi.pmi.quorum" : "mi.smi.cancel";

  const sd = getValues(sdInstance) as any;
  // MI_CANCEL's left side is EITHER a workflow parameter (a per-instance decision
  // output) OR a built-in instance counter such as `miCompletedCount` — the tool's
  // own contract offers both. `resolveMapInputSource` is the existing source of
  // truth for that choice (a same-named parameter wins over the keyword), and the
  // generator recognises a counter by its keyword `inputName`, so the keyword must
  // survive verbatim instead of being written as if it were a parameter name.
  const miParams: any[] = sd.selectedWf?.parameters ?? [];
  const mappedConditions: IConditionModel[] = (input.conditions || []).map((c: any): IConditionModel => {
    const source = resolveMapInputSource(String(c.propertyName ?? ""), false, miParams);
    if (source.kind === "instance") {
      return buildWorkflowConditionRowWithOperand(
        c,
        buildWorkflowInstanceOperand(source.inputName, source.label, source.type)
      );
    }
    return buildWorkflowConditionRow(c, source.kind === "parameter" ? source.param : undefined);
  });

  const condition: IConditionModel = buildWorkflowConditionGroup(mappedConditions, input.isOrGroup === true);

  if (!step.extraParameters) step.extraParameters = [];
  const hit = step.extraParameters.find((p: any) => p?.key === conditionKey);
  if (hit) hit.value = JSON.stringify(condition);
  else step.extraParameters.push({ key: conditionKey, value: JSON.stringify(condition) });

  const wp = getWp(sdInstance);
  if (wp && wp.updateWfStep) wp.updateWfStep(step);
  return { success: true, message: `Cancel condition initialized for ${mode}.` };
}

export function workflowConfigureMiAggregationAction(sdInstance: ScreenDesignInstance, input: any) {
  const step = forceSyncAndFindNode(sdInstance, input.stepId);
  if (!step) return { error: `Step ${input.stepId} not found.` };

  const updateExtra = (key: string, value: any) => {
    if (!step.extraParameters) step.extraParameters = [];
    const hit = step.extraParameters.find((p: any) => p?.key === key);
    if (hit) hit.value = JSON.stringify(value);
    else step.extraParameters.push({ key, value: JSON.stringify(value) });
  };

  updateExtra("mi.aggregate.childOutputKey", input.childOutputKey);
  updateExtra("mi.aggregate.strategy", input.strategy);
  updateExtra("mi.aggregate.aggregateTarget", `MI_${input.childOutputKey}_${input.stepId}_Agg`);
  updateExtra("mi.aggregate.collectAllTarget", `MI_${input.childOutputKey}_${input.stepId}_All`);

  const wp = getWp(sdInstance);
  if (wp && wp.updateWfStep) wp.updateWfStep(step);
  return { success: true, message: `Aggregation configured.` };
}

export function workflowAddLaneAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry } = getBpmnModules();

  const participants = elementRegistry.filter((e: any) => e.type === "bpmn:Participant");

  if (!participants || participants.length === 0) {
    return { error: "No bpmn:Participant found in the diagram." };
  }

  const mainPool = participants[0];

  // Reuse an existing lane with the same name instead of creating a duplicate.
  // "akışa Personel/Yönetici lane'lerini ekle" on a workflow that ALREADY has
  // those lanes must NOT spawn a second "Personel" band — bpmn.js would leave
  // the originals empty and scatter the steps. Match by name (case-insensitive;
  // JS toLowerCase is locale-independent). The default "Everyone" lane only
  // matches when the request literally names "Everyone".
  if (input.name) {
    const wanted: string = String(input.name).trim().toLowerCase();
    const existingLanes = elementRegistry.filter((e: any) => e.type === "bpmn:Lane");
    const match = existingLanes.find(
      (l: any) =>
        String(l.businessObject?.name || "")
          .trim()
          .toLowerCase() === wanted
    );
    if (match) {
      const wpExisting = getWp(sdInstance);
      adoptCanvasIntoModel(wpExisting);
      return { success: true, message: `Lane '${input.name}' already exists.`, laneId: match.id };
    }
  }

  const addedShape = modeling.addLane(mainPool, "bottom");

  if (input.name) {
    modeling.updateProperties(addedShape, { name: input.name });
  }

  const wp = getWp(sdInstance);
  adoptCanvasIntoModel(wp);

  return { success: true, message: `Lane created with ID: ${addedShape.id}`, laneId: addedShape.id };
}

export function workflowUpdateLaneNameAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry } = getBpmnModules();
  const element = elementRegistry.get(input.laneId);
  if (!element) return { error: `Lane ${input.laneId} not found.` };

  modeling.updateProperties(element, { name: input.newName });

  const laneJson = forceSyncAndFindNode(sdInstance, input.laneId);
  if (laneJson) {
    laneJson.name = input.newName;
  }

  const wp = getWp(sdInstance);
  adoptCanvasIntoModel(wp);

  return { success: true, message: `Lane name updated.` };
}

/**
 * KOA-18136 — A ROLE ID THAT IS NOT AN ID MUST NEVER BE WRITTEN.
 *
 * The platform stores a lane's or a step's role reference in a field it deserializes as a Guid,
 * and its reader tolerates only an empty string. So a single unparseable value does not degrade
 * one lane — it makes the WHOLE process definition unreadable, and the generation path reads
 * every process of every project in the domain, which turns one bad row into a dead preview for
 * projects that never touched it.
 *
 * Refusing costs one failed line and a report the caller can act on. Accepting costs the domain.
 * The guarantee therefore lives HERE, at the write, not in whichever caller happened to be
 * careful: anything that reaches this writer — the process language, the panel, a future client —
 * is held to the same shape.
 */
const ROLE_ID_SHAPE = /^[0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}$/;

export function isWritableRoleId(value: unknown): value is string {
  return typeof value === "string" && ROLE_ID_SHAPE.test(value.trim());
}

export function workflowSetLaneAssignmentAction(sdInstance: ScreenDesignInstance, input: any) {
  const lane = forceSyncAndFindNode(sdInstance, input.laneId);
  if (!lane) return { error: "Lane not found in workflow JSON." };

  let data = input.assignmentData;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (e) {}
  }
  if (!data || typeof data !== "object") {
    data = {};
  }

  let extractedId = data.roleId || data.id || data.role || undefined;
  if (!extractedId && Array.isArray(data.roleIds) && data.roleIds.length > 0) {
    extractedId = data.roleIds[0];
  }

  if (extractedId !== undefined && !isWritableRoleId(extractedId)) {
    return {
      error:
        `Role id "${String(extractedId)}" is not a role id — it must be 8-4-4-4-12 hex. ` +
        "Refused: an unreadable id makes the whole process definition fail to load."
    };
  }

  let roleParameter = data.roleAssignmentParameter;
  if (roleParameter && !isWritableRoleId(roleParameter.id)) {
    return {
      error:
        `Role id "${String(roleParameter.id)}" is not a role id — it must be 8-4-4-4-12 hex. ` +
        "Refused: an unreadable id makes the whole process definition fail to load."
    };
  }
  if (!roleParameter && extractedId) {
    roleParameter = {
      id: extractedId,
      name: data.roleName || data.name || "Assigned Role",
      roleType: 1
    };
  }

  const newAssignment = {
    assignmentType: data.assignmentType || 2,
    assignmentId: data.assignmentId || "Role",
    roleAssignmentParameter: roleParameter
  };

  const wp = getWp(sdInstance);
  if (wp && wp.handleLaneAssignmentChange) {
    lane.assignment = newAssignment;
    wp.handleLaneAssignmentChange(lane, newAssignment);

    adoptCanvasIntoModel(wp);
  }

  return { success: true, message: "Lane assignment updated and cascaded." };
}

export function workflowDeleteLaneAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry } = getBpmnModules();
  const element = elementRegistry.get(input.laneId);
  if (!element) return { error: `Lane ${input.laneId} not found.` };
  modeling.removeElements([element]);

  return { success: true, message: `Lane deleted.` };
}

export function workflowAddTimerIntermediateEventAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry, elementFactory, bpmnFactory } = getBpmnModules();

  const afterElement = input.afterNodeId ? elementRegistry.get(input.afterNodeId) : null;
  const beforeElement = input.beforeNodeId ? elementRegistry.get(input.beforeNodeId) : null;

  if (!afterElement) return { error: "afterNodeId is required to place the event." };

  const process = elementRegistry.filter((e: any) => e.type === "bpmn:Process" || e.type === "bpmn:Participant")[0];
  let parentShape = process;
  if (afterElement.parent) parentShape = afterElement.parent;

  const businessObject = bpmnFactory.create("bpmn:IntermediateCatchEvent", {
    name: typeof input.name === "string" && input.name.trim() ? input.name.trim() : "Timer Event"
  });
  const timerDef = bpmnFactory.create("bpmn:TimerEventDefinition");
  businessObject.eventDefinitions = [timerDef];

  const newShapeDef = elementFactory.createShape({ type: "bpmn:IntermediateCatchEvent", businessObject });

  const targetX = afterElement.x + 150;
  const targetY = afterElement.y;

  const createdShape = modeling.createShape(newShapeDef, { x: targetX, y: targetY }, parentShape);

  setTimeout(() => {
    let oldTarget: any = null;
    let existingConnection: any = null;

    if (afterElement.outgoing && afterElement.outgoing.length > 0) {
      existingConnection = afterElement.outgoing.find(
        (c: any) => c.type === "bpmn:SequenceFlow" && c.target?.type !== "bpmn:BoundaryEvent"
      );
      if (existingConnection) {
        oldTarget = existingConnection.target;
        modeling.removeConnection(existingConnection);
      }
    }

    modeling.connect(afterElement, createdShape, { type: "bpmn:SequenceFlow" });

    if (beforeElement) {
      modeling.connect(createdShape, beforeElement, { type: "bpmn:SequenceFlow" });
    } else if (oldTarget) {
      modeling.connect(createdShape, oldTarget, { type: "bpmn:SequenceFlow" });
    }

    const wp = getWp(sdInstance);
    adoptCanvasIntoModel(wp);
  }, 50);

  return { success: true, message: `Timer Event created with ID: ${createdShape.id}` };
}

export function workflowAttachBoundaryEventAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry, elementFactory, bpmnFactory } = getBpmnModules();
  const targetNode = elementRegistry.get(input.nodeId);
  if (!targetNode) return { error: "Target node not found." };

  const businessObject = bpmnFactory.create("bpmn:BoundaryEvent");
  const eventDef = bpmnFactory.create(`bpmn:${input.eventType}`);
  businessObject.eventDefinitions = [eventDef];
  businessObject.attachedToRef = targetNode.businessObject;

  const newShapeDef = elementFactory.createShape({ type: "bpmn:BoundaryEvent", businessObject });

  const position = {
    x: targetNode.x + targetNode.width / 2,
    y: targetNode.y + targetNode.height
  };

  const newShape = modeling.createShape(newShapeDef, position, targetNode, { attach: true });

  const stepJson = forceSyncAndFindNode(sdInstance, input.nodeId);
  if (stepJson) {
    if (!stepJson.boundaryEvent) stepJson.boundaryEvent = {};
    stepJson.boundaryEvent.id = newShape.id;
  }

  const wp = getWp(sdInstance);
  adoptCanvasIntoModel(wp);

  return { success: true, message: `Boundary event attached with ID: ${newShape.id}` };
}

export function workflowSetTimerEventDueDateAction(sdInstance: ScreenDesignInstance, input: any) {
  if (input.eventId && input.eventId.startsWith("Flow_")) {
    return {
      error:
        "You provided a SequenceFlow ID instead of a Timer Event ID. Please provide the ID of the IntermediateCatchEvent."
    };
  }

  const event = forceSyncAndFindNode(sdInstance, input.eventId);
  if (!event) return { error: "Event not found in JSON." };

  event.dueIn = { dueUnit: input.timerConfig.dueUnit, dueValue: input.timerConfig.dueValue };

  const wp = getWp(sdInstance);
  const sd = getValues(sdInstance) as any;
  persistUnlessBatched(wp, sd.selectedWf);
  return { success: true, message: "Timer duration updated." };
}

export function workflowDeleteTimerIntermediateEventAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry } = getBpmnModules();
  const element = elementRegistry.get(input.eventId);
  if (!element) return { error: `Event ${input.eventId} not found.` };
  modeling.removeElements([element]);

  return { success: true, message: `Event deleted.` };
}

export function workflowDeleteBoundaryEventAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry } = getBpmnModules();
  const element = elementRegistry.get(input.eventId);
  if (!element) return { error: `Boundary Event ${input.eventId} not found.` };
  modeling.removeElements([element]);

  return { success: true, message: `Boundary Event deleted.` };
}

export function workflowConfigureBoundaryEventAction(sdInstance: ScreenDesignInstance, input: any) {
  let step = forceSyncAndFindNode(sdInstance, input.eventId);

  if (!step || !step.boundaryEvent) {
    const sd = getValues(sdInstance) as any;
    const wf = sd.selectedWf;
    if (wf && wf.nodes && wf.nodes.steps) {
      step = wf.nodes.steps.find((s: any) => s.boundaryEvent && s.boundaryEvent.id === input.eventId);
    }
  }

  if (!step || !step.boundaryEvent) return { error: "Boundary event not found in JSON." };

  step.boundaryEvent.isInterrupting = input.isInterrupting;
  if (input.timerDuration && input.timerDuration.dueUnit) {
    step.boundaryEvent.timerEventDue = { dueUnit: input.timerDuration.dueUnit, dueValue: input.timerDuration.dueValue };
  }

  const wp = getWp(sdInstance);
  if (wp && wp.updateDiagramElement)
    wp.updateDiagramElement(input.eventId, [{ name: "cancelActivity", value: input.isInterrupting }]);
  if (wp && wp.updateWfStep) wp.updateWfStep(step);
  return { success: true, message: "Boundary event configured." };
}

export function workflowAddSubprocessAction(sdInstance: ScreenDesignInstance, input: any) {
  const shape = insertBpmnNode(sdInstance, "bpmn:SubProcess", input.name || "Sub Process", input.afterNodeId);
  return { success: true, message: `Subprocess created: ${shape.id}` };
}

export function workflowDeleteSubprocessAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry } = getBpmnModules();
  const element = elementRegistry.get(input.subprocessId);
  if (!element) return { error: `Subprocess not found.` };
  modeling.removeElements([element]);

  return { success: true, message: `Subprocess deleted.` };
}

export function workflowUpdateSubprocessNameAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry } = getBpmnModules();
  const element = elementRegistry.get(input.subprocessId);
  if (!element) return { error: `Subprocess not found.` };
  modeling.updateProperties(element, { name: input.newName });

  return { success: true, message: `Subprocess name updated.` };
}

export function workflowAddLinkedProcessAction(sdInstance: ScreenDesignInstance, input: any) {
  const shape = insertBpmnNode(sdInstance, "bpmn:CallActivity", "Linked Process", input.afterNodeId);
  const step = forceSyncAndFindNode(sdInstance, shape.id);
  const sd = getValues(sdInstance) as any;

  if (step && sd.actionDefinitions) {
    const startProcessDef = sd.actionDefinitions.find((ad: any) => ad.name === "StartProcess");
    if (startProcessDef) {
      const newAction = {
        id: globalThis.crypto ? globalThis.crypto.randomUUID() : Math.random().toString(),
        actionDefinitionId: startProcessDef.id,
        label: startProcessDef.name,
        description: startProcessDef.name,
        parameters: startProcessDef.actionParameters.map((x: any) => ({ ...x, symbol: undefined })),
        returnType: startProcessDef.returnTypeName,
        returnCardinality: startProcessDef.returnCardinality
      };
      step.actions = [newAction];
      const wp = getWp(sdInstance);
      if (wp && wp.updateWfStep) wp.updateWfStep(step);
    }
  }

  return { success: true, message: `Linked Process created: ${shape.id}` };
}

/** New uuid for a generated workflow parameter (crypto when available). */
function newWfParameterId(): string {
  return globalThis.crypto ? globalThis.crypto.randomUUID() : Math.random().toString();
}

/**
 * Build an internal (Bag) workflow parameter with a fixed-value default. Shared
 * by the explicit `PARAM INTERNAL` action and by linked-process mapping, which
 * auto-creates a missing caller parameter typed to match its linked-process
 * target. `id` is passed in (the caller supplies a uuid) so this stays pure.
 * Internal parameters may be Input/Output like business objects; `parameterType`
 * tracks `isInput` because the workflow-load normalizers recompute the flag from it.
 */
export function buildInternalWfParameter(
  id: string,
  name: string,
  dataType: string,
  defaultValue?: unknown,
  isInput: boolean = false,
  isOutput: boolean = false
): IWFParameter {
  return {
    id,
    name,
    dataType: dataType as IWFParameter["dataType"],
    isBusinessObject: false,
    isInput,
    isOutput,
    parameterType: isInput ? EWFParamType.Input : EWFParamType.Bag,
    defaultValue: {
      category: ESymbolCategory.fixed,
      fixedValue: defaultValue ?? "",
      label: "Fixed Value",
      description: String(defaultValue ?? ""),
      isFixed: true
    }
  };
}

/** One requested mapping, expressed by parameter NAME (or id) on each side. */
export interface IRequestedLinkedMapping {
  sourceName: string;
  targetName: string;
}

/** Full parameter info needed to route + type-check a linked-process mapping. */
export interface IWfParamFull {
  id: string;
  name: string;
  dataType: string;
  isInput?: boolean;
  isOutput?: boolean;
}

/** A caller value bound INTO a linked-process input parameter (StartProcess action param). */
export interface IPlannedInputBinding {
  targetParamName: string;
  targetDataType: string;
  callerParamId: string;
  callerParamName: string;
  callerDataType: string;
}

/** A linked-process OUTPUT param routed back to a caller param (keyed by real ids). */
export interface IPlannedOutputMapping {
  linkedProcessOutputParameter: string;
  callerProcessOutputParameter: string;
}

export interface ILinkedTypeMismatch {
  caller: string;
  callerType: string;
  target: string;
  targetType: string;
}

export interface ILinkedMappingPlan {
  inputBindings: IPlannedInputBinding[];
  outputMappings: IPlannedOutputMapping[];
  unresolvedCaller: string[];
  unresolvedTarget: string[];
  typeMismatches: ILinkedTypeMismatch[];
}

/**
 * Plan how each requested linked-process mapping (`caller -> target`, by NAME)
 * should be applied. A target INPUT parameter receives the caller value via the
 * StartProcess action's input parameter (an INPUT binding); a target OUTPUT
 * parameter routes back to a caller parameter via `linkedProcessMappings`. The
 * model + UI key on parameter IDs and require an EXACT dataType match (the panel
 * filters caller options with `prm.dataType === p.dataType`), so we resolve both
 * sides id-or-name (case-insensitive), reject a junk/mismatched pair, and report
 * what could not be applied instead of silently storing an invalid entry.
 */
export function planLinkedProcessMappings(
  requested: readonly IRequestedLinkedMapping[],
  callerParams: readonly IWfParamFull[],
  targetParams: readonly IWfParamFull[]
): ILinkedMappingPlan {
  const inputBindings: IPlannedInputBinding[] = [];
  const outputMappings: IPlannedOutputMapping[] = [];
  const unresolvedCaller: string[] = [];
  const unresolvedTarget: string[] = [];
  const typeMismatches: ILinkedTypeMismatch[] = [];

  const findByIdOrName = (params: readonly IWfParamFull[], ref: string): IWfParamFull | undefined => {
    const needle: string = String(ref).toLowerCase();
    return params.find(
      (p: IWfParamFull) => String(p.id).toLowerCase() === needle || String(p.name).toLowerCase() === needle
    );
  };

  for (const req of requested) {
    const callerObj: IWfParamFull | undefined = findByIdOrName(callerParams, req.sourceName);
    const targetObj: IWfParamFull | undefined = findByIdOrName(targetParams, req.targetName);
    if (!callerObj) unresolvedCaller.push(req.sourceName);
    if (!targetObj) unresolvedTarget.push(req.targetName);
    if (!callerObj || !targetObj) continue;

    if (String(callerObj.dataType) !== String(targetObj.dataType)) {
      typeMismatches.push({
        caller: callerObj.name,
        callerType: String(callerObj.dataType),
        target: targetObj.name,
        targetType: String(targetObj.dataType)
      });
      continue;
    }

    if (targetObj.isOutput === true && targetObj.isInput !== true) {
      outputMappings.push({
        linkedProcessOutputParameter: targetObj.id,
        callerProcessOutputParameter: callerObj.id
      });
    } else {
      // Input (or bag-as-input) target → pass the caller value IN.
      inputBindings.push({
        targetParamName: targetObj.name,
        targetDataType: String(targetObj.dataType),
        callerParamId: callerObj.id,
        callerParamName: callerObj.name,
        callerDataType: String(callerObj.dataType)
      });
    }
  }

  return { inputBindings, outputMappings, unresolvedCaller, unresolvedTarget, typeMismatches };
}

export async function workflowConfigureLinkedProcessParametersAction(sdInstance: ScreenDesignInstance, input: any) {
  const step = forceSyncAndFindNode(sdInstance, input.linkedProcessId);
  if (!step) return { error: "Linked process step not found." };

  const sd = getValues(sdInstance) as any;
  const startAction = step.actions && step.actions.length > 0 ? step.actions[0] : undefined;

  if (input.targetProcessId && startAction) {
    const wfParam = startAction.parameters.find((p: any) => p.name === "workflow");
    if (wfParam) {
      wfParam.symbol = {
        category: ESymbolCategory.fixed,
        fixedValue: input.targetProcessId,
        description: input.targetProcessId,
        label: "Fixed Value",
        isFixed: true
      };
    }
    const projParam = startAction.parameters.find((p: any) => p.name === "projectId");
    if (projParam && sd.project && sd.project.id) {
      projParam.symbol = {
        category: ESymbolCategory.fixed,
        fixedValue: sd.project.id,
        description: sd.project.id,
        label: "Fixed Value",
        isFixed: true
      };
    }
  }

  if (!step.linkedProcess) {
    step.linkedProcess = { linkedProcessId: undefined, linkedProcessMappings: [] };
  }

  if (input.targetProcessId) {
    step.linkedProcess.linkedProcessId = input.targetProcessId;
  }

  let plan: ILinkedMappingPlan | undefined;
  const createdCallerParams: string[] = [];
  if (Array.isArray(input.parameters) && input.parameters.length > 0) {
    // Target params: the list endpoint omits `json` (and thus `parameters`), so
    // workFlowList entries usually carry none — fetch the full definition so a
    // target parameter NAME can resolve to its real id + dataType + direction.
    const targetParams: IWfParamFull[] = await resolveTargetWorkflowParameters(sd, input.targetProcessId);

    const requested: IRequestedLinkedMapping[] = input.parameters.map((m: any) => ({
      sourceName: m.sourceParameterName || m.sourceParameterId || m.callerProcessOutputParameter,
      targetName: m.targetParameterName || m.targetParameterId || m.linkedProcessOutputParameter
    }));

    // Auto-create a MISSING caller parameter, typed to match its linked-process
    // target. The agent cannot see the target workflow's parameter types (LIST
    // only shows the open workflow), so it can neither guess the right type nor
    // reliably pre-create the param — make `map:"x->y"` self-sufficient: if the
    // caller side `x` does not exist, create it as an internal param with `y`'s
    // dataType so the binding is valid in one shot. An EXISTING but wrong-typed
    // caller param is NOT auto-changed (it may be used elsewhere) — reported.
    const wf = sd.selectedWf;
    if (wf) {
      if (!Array.isArray(wf.parameters)) wf.parameters = [];
      const findParam = (ref: string): IWFParameter | undefined => {
        const needle: string = String(ref).toLowerCase();
        return wf.parameters.find(
          (p: IWFParameter) => String(p.id).toLowerCase() === needle || String(p.name).toLowerCase() === needle
        );
      };
      for (const req of requested) {
        if (!req.sourceName) continue;
        const targetObj = targetParams.find(
          (p: IWfParamFull) =>
            String(p.id).toLowerCase() === String(req.targetName).toLowerCase() ||
            String(p.name).toLowerCase() === String(req.targetName).toLowerCase()
        );
        if (!targetObj) continue; // unknown target → planner reports it
        if (findParam(req.sourceName)) continue; // caller already exists
        const typeResult = normalizeWorkflowParamType(targetObj.dataType);
        const dataType: string = typeResult.ok ? typeResult.type : targetObj.dataType;
        wf.parameters.push(buildInternalWfParameter(newWfParameterId(), req.sourceName, dataType));
        createdCallerParams.push(`${req.sourceName} (${dataType})`);
      }
    }

    // Caller params come from the OPEN workflow (now incl. any just-created ones).
    const callerParams: IWfParamFull[] = (sd.selectedWf?.parameters || []) as IWfParamFull[];
    plan = planLinkedProcessMappings(requested, callerParams, targetParams);

    // INPUT bindings: the caller value flows into the StartProcess action's input
    // parameter (which the action panel augments per target input param). Mirror
    // that augmentation here and bind the param to the caller via a processParameter
    // symbol. Keep `Typename` === target dataType so the panel won't reset our symbol.
    if (startAction && plan.inputBindings.length > 0) {
      if (!Array.isArray(startAction.parameters)) startAction.parameters = [];
      plan.inputBindings.forEach((b: IPlannedInputBinding) => {
        const symbol: ISymbol = buildWorkflowParamRefSymbol({
          name: b.callerParamName,
          id: b.callerParamId,
          dataType: b.callerDataType
        });
        const existingParam = startAction.parameters.find((p: any) => p.name === b.targetParamName);
        if (existingParam) {
          existingParam.symbol = symbol;
          existingParam.Typename = b.targetDataType;
        } else {
          startAction.parameters.push({
            name: b.targetParamName,
            symbol,
            cardinality: ECardinality.One,
            Typename: b.targetDataType
          });
        }
      });
    }

    // OUTPUT mappings: target output → caller param, keyed by ids.
    plan.outputMappings.forEach((mp: IPlannedOutputMapping) => {
      const existing = step.linkedProcess.linkedProcessMappings.find(
        (m: any) => String(m.linkedProcessOutputParameter) === String(mp.linkedProcessOutputParameter)
      );
      if (existing) {
        existing.callerProcessOutputParameter = mp.callerProcessOutputParameter;
      } else {
        step.linkedProcess.linkedProcessMappings.push({ ...mp });
      }
    });
  }

  const wp = getWp(sdInstance);
  if (wp && wp.updateWfStep) wp.updateWfStep(step);
  adoptCanvasIntoModel(wp);

  if (
    plan &&
    (plan.unresolvedCaller.length > 0 || plan.unresolvedTarget.length > 0 || plan.typeMismatches.length > 0)
  ) {
    const parts: string[] = [];
    if (plan.unresolvedCaller.length > 0) {
      parts.push(`caller parameter(s) not found in this workflow: ${plan.unresolvedCaller.join(", ")}`);
    }
    if (plan.unresolvedTarget.length > 0) {
      parts.push(`parameter(s) not found in the linked workflow: ${plan.unresolvedTarget.join(", ")}`);
    }
    plan.typeMismatches.forEach((m: ILinkedTypeMismatch) => {
      parts.push(
        `type mismatch: caller "${m.caller}" is ${m.callerType} but linked-process "${m.target}" is ${m.targetType} — set the caller parameter's type to ${m.targetType} (UPDATE_PARAM ${m.caller} type:${m.targetType}) then retry`
      );
    });
    const applied: number = plan.inputBindings.length + plan.outputMappings.length;
    const appliedNote: string = applied > 0 ? ` ${applied} mapping(s) were applied.` : "";
    return {
      success: false,
      error: `Linked process target set, but ${parts.join("; ")}.${appliedNote}`
    };
  }

  const createdNote: string =
    createdCallerParams.length > 0 ? ` Created caller parameter(s): ${createdCallerParams.join(", ")}.` : "";
  return { success: true, message: `Linked process parameters configured.${createdNote}` };
}

/**
 * Gather the target (linked) workflow's parameters for id/type/direction
 * resolution. Prefers an in-memory copy (same-project list or external-project
 * cache) and falls back to fetching the full definition — the project list API
 * returns base info without `json`, so `workFlowList` entries carry no
 * `parameters`. Direction flags are normalized from `parameterType` when absent.
 */
async function resolveTargetWorkflowParameters(sd: any, targetProcessId: string | undefined): Promise<IWfParamFull[]> {
  if (!targetProcessId) return [];

  const normalize = (params: IWFParameter[] | undefined): IWfParamFull[] =>
    (params || []).map((p: IWFParameter) => ({
      id: p.id,
      name: p.name,
      dataType: String(p.dataType),
      isInput: p.isInput ?? p.parameterType === 1,
      isOutput: p.isOutput ?? p.parameterType === 2
    }));

  const fromList = sd.workFlowList?.find((p: any) => String(p.id) === String(targetProcessId));
  if (fromList?.parameters?.length) return normalize(fromList.parameters as IWFParameter[]);

  const fromExternal = (sd.projectsWithWFData || [])
    .flatMap((proj: any) => proj.workflows || [])
    .find((w: any) => String(w.id) === String(targetProcessId));
  if (fromExternal?.parameters?.length) return normalize(fromExternal.parameters as IWFParameter[]);

  try {
    const res = await WorkFlowService.Get(targetProcessId);
    if (res?.status === 200 && res.data?.json) {
      const parsed = JSON.parse(res.data.json) as { parameters?: IWFParameter[] };
      return normalize(parsed.parameters);
    }
  } catch {
    // Fall through to empty — the caller reports the target params as unresolved.
  }
  return [];
}

export function workflowDeleteLinkedProcessAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry } = getBpmnModules();
  const element = elementRegistry.get(input.linkedProcessId);
  if (!element) return { error: `Linked Process not found.` };
  modeling.removeElements([element]);

  return { success: true, message: `Linked Process deleted.` };
}

export function workflowUpdateLinkedProcessNameAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry } = getBpmnModules();
  const element = elementRegistry.get(input.linkedProcessId);
  if (!element) return { error: `Linked Process not found.` };
  modeling.updateProperties(element, { name: input.newName });

  return { success: true, message: `Linked Process name updated.` };
}

/**
 * Every type name this project can reference — the datasource-derived types plus the
 * user-managed Kuika Data Types catalog, which the project provider merges into `sd.types`
 * by name. Read here rather than fetched: the catalog is already in state, and a DSL script
 * declares many parameters in one run.
 */
function availableCatalogTypeNames(sd: any): string[] {
  const types: unknown = sd?.types;
  if (!Array.isArray(types)) return [];
  return types
    .map((type: any): string => String(type?.name ?? "").trim())
    .filter((name: string): boolean => name.length > 0);
}

export function workflowCreateBusinessObjectParameterAction(sdInstance: ScreenDesignInstance, input: any) {
  const sd = getValues(sdInstance) as any;
  const wf = sd.selectedWf;
  if (!wf) return { error: "No active workflow." };

  // OBJECT parameters (KOA-15840) reference a Kuika Data Type by name and are emitted as a
  // typed POCO; scalars keep the nine engine-supported types. The two vocabularies never
  // mix, so which one applies is decided ONCE, here, by the explicit `isObject` flag the
  // parser sets from the `OBJECT` keyword.
  const isObject: boolean = input?.isObject === true;
  let objectTypeName: string | undefined;
  let dataType: string;

  if (isObject) {
    const resolved: CatalogTypeResolution = resolveCatalogTypeName(
      input?.objectTypeName,
      availableCatalogTypeNames(sd)
    );
    if (!resolved.ok) return { error: resolved.message };
    objectTypeName = resolved.name;
    // The engine ignores `dataType` for an object, but the model requires a value — the
    // human parameter editor stores "String" here for exactly the same reason.
    dataType = "String";
  } else {
    const typeResult = normalizeWorkflowParamType(input.dataType);
    if (!typeResult.ok) return { error: typeResult.message };
    dataType = typeResult.type;
  }

  if (!wf.parameters) wf.parameters = [];

  const existingParam = wf.parameters.find((p: any) => p.name === input.name);
  if (existingParam) {
    return { success: true, message: `Parameter '${input.name}' already exists.` };
  }

  wf.parameters.push({
    id: globalThis.crypto ? globalThis.crypto.randomUUID() : Math.random().toString(),
    name: input.name,
    dataType,
    isBusinessObject: true,
    isInput: input.isInput || false,
    isOutput: input.isOutput || false,
    parameterType: input.isInput ? 1 : 3,
    ...(isObject
      ? {
          isObject: true,
          dataTypeName: objectTypeName,
          // ECardinality.Many = 2, .One = 1 — a list of objects vs a single one.
          cardinality: input?.isList === true ? ECardinality.Many : ECardinality.One
        }
      : {}),
    defaultValue: {
      category: ESymbolCategory.fixed,
      fixedValue: input.defaultValue || "",
      label: "Fixed Value",
      description: String(input.defaultValue || ""),
      isFixed: true
    }
  });

  const wp = getWp(sdInstance);
  if (wp && wp.updateWorkFlowList) wp.updateWorkFlowList(wf);
  persistUnlessBatched(wp, wf, 26);
  const shape: string = isObject
    ? `object parameter '${input.name}' (${objectTypeName}${input?.isList === true ? " list" : ""})`
    : `parameter '${input.name}'`;
  return { success: true, message: `Business Object ${shape} created.` };
}

export function workflowCreateInternalParameterAction(sdInstance: ScreenDesignInstance, input: any) {
  const sd = getValues(sdInstance) as any;
  const wf = sd.selectedWf;
  if (!wf) return { error: "No active workflow." };

  const typeResult = normalizeWorkflowParamType(input.dataType);
  if (!typeResult.ok) return { error: typeResult.message };

  if (!wf.parameters) wf.parameters = [];

  const existingParam = wf.parameters.find((p: any) => p.name === input.name);
  if (existingParam) {
    return { success: true, message: `Internal parameter '${input.name}' already exists.` };
  }

  wf.parameters.push(
    buildInternalWfParameter(
      newWfParameterId(),
      input.name,
      typeResult.type,
      input.defaultValue,
      input.isInput || false,
      input.isOutput || false
    )
  );

  const wp = getWp(sdInstance);
  if (wp && wp.updateWorkFlowList) wp.updateWorkFlowList(wf);
  persistUnlessBatched(wp, wf, 26);
  return { success: true, message: `Internal parameter '${input.name}' created.` };
}

// ── Happy Path phases (KOA-17362 Adım 3) ───────────────────────────────────
//
// The engine ALREADY owns a human-readable business status for a running
// process: the Happy Path phase. `UserTaskLogMiddleware` writes
// `workflowContext.Status = currentStepPhase.PhaseName` as the instance advances,
// and `GetProcessesStartedByUser` returns it as `Status`. An AI-built process
// with NO phases → `Status` comes back empty → the only way to show a request's
// state was a custom `Status` column on a parallel table, which is exactly the
// duplication we forbid. These two actions let the DSL declare phases and attach
// steps to them through the same `saveWorkFlow` persistence path. The other
// writer is the DIAGRAM: phases render as native bpmn:Group columns managed by
// `moddle/happyPathPhaseInterceptor.tsx` (the former Happy Path panel/tab was
// retired). Note the diagram is positional — on the next group/step move the
// interceptor re-derives each step's phase from its position, so a DSL
// assignment that contradicts a step's location will be re-derived; DSL-declared
// phases without groups are materialized as columns on the next diagram load.

/** Minimal shape of a Happy Path phase as persisted on `wf.happyPathDefinition`. */
interface IHappyPathPhaseRef {
  id: string;
  happyPathPhase: string;
  phaseOrder: number;
}

/** Case-insensitive phase lookup by display name. */
function findPhaseByName(wf: any, name: string): IHappyPathPhaseRef | undefined {
  const list: IHappyPathPhaseRef[] = Array.isArray(wf?.happyPathDefinition) ? wf.happyPathDefinition : [];
  const wanted: string = name.trim().toLowerCase();
  return list.find(
    (p: IHappyPathPhaseRef): boolean =>
      String(p?.happyPathPhase ?? "")
        .trim()
        .toLowerCase() === wanted
  );
}

/**
 * `PHASE "<Name>"` — declare a Happy Path phase, idempotently.
 *
 * `phaseOrder` derives from declaration order (`count + 1`), mirroring the
 * panel's `AddNewStatus`. Re-declaring an existing name is a no-op success, the
 * same "already exists → reuse" contract `PARAM INTERNAL` and `STEP_ACTION`
 * use — so a follow-up script can `PHASE_ASSIGN` without re-declaring.
 */
export function workflowDeclareHappyPathPhaseAction(sdInstance: ScreenDesignInstance, input: any) {
  const sd = getValues(sdInstance) as any;
  const wf = sd?.selectedWf;
  if (!wf) return { error: "No active workflow." };

  const name: string = String(input?.name ?? "").trim();
  if (name.length === 0) return { error: "PHASE requires a non-empty name." };

  if (!Array.isArray(wf.happyPathDefinition)) wf.happyPathDefinition = [];
  const existing: IHappyPathPhaseRef | undefined = findPhaseByName(wf, name);
  if (existing) {
    return {
      success: true,
      message: `Phase '${existing.happyPathPhase}' already exists (order ${existing.phaseOrder}).`
    };
  }

  const order: number = wf.happyPathDefinition.length + 1;
  wf.happyPathDefinition.push({ id: newWfParameterId(), happyPathPhase: name, phaseOrder: order });

  const wp = getWp(sdInstance);
  if (wp && wp.updateWorkFlowList) wp.updateWorkFlowList(wf);
  persistUnlessBatched(wp, wf, 26);
  return { success: true, message: `Phase '${name}' created (order ${order}).` };
}

/**
 * `PHASE_ASSIGN <stepRef> "<Name>"` — attach a step to a declared phase.
 *
 * Writes `step.extraParameters["happyPath"] = <phaseId>`, exactly what the
 * panel's `StepChangeHandler("ADD")` writes and what the engine middleware reads.
 * Resolution differences from the panel, both deliberate:
 *   - the step is found RECURSIVELY (`forceSyncAndFindNode`), so a step inside a
 *     subprocess can carry a phase; the panel's handler only scanned top level.
 *   - the phase is addressed by NAME, not by a DSL alias — a phase is named
 *     metadata like a parameter or an action, and name addressing is what makes a
 *     later script able to assign without re-declaring.
 * An unknown name is a hard error listing the declared phases rather than a
 * silent no-op, because a mistyped phase leaves the step unphased and the
 * instance's `Status` stale — a failure the author cannot see at runtime.
 */
export function workflowAssignStepHappyPathPhaseAction(sdInstance: ScreenDesignInstance, input: any) {
  const sd = getValues(sdInstance) as any;
  const wf = sd?.selectedWf;
  if (!wf) return { error: "No active workflow." };

  const name: string = String(input?.phaseName ?? "").trim();
  if (name.length === 0) return { error: "PHASE_ASSIGN requires a phase name." };

  const phase: IHappyPathPhaseRef | undefined = findPhaseByName(wf, name);
  if (!phase) {
    const declared: string = (Array.isArray(wf.happyPathDefinition) ? wf.happyPathDefinition : [])
      .map((p: IHappyPathPhaseRef): string => p?.happyPathPhase)
      .filter(Boolean)
      .join(", ");
    return {
      error:
        `Phase '${name}' is not declared in this workflow. Add \`PHASE "${name}"\` first. ` +
        `Declared phases: ${declared || "(none)"}.`
    };
  }

  const step = forceSyncAndFindNode(sdInstance, input?.stepId);
  if (!step) return { error: "Step not found." };

  if (!Array.isArray(step.extraParameters)) step.extraParameters = [];
  const hit = step.extraParameters.find((p: any): boolean => p?.key === "happyPath");
  if (hit) hit.value = phase.id;
  else step.extraParameters.push({ key: "happyPath", value: phase.id });

  const wp = getWp(sdInstance);
  if (wp && wp.updateWfStep) wp.updateWfStep(step);
  persistUnlessBatched(wp, wf, 26);
  return { success: true, message: `Step assigned to phase '${phase.happyPathPhase}'.` };
}

export function workflowUpdateProcessParameterAction(sdInstance: ScreenDesignInstance, input: any) {
  const sd = getValues(sdInstance) as any;
  const wf = sd.selectedWf;
  if (!wf || !wf.parameters) return { error: "No active workflow or parameters." };

  // Resolve by id OR by name (case-insensitive) — callers (DSL UPDATE_PARAM,
  // the LLM) naturally reference a parameter by its user-visible name, not the
  // synthetic id. JS toLowerCase is locale-independent, so the Turkish-i trap
  // does not apply here.
  const ref: string = String(input.parameterId);
  const refLower: string = ref.toLowerCase();
  const param = wf.parameters.find((p: any) => String(p.id) === ref || String(p.name).toLowerCase() === refLower);
  if (!param) return { error: `Parameter '${ref}' not found by id or name.` };

  if (input.updates.name) param.name = input.updates.name;
  if (input.updates.dataType) {
    const typeResult = normalizeWorkflowParamType(input.updates.dataType);
    if (!typeResult.ok) return { error: typeResult.message };
    param.dataType = typeResult.type;
  }

  if (input.updates.defaultValue !== undefined) {
    param.defaultValue = {
      category: ESymbolCategory.fixed,
      fixedValue: input.updates.defaultValue,
      label: "Fixed Value",
      description: String(input.updates.defaultValue),
      isFixed: true
    };
  }

  // Input/Output apply to internal parameters too — the runtime is direction-agnostic
  // (the generated WF data class emits every parameter; [WorkflowOutput] keys on IsOutput
  // alone). parameterType must track isInput: the workflow-load normalizers recompute
  // isInput as `parameterType === Input` and discard the persisted boolean.
  if (input.updates.isInput !== undefined) {
    param.isInput = input.updates.isInput;
    param.parameterType = input.updates.isInput ? 1 : 3;
  }
  if (input.updates.isOutput !== undefined) param.isOutput = input.updates.isOutput;

  const wp = getWp(sdInstance);
  if (wp && wp.updateWorkFlowList) wp.updateWorkFlowList(wf);
  persistUnlessBatched(wp, wf, 26);
  return { success: true, message: `Parameter updated.` };
}

export function workflowDeleteProcessParameterAction(sdInstance: ScreenDesignInstance, input: any) {
  const sd = getValues(sdInstance) as any;
  const wf = sd.selectedWf;
  if (!wf || !wf.parameters) return { error: "No active workflow or parameters." };

  // Resolve by id OR by name (case-insensitive), mirroring the update action.
  const ref: string = String(input.parameterId);
  const refLower: string = ref.toLowerCase();
  const before: number = wf.parameters.length;
  wf.parameters = wf.parameters.filter(
    (p: any) => !(String(p.id) === ref || String(p.name).toLowerCase() === refLower)
  );
  if (wf.parameters.length === before) return { error: `Parameter '${ref}' not found by id or name.` };

  const wp = getWp(sdInstance);
  if (wp && wp.updateWorkFlowList) wp.updateWorkFlowList(wf);
  persistUnlessBatched(wp, wf, 26);
  return { success: true, message: `Parameter deleted.` };
}

/** A `SET <name>=<value>` assignment carried from the DSL INTERACTION command. */
export interface IInteractionSetAssignment {
  name: string;
  value: string;
}

/** Outcome of resolving DSL SET assignments into real interaction parameters. */
export interface IBuiltInteractionParameters {
  parameters: IInteractionParameter[];
  error?: string;
  /** Set when the interaction covers only SOME of the step's output params —
   *  the code generator (`ClientHelperGeneratorBase`) emits the button ONLY
   *  when its parameter count equals the step's output-parameter count, so a
   *  partial cover means the button silently won't render at runtime. */
  coverageWarning?: string;
}

/**
 * Coerce a raw DSL string value to the fixed-value shape a workflow parameter's
 * dataType expects. Mirrors the type handling in the designer's interaction
 * editor (`workflow-step-user-interactions.tsx` `_getDefaultFixedValue`): a
 * Boolean fixed value is a real boolean, numerics are numbers, everything else
 * (String/Guid/DateTimeOffset/Image/Unknown) stays a string. A non-numeric
 * value for a numeric type is left as the raw string rather than becoming NaN.
 * Pure + exported for unit testing. Locale-independent (`toLowerCase`).
 */
export function coerceInteractionFixedValue(rawValue: string, dataType: string): string | number | boolean {
  switch (dataType) {
    case "Boolean":
      return String(rawValue).toLowerCase() === "true";
    case "Byte":
    case "Int16":
    case "Int32":
    case "Int64":
    case "Single":
    case "Decimal":
    case "Double": {
      const num: number = Number(rawValue);
      return Number.isNaN(num) ? rawValue : num;
    }
    default:
      return rawValue;
  }
}

/**
 * Resolve DSL `SET name=value` assignments into `IInteractionParameter[]`, each
 * pointing at a real workflow OUTPUT parameter of the step and carrying a fixed
 * ISymbol value. Mirrors `createUserInteraction` in the interaction editor. Pure
 * + exported for unit testing.
 *
 * A SET target that is NOT one of the step's output parameters is a hard error
 * (the caller must MAP_OUTPUT it first).
 *
 * COVERAGE IS NOT BLANKET (KOA-17362, 2026-07-28 live incident). The managed task
 * screen binds every step output into its `ResumeProcess` from ONE of three
 * sources, in order (`WorkflowScreenService.cs:374-449`): the button's own SET (a
 * fixed literal) → else the param's BO FORM FIELD `.value` → else its default
 * (`false` for a Boolean). So an uncovered output is only a BUG when it has no
 * form field, i.e. when it is NOT a business-object param: it then freezes at its
 * default and a gateway testing it can never take the other branch. An uncovered
 * BUSINESS-OBJECT output is CORRECT — that is exactly how the value the approver
 * typed reaches the process, and SETting it would pin a literal over their input.
 * `coverageWarning` therefore names only the uncovered NON-BO outputs. It is
 * non-fatal: the interaction is still created so a partial script isn't lost.
 *
 * The old wording ("the button renders at codegen ONLY when it sets ALL of them")
 * came from `ClientHelperGeneratorBase.cs:246`, whose count-equality gates only
 * the generated backend AppHelper's interaction registry — React/iOS/Android emit
 * that template as `string.Empty`, the screen's footer buttons are generated
 * unconditionally from `step.UserInteractions`, and the mail-interaction path
 * (`WorkflowStepGenerator.GenerateMailInteractions`) has no count check either.
 * Telling the author to "SET the remaining outputs" is what produced
 * `SET accountingApproved=true approvedAmount=0` — an approve button that zeroed
 * the amount it was approving and routed every approval to the reject branch.
 */
export function buildInteractionParameters(
  setAssignments: IInteractionSetAssignment[],
  stepOutputParamIds: string[],
  wfParams: IWFParameter[]
): IBuiltInteractionParameters {
  const outputIdSet = new Set(stepOutputParamIds.map((id) => String(id)));
  const outputParams: IWFParameter[] = wfParams.filter((p) => outputIdSet.has(String(p.id)));

  const parameters: IInteractionParameter[] = [];
  for (const assignment of setAssignments) {
    const target: IWFParameter | undefined = outputParams.find(
      (p) => String(p.name).toLowerCase() === assignment.name.toLowerCase()
    );
    if (!target) {
      const available: string = outputParams
        .map((p) => p.name)
        .filter(Boolean)
        .join(", ");
      return {
        parameters: [],
        error:
          `INTERACTION SET target '${assignment.name}' is not an output parameter of this task. ` +
          `MAP_OUTPUT it first. Available task outputs: ${available || "(none)"}.`
      };
    }
    const coerced: string | number | boolean = coerceInteractionFixedValue(assignment.value, String(target.dataType));
    parameters.push({
      id: target.id,
      isMailParameter: false,
      value: {
        ...(target.defaultValue || {}),
        label: "Fixed Value",
        category: ESymbolCategory.fixed,
        fixedValue: coerced
      } as ISymbol
    });
  }

  const coveredIds: Set<string> = new Set(parameters.map((p) => String(p.id)));
  const uncoveredInternal: IWFParameter[] = outputParams.filter(
    (p) => !coveredIds.has(String(p.id)) && !p.isBusinessObject
  );

  let coverageWarning: string | undefined;
  if (parameters.length > 0 && uncoveredInternal.length > 0) {
    const names: string = uncoveredInternal
      .map((p) => p.name)
      .filter(Boolean)
      .join(", ");
    coverageWarning =
      `Interaction leaves ${uncoveredInternal.length} INTERNAL task output(s) unset (${names}). ` +
      `An internal output has no form field, so ResumeProcess freezes it at its default (false for a Boolean) ` +
      `and a gateway testing it can never take the other branch — add a SET for each. ` +
      `(A business-object output is bound from the approver's form field instead, so leaving THAT out of the SET is correct.)`;
  }

  return { parameters, coverageWarning };
}

export function workflowAddUserInteractionAction(sdInstance: ScreenDesignInstance, input: any) {
  const step = forceSyncAndFindNode(sdInstance, input.taskId);
  if (!step) return { error: "Task not found." };

  if (!step.userInteractions) step.userInteractions = [];

  const interactionLabel = input.interactionData?.label || "New Interaction";
  const existingInteraction = step.userInteractions.find((i: any) => i.label === interactionLabel);
  if (existingInteraction) {
    return { success: true, message: `User interaction '${interactionLabel}' already exists.` };
  }

  // Resolve `SET name=value` decision payload (from the DSL) into real interaction
  // parameters. `parameters` (already-built IInteractionParameter[]) is still
  // accepted for the direct-call path; `setParameters` is the DSL name/value form.
  let builtParameters: IInteractionParameter[] = input.interactionData?.parameters || [];
  let coverageWarning: string | undefined;
  const setAssignments: IInteractionSetAssignment[] = input.interactionData?.setParameters || [];
  if (setAssignments.length > 0) {
    const sd = getValues(sdInstance) as any;
    const wfParams: IWFParameter[] = collectWorkflowParamsUnion(sdInstance, sd) as IWFParameter[];
    const stepOutputIds: string[] = (step.outputParameters || []).map((o: any) => o.id);
    const resolved: IBuiltInteractionParameters = buildInteractionParameters(setAssignments, stepOutputIds, wfParams);
    if (resolved.error) return { error: resolved.error };
    builtParameters = resolved.parameters;
    coverageWarning = resolved.coverageWarning;
  }

  step.userInteractions.push({
    id: globalThis.crypto ? globalThis.crypto.randomUUID() : Math.random().toString(),
    label: interactionLabel,
    parameters: builtParameters
  });

  const wp = getWp(sdInstance);
  if (wp && wp.updateWfStep) wp.updateWfStep(step);
  const baseMessage = "User interaction added.";
  return { success: true, message: coverageWarning ? `${baseMessage} ${coverageWarning}` : baseMessage };
}

export function workflowSetUserTaskDueDateAction(sdInstance: ScreenDesignInstance, input: any) {
  const step = forceSyncAndFindNode(sdInstance, input.taskId);
  if (!step) return { error: "Task not found." };

  step.dueIn = { dueUnit: input.dueDateConfig.dueUnit, dueValue: input.dueDateConfig.dueValue };

  const wp = getWp(sdInstance);
  if (wp && wp.updateWfStep) wp.updateWfStep(step);
  return { success: true, message: "Task due date set." };
}

export function workflowSetUserTaskNotificationPreferenceAction(sdInstance: ScreenDesignInstance, input: any) {
  const step = forceSyncAndFindNode(sdInstance, input.taskId);
  if (!step) return { error: "Task not found." };

  step.notifyUser = input.sendNotification;
  if (input.sendNotification === false) {
    step.addUserInteractionsToMail = false;
    step.mailInteractions = [];
    step.addGoToTaskButtonToMail = false;
    step.selectedAppIdsForNotification = [];
    step.templateId = undefined;
    step.templateParameters = undefined;
    step.useCustomTemplate = false;
  }

  const wp = getWp(sdInstance);
  if (wp && wp.updateWfStep) wp.updateWfStep(step);
  return { success: true, message: "Notification preference updated." };
}

/**
 * Find an action ALREADY attached to a step that is the same definition as `def`
 * — primarily by `actionDefinitionId`, falling back to a case-insensitive label
 * match. Attaching is otherwise NOT idempotent: a script that times out mid-run
 * (the ~90s ui_workflow_dsl ceiling on large scripts) gets re-issued by the
 * builder, and a second `STEP_ACTION` for the same action would stack a duplicate
 * on the step — and since only one pass's MAP_INPUTs land, the extra copies show
 * up with empty inputs. Reusing the existing attachment makes STEP_ACTION/PRE_ACTION
 * idempotent (mirrors the SYSTEM/TASK "already exists" guard + the add_action_to_event
 * dedup key on actionDefinitionId).
 */
export function findAttachedActionByDefinition(step: any, def: any): any {
  const actions: any[] = Array.isArray(step?.actions) ? step.actions : [];
  if (actions.length === 0) return undefined;
  const defId: string | undefined = def?.id || def?.Id;
  const defLabel: string = String(def?.displayName || def?.name || def?.Name || "").toLowerCase();
  return actions.find((a: any) => {
    if (defId && a?.actionDefinitionId && String(a.actionDefinitionId) === String(defId)) return true;
    const aLabel: string = String(a?.label ?? a?.description ?? "").toLowerCase();
    return defLabel.length > 0 && aLabel === defLabel;
  });
}

/**
 * KOA-18136 §37 — the refusal an attach gets when the host step cannot be VERIFIED.
 *
 * An attach used to proceed on an unresolvable step: `forceSyncAndFindNode` returned
 * nothing, so the idempotency probe saw "nothing attached yet", and the write went ahead
 * anyway. Two consequences, both observed live (session be14b8f5):
 *
 *   • it CREATES A FRESH, EMPTY action — every later `MAP_INPUT` for that step fails
 *     "Step not found.", so the action ships with its parameters at their fixed-value
 *     defaults. That is precisely the "the send-mail action's parameters were all left
 *     empty" report;
 *   • on a REPAIR script it stacks a second copy next to a LIVE action that already had
 *     its inputs bound — the repair overwrites working configuration with a blank one.
 *
 * The step is unverifiable for exactly two reasons, and the message must separate them:
 * a canvas that cannot be read (retryable, nothing to re-author) versus an id that does
 * not exist (the snapshot is stale — LIST first). Refusing is the only answer that
 * cannot destroy work: nothing is written, and the author is told what to do.
 */
function unverifiedStepRefusal(actionLabel: string, stepId: string, what: string): string {
  const canvasReadable: boolean = isBpmnModelerReady();
  if (!canvasReadable) {
    return (
      `Refused to attach ${what} '${actionLabel}': the BPMN canvas cannot be read right now, so ${stepId} ` +
      `cannot be verified. NOTHING was written. Do NOT re-author or reduce the process — re-send this line ` +
      `unchanged once the process canvas is open again.`
    );
  }
  return (
    `Refused to attach ${what} '${actionLabel}': step ${stepId} does not exist in this process. NOTHING was ` +
    `written (attaching to an unverified step creates a fresh action with EMPTY parameters and hides the ` +
    `real one). Put \`LIST\` as the first line of your next script and use only ids from that output.`
  );
}

export function workflowAddPreActionToUserTaskAction(sdInstance: ScreenDesignInstance, input: any) {
  const wp = getWp(sdInstance);
  if (!wp || !wp.setSelectedWfNodeId || !wp.addActionToWorkflowStep) {
    return { error: "Native handlers not available." };
  }

  const resolved = resolveWorkflowAction(sdInstance, input.actionName);
  if (!resolved.ok) return { error: resolved.error };

  const { action, label } = buildStepAction(resolved.def);
  const step = forceSyncAndFindNode(sdInstance, input.taskId);
  // KOA-18136 §37 — an unverifiable host is a REFUSAL, never a fresh empty action.
  if (!step) return { error: unverifiedStepRefusal(label, String(input.taskId), "pre-action") };

  const existing = findAttachedActionByDefinition(step, resolved.def);
  if (existing) {
    return {
      success: true,
      message: `Pre-action '${label}' is already attached to this task — reused the existing one (no duplicate created). Bind its inputs with MAP_INPUT.`
    };
  }

  wp.setSelectedWfNodeId(input.taskId);
  wp.addActionToWorkflowStep(action as never, 26);
  // KOA-17362 — name the REAL host here too. PRE_ACTION and STEP_ACTION write the
  // SAME `actions` array with the same shape; what a stored action MEANS is decided
  // by the node type at code-generation time (pre-logic on a user task, the step's
  // own work on a system step). A message that asserts the host instead of reading
  // it is therefore a claim the data cannot back — exactly how the sibling call
  // reported a user-task write as a system-step write.
  const hostType: string = bpmnElementTypeOf(input.taskId) ?? "step";
  const targetName: string = step?.name || input.taskId;
  return { success: true, message: `Pre-action '${label}' added to ${targetName} (${hostType}).` };
}

export function workflowAddActionToSystemStepAction(sdInstance: ScreenDesignInstance, input: any) {
  const wp = getWp(sdInstance);
  if (!wp || !wp.setSelectedWfNodeId || !wp.addActionToWorkflowStep) {
    return { error: "Native handlers not available." };
  }

  const resolved = resolveWorkflowAction(sdInstance, input.actionName);
  if (!resolved.ok) return { error: resolved.error };

  const { action, label } = buildStepAction(resolved.def);
  // Idempotency: if this same action is already attached (e.g. a prior large
  // script applied it before timing out, and the builder re-issued STEP_ACTION),
  // reuse it instead of stacking a duplicate-with-empty-inputs.
  const step = forceSyncAndFindNode(sdInstance, input.stepId);

  // KOA-17362 — refuse the host that cannot carry a step action at all. Nothing
  // downstream can detect this: the model has a single `actions` array on every
  // step, so the write "succeeds", LIST counts it, and only the generated code
  // quietly drops the result. See `userTaskActionRefusal`.
  if (isUserTaskNode(input.stepId)) {
    return { error: userTaskActionRefusal(label, input.stepId, step?.name || input.stepId) };
  }

  // KOA-18136 §37 — an unverifiable host is a REFUSAL, never a fresh empty action.
  if (!step) return { error: unverifiedStepRefusal(label, String(input.stepId), "action") };

  const existing = findAttachedActionByDefinition(step, resolved.def);
  if (existing) {
    return {
      success: true,
      message: `Action '${label}' is already attached to this step — reused the existing one (no duplicate created). Bind its inputs with MAP_INPUT.`
    };
  }

  wp.setSelectedWfNodeId(input.stepId);
  wp.addActionToWorkflowStep(action as never, 26);
  // Name the REAL host. The old text said "system step" unconditionally, which is
  // how a user-task write got confirmed as a system-step write for 13 smokes.
  const hostType: string = bpmnElementTypeOf(input.stepId) ?? "step";
  return { success: true, message: `Action '${label}' added to ${step?.name || input.stepId} (${hostType}).` };
}

/**
 * Flatten the actions attached to a step into what output-source resolution needs:
 * the action's definition id (how `SetOutputs` finds it), its instance id, a
 * display name, and the fields of its RESULT type. The step carries only
 * `actionDefinitionId`; the result shape lives on the action DEFINITION
 * (`returnTypeModel.elements`), so the definitions list is the second input.
 */
/**
 * Classify a definition's statement so output resolution can tell a scalar VALUE
 * from a rows-affected COUNT (see `workflow-output-source.ts`). Only the DB
 * statement types answer this; anything else stays undefined rather than guessing.
 *
 * The platform sets this itself when the action is saved: a statement whose result
 * has columns becomes `CustomExecuteReader`, one with none becomes
 * `CustomExecuteNonQuery` with `ReturnTypeName = "Int32"`
 * (`DatasourceService.Internal_SaveAction`). An AI-authored `INSERT` with no
 * OUTPUT/SELECT therefore arrives here as a non-query with zero result fields —
 * which is exactly the case resolution must refuse.
 */
function statementKindOf(def: IActionDefinitionDto | undefined): OutputSourceStatementKind | undefined {
  switch (def?.statementType) {
    case EStatementTypes.Save:
    case EStatementTypes.Delete:
    case EStatementTypes.CustomExecuteNonQuery:
      return "write";
    case EStatementTypes.Select:
    case EStatementTypes.CustomExecuteReader:
      return "read";
    default:
      return undefined;
  }
}

function collectStepOutputSourceActions(sd: any, step: any): IOutputSourceAction[] {
  const attached: any[] = Array.isArray(step?.actions) ? step.actions : [];
  const defs: IActionDefinitionDto[] = Array.isArray(sd?.actionDefinitions) ? sd.actionDefinitions : [];
  const result: IOutputSourceAction[] = [];
  attached.forEach((a: any): void => {
    const definitionId: string = String(a?.actionDefinitionId ?? "");
    if (definitionId.length === 0) return;
    const def: IActionDefinitionDto | undefined = defs.find(
      (d: IActionDefinitionDto): boolean => d.id === definitionId
    );
    const elements: IKTypeField[] = Array.isArray(def?.returnTypeModel?.elements) ? def!.returnTypeModel.elements! : [];
    const resultFields: IOutputSourceField[] = elements
      .filter((f: IKTypeField): boolean => Boolean(f?.name))
      .map((f: IKTypeField): IOutputSourceField => ({ name: f.name, type: f.type?.name }));
    result.push({
      definitionId,
      instanceId: a?.id ? String(a.id) : undefined,
      displayName: String(a?.label || def?.displayName || def?.name || a?.description || ""),
      definitionName: def?.name,
      resultFields,
      statementKind: statementKindOf(def)
    });
  });
  return result;
}

export function workflowMapStepOutputParameterAction(sdInstance: ScreenDesignInstance, input: any) {
  const step = forceSyncAndFindNode(sdInstance, input.nodeId);
  if (!step) return { error: "Step not found." };

  const sd = getValues(sdInstance) as any;

  // Read from the multi-ref UNION, not a single `sd.selectedWf.parameters` — the
  // forceSyncAndFindNode above can swap selectedWf for a provider copy that lacks
  // a PARAM created earlier in THIS script (same race CONDITION/MAP_INPUT hit).
  const allParams: any[] = collectWorkflowParamsUnion(sdInstance, sd);
  const foundParam = allParams.find(
    (p: any) => p.id === input.parameterId || String(p.name).toLowerCase() === String(input.parameterId).toLowerCase()
  );

  if (!foundParam) {
    const available: string = allParams
      .map((p: any) => p.name)
      .filter(Boolean)
      .join(", ");
    return {
      error:
        `Parameter '${input.parameterId}' not found in workflow. Create it first with ` +
        `\`PARAM INTERNAL ${input.parameterId} <Type>\` in this same script. Available: ${available || "(none)"}.`
    };
  }

  // KOA-17362 — a USER TASK's output can never come from an action RESULT.
  //
  // The generated screen-interaction template emits NO output-mapping block at all
  // (the generator substitutes an empty string for it on this step type), so an
  // action result mapped here is discarded at runtime while every layer reports
  // success. A user task's outputs legitimately come from its INTERACTION buttons
  // or its task form, which is why a plain `MAP_OUTPUT <task> "<param>"` with no
  // FROM stays fully supported below — only sourcing it from an action is refused.
  const targetIsUserTask: boolean = isUserTaskNode(String(input.nodeId));
  if (targetIsUserTask && input.sourceActionName) {
    return {
      error:
        `Cannot source this output from an action: "${step.name || input.nodeId}" is a USER TASK ` +
        `(bpmn:UserTask), and a user task's generated code contains no output mapping — the result of ` +
        `'${String(input.sourceActionName)}' would be discarded and every later step reading ` +
        `'${foundParam.name}' would get nothing.\n\n` +
        `A user task's outputs come from the person: declare them with ` +
        `\`MAP_OUTPUT ${input.nodeId} "${foundParam.name}"\` (no FROM) and let an ` +
        `\`INTERACTION\` button SET them, or let the task form's field carry them (a \`PARAM BO\`).\n` +
        `If the VALUE must come from an action instead, that work belongs on a system step: ` +
        `create one with \`SYSTEM\`, attach the action there, and MAP_OUTPUT ... FROM it there.`
    };
  }

  // Resolve WHERE the value comes from BEFORE mutating the step, so an
  // unresolvable `FROM` is a hard error the script can fix rather than a mapping
  // that silently persists with no source (see `workflow-output-source.ts`).
  //
  // On a user task the candidate list is deliberately EMPTY: with no output mapping
  // in the generated code there is nothing to auto-bind to, and claiming
  // "auto-bound to X" would be the same false confirmation in a quieter voice.
  const sourceActions: IOutputSourceAction[] = targetIsUserTask ? [] : collectStepOutputSourceActions(sd, step);
  const source: OutputSourceResolution = resolveOutputSource(sourceActions, {
    parameterName: String(foundParam.name ?? ""),
    parameterType: foundParam.dataType ? String(foundParam.dataType) : undefined,
    sourceActionName: input.sourceActionName ? String(input.sourceActionName) : undefined,
    sourceFieldName: input.sourceFieldName ? String(input.sourceFieldName) : undefined
  });

  if (source.kind === "unknown-action") {
    return {
      error:
        `FROM source action '${String(input.sourceActionName)}' is not attached to this step. ` +
        `Attach it with \`STEP_ACTION\` first, or use one of: ${source.available.join(", ") || "(none)"}.`
    };
  }
  if (source.kind === "unknown-field") {
    return {
      error:
        `Action '${source.actionDisplayName}' has no result field '${String(input.sourceFieldName ?? foundParam.name)}'. ` +
        `Available result fields: ${source.available.join(", ") || "(none — the action returns a scalar, so drop the .field suffix)"}.`
    };
  }
  if (source.kind === "ambiguous") {
    return {
      error:
        `Output '${foundParam.name}' matches more than one action result on this step ` +
        `(${source.candidates.join(", ")}). Name the source explicitly: ` +
        `\`MAP_OUTPUT <ref> "${foundParam.name}" FROM <actionName>.<field>\`.`
    };
  }
  // KOA-17362 — a write with no OUTPUT has no value to give.
  if (source.kind === "write-without-result") {
    return {
      error:
        `Action '${source.actionDisplayName}' writes rows and returns no result set, so ` +
        `'${foundParam.name}' would be set to the number of rows affected (1), not to a value ` +
        `from the row — every later step reading '${foundParam.name}' would then match nothing.\n\n` +
        `Give the action a result: recreate its SQL so it RETURNS the value, e.g. ` +
        `\`INSERT INTO ... ; SELECT @Id AS Id;\` (an insert that returns the new key), then ` +
        `\`MAP_OUTPUT <ref> "${foundParam.name}" FROM ${source.actionDisplayName}.Id\`.\n` +
        `If the value is one the PROCESS already owns (the instance id, the initiator), map it ` +
        `from there instead of from an action.`
    };
  }

  if (sd && typeof sd.setWFStepParameters === "function") {
    // MAP_OUTPUT is INCREMENTAL — the DSL maps ONE output param per line. But
    // `setWFStepParameters` REPLACES `step.outputParameters` with exactly the set
    // it is handed (correct for the UI multi-select panel, which submits the WHOLE
    // selection so a deselect can drop a param). Handing it only the new param
    // therefore WIPES any output an earlier MAP_OUTPUT mapped on the SAME task —
    // so a two-output decision task (e.g. managerApproved + rejectReason) ends up
    // with just the last one, and a following `INTERACTION … SET <earlierOutput>=…`
    // hard-fails ("not an output parameter of this task"), so its decision button
    // never renders (KOA-17362). Union the already-mapped output ids with the new
    // one so repeated MAP_OUTPUT calls ACCUMULATE; the setter re-preserves each
    // existing param's mapping when its id is present again, so this is idempotent.
    const existingOutputs: IStepParameter[] = Array.isArray(step.outputParameters) ? step.outputParameters : [];
    const existingOutputIds: string[] = existingOutputs
      .map((p: IStepParameter): string => p?.id)
      .filter((id: string): boolean => Boolean(id));
    const mergedOutputIds: string[] = Array.from(new Set<string>([...existingOutputIds, foundParam.id]));
    const paramsToMap: IStepParameter[] = mergedOutputIds.map((id: string): IStepParameter => ({ id }));
    sd.setWFStepParameters(step, paramsToMap, 3);

    // The setter creates a NEW output entry as an empty fixed-value symbol (the
    // panel's "now pick a source" state). On a system step that source is an
    // action result, and nobody comes back to pick it — so write it here. Only
    // ever overwrite the empty placeholder: an entry that already carries a real
    // symbol (a previous MAP_OUTPUT FROM, an INTERACTION SET, or a hand-pick)
    // stays untouched, keeping repeated MAP_OUTPUTs idempotent.
    const wp = getWp(sdInstance);
    let sourceNote: string = "";
    if (source.kind === "bound") {
      const entries: IStepParameter[] = Array.isArray(step.outputParameters) ? step.outputParameters : [];
      const entry: IStepParameter | undefined = entries.find((p: IStepParameter): boolean => p?.id === foundParam.id);
      if (entry) {
        const bound: ISymbol = buildActionResultSymbol({
          actionDefinitionId: source.actionDefinitionId,
          actionDisplayName: source.actionDisplayName,
          resultActionId: source.resultActionId,
          fieldName: source.fieldName,
          type: source.fieldType,
          targetType: foundParam.dataType ? String(foundParam.dataType) : undefined
        });
        const current: ISymbol | undefined = entry.value as ISymbol | undefined;
        const currentIsEmptyPlaceholder: boolean =
          !current ||
          current.category === undefined ||
          (current.category === ESymbolCategory.fixed &&
            (current.fixedValue === undefined || String(current.fixedValue).length === 0));
        if (currentIsEmptyPlaceholder) {
          entry.value = bound;
          if (wp && wp.updateWfStep) wp.updateWfStep(step);
        }
      }
      const fieldSuffix: string = source.fieldName ? `.${source.fieldName}` : "";
      const verb: string = source.inferred ? "auto-bound to" : "bound to";
      sourceNote = ` Value ${verb} ${source.actionDisplayName}${fieldSuffix}.`;
    } else if (sourceActions.length > 0) {
      // A system step whose output has no resolvable source would generate `= ""`.
      // Say so on the spot; `kuika/step-output-unbound` reports it at LINT too.
      sourceNote =
        ` WARNING: no value source — this output will generate as an empty string. ` +
        `Bind it with \`MAP_OUTPUT <ref> "${foundParam.name}" FROM <actionName>.<field>\`.`;
    }

    adoptCanvasIntoModel(wp);

    return {
      success: true,
      message: `Parameter '${foundParam.name}' successfully mapped as step output.${sourceNote}`
    };
  }

  return { error: "Native handler 'setWFStepParameters' not found on context." };
}

/** Minimal shape of a workflow parameter the MAP_INPUT resolver needs. */
export interface IMapInputParamRef {
  name: string;
  id?: string;
  dataType?: string;
}

export type MapInputResolution<T extends IMapInputParamRef> =
  | { kind: "parameter"; param: T }
  | { kind: "instance"; inputName: string; label: string; type: string }
  | { kind: "literal"; value: string }
  | { kind: "default"; defaultName: string }
  | { kind: "error"; available: string[] };

/**
 * Bare MAP_INPUT keywords that resolve to a platform-generated/default value
 * (an `ESymbolCategory.default` symbol → codegen `{default:<Name>}`), mirroring
 * the screen Binder's PK→NewGuid / user-FK→CurrentUserid defaults. Lets a
 * workflow Insert bind its PK to a generated Guid (`MAP_INPUT @save "Id" NewGuid`)
 * instead of leaving it empty or forcing the user to type a Guid. Keys are
 * lower-cased aliases; values are the canonical `defaultValueName`.
 */
const MAP_INPUT_PLATFORM_DEFAULTS: Record<string, string> = {
  newguid: "NewGuid",
  now: "Now",
  currentdate: "Now",
  today: "Now",
  empty: "Empty",
  currentuserid: "CurrentUserid",
  currentusername: "CurrentUsername"
};

/**
 * Bare MAP_INPUT keywords that resolve to a process-INSTANCE parameter — a value
 * the RUNNING PROCESS carries about itself (the initiator's identity, the
 * instance / step ids). The designer symbol picker surfaces these under the
 * "Common" group as `ESymbolCategory.processParameter` leaves whose `inputName`
 * is a FIXED keyword (NOT a workflow param id) — see
 * `designer-symbol-picker-engine.tsx`. Exposing them here lets a workflow step
 * bind e.g. `SendMail.receivers` to the REQUESTER — the process INITIATOR's
 * e-mail/username — which is the correct source on a step an APPROVER runs,
 * where `CurrentUserid` would be the wrong user (KOA-17362). Keys are lower-cased
 * aliases; `inputName` is the exact keyword the picker emits. The initiator/instance
 * identities are String; the multi-instance counters (`mi.instanceCount` /
 * `mi.completedCount`) are Int32 — so each source carries its own `type` (the
 * picker sets these `domainType`s at `designer-symbol-picker-engine.tsx`).
 */
interface InstanceParamSource {
  readonly inputName: string;
  readonly label: string;
  /** Backend value type of the instance value — String for identities, Int32 for MI counters. */
  readonly type: string;
}
const MAP_INPUT_INSTANCE_PARAMS: Record<string, InstanceParamSource> = {
  processinitiator: { inputName: "processInitiator", label: "Process Initiator Email/Username", type: "String" },
  processinitiatoremail: { inputName: "processInitiator", label: "Process Initiator Email/Username", type: "String" },
  processinitiatorusername: {
    inputName: "processInitiator",
    label: "Process Initiator Email/Username",
    type: "String"
  },
  processinitiatorfullname: {
    inputName: "processInitiatorFullName",
    label: "Process Initiator FullName",
    type: "String"
  },
  processinstanceid: { inputName: "processInstance", label: "ProcessInstanceId", type: "String" },
  stepinstanceid: { inputName: "workflowStepInstanceId", label: "StepInstanceId", type: "String" },
  // Multi-instance counters (Int32). The picker surfaces these only when the step
  // is MI-enabled; the DSL resolver accepts the keyword either way and the symbol
  // is inert on a non-MI step. Accept both the natural word and the raw picker key.
  miinstancecount: { inputName: "mi.instanceCount", label: "MI Instance Count", type: "Int32" },
  "mi.instancecount": { inputName: "mi.instanceCount", label: "MI Instance Count", type: "Int32" },
  micompletedcount: { inputName: "mi.completedCount", label: "MI Completed Count", type: "Int32" },
  "mi.completedcount": { inputName: "mi.completedCount", label: "MI Completed Count", type: "Int32" }
};

/**
 * Decide how a MAP_INPUT source resolves — WITHOUT touching the diagram. The
 * contract is purely the quote style the parser captured:
 *   • QUOTED source (isLiteral=true)  → always a fixed LITERAL value.
 *   • BARE source   (isLiteral=false) → first a REFERENCE to an existing workflow
 *     parameter; else a PROCESS-INSTANCE keyword (ProcessInitiator / … — the
 *     running process's own values); else a PLATFORM DEFAULT keyword (NewGuid /
 *     Now / Empty / CurrentUserid); else `error` rather than silently coercing
 *     the bare word into literal text (the "SendMail receivers = \"employeeId\""
 *     corruption).
 * Param match wins over the keyword tables so an explicit same-named parameter
 * is never shadowed. Pure + side-effect-free → unit-testable like
 * planLinkedProcessMappings.
 */
export function resolveMapInputSource<T extends IMapInputParamRef>(
  source: string,
  isLiteral: boolean,
  allParams: T[]
): MapInputResolution<T> {
  if (isLiteral) return { kind: "literal", value: source };
  const lc: string = source.toLowerCase();
  const match: T | undefined = allParams.find((p: T) => String(p.name).toLowerCase() === lc);
  if (match) return { kind: "parameter", param: match };
  const instance: InstanceParamSource | undefined = MAP_INPUT_INSTANCE_PARAMS[lc];
  if (instance) return { kind: "instance", inputName: instance.inputName, label: instance.label, type: instance.type };
  const platformDefault: string | undefined = MAP_INPUT_PLATFORM_DEFAULTS[lc];
  if (platformDefault) return { kind: "default", defaultName: platformDefault };
  return { kind: "error", available: allParams.map((p: T) => p.name).filter(Boolean) };
}

/**
 * MAP_INPUT — bind an attached step-action's INPUT parameter to a workflow
 * parameter (by name) or to a fixed literal value. STEP_ACTION attaches an
 * action whose input parameters default to empty fixed-value symbols; this
 * wires a chosen input to a real source so the action does something at
 * runtime (KOA-17362 Faz 5). Mirrors the LINKED_CONFIG input-binding pattern
 * (a `processParameter` symbol) + the MAP_OUTPUT step-find/persist pattern.
 */
export function workflowMapStepActionInputAction(sdInstance: ScreenDesignInstance, input: any) {
  const step = forceSyncAndFindNode(sdInstance, input.stepId);
  if (!step) return { error: "Step not found." };

  const actions: any[] = Array.isArray(step.actions) ? step.actions : [];
  if (actions.length === 0) {
    return { error: "Step has no attached action. Attach one with STEP_ACTION first, then MAP_INPUT its parameters." };
  }

  const lcParam: string = String(input.parameterName).toLowerCase();
  let targetParam: any;
  for (const a of actions) {
    const params: any[] = Array.isArray(a.parameters) ? a.parameters : [];
    const found = params.find((pp: any) => String(pp.name).toLowerCase() === lcParam);
    if (found) {
      targetParam = found;
      break;
    }
  }
  if (!targetParam) {
    const available: string = actions
      .flatMap((a: any) => (Array.isArray(a.parameters) ? a.parameters : []))
      .map((p: any) => p.name)
      .filter(Boolean)
      .join(", ");
    return {
      error: `Input parameter '${input.parameterName}' not found on the step's action(s). Available: ${available || "(none)"}.`
    };
  }

  const binding = bindActionInputSource(
    sdInstance,
    targetParam,
    String(input.source),
    input.isLiteral === true,
    "MAP_INPUT"
  );
  if (!binding.success) return { error: binding.error };

  const wp = getWp(sdInstance);
  if (wp && wp.updateWfStep) wp.updateWfStep(step);
  adoptCanvasIntoModel(wp);

  return { success: true, message: binding.message };
}

/** KOA-18593 — what a dynamic assignment's rows name: people (their user id) or roles (their name). */
export type DynamicAssigneeKind = "User" | "Role";

/**
 * KOA-18593 — the field of each returned row that says who gets the task.
 *
 * One field for both paths the engine has, read from the code generator and the repeat primitive:
 * a single task reads the configured field of every row as `User#<value>` / `Role#<value>` (a user
 * id or username; a role name or id), while a task that repeats per user reads each row's `Id` and
 * ignores the configured field altogether. `Id` is the one name both read.
 */
export const DYNAMIC_ASSIGNEE_FIELD: string = "Id";

/** KOA-18593 — `ASSIGN_DYNAMIC`: the user task, the action that returns who gets it, and what it returns. */
export interface IDynamicAssignmentInput {
  stepId: string;
  actionName: string;
  returns: DynamicAssigneeKind;
}

/** KOA-18593 — `ASSIGN_INPUT`: one input of a task's dynamic assignment action, and its source. */
export interface IDynamicAssignmentInputBinding {
  stepId: string;
  parameterName: string;
  source: string;
  isLiteral: boolean;
}

/** KOA-18593 — what a script step action reports back. */
export interface IScriptStepResult {
  success?: boolean;
  message?: string;
  error?: string;
}

/**
 * KOA-18593 — why an action cannot say who gets a task, or `undefined` when it can.
 *
 * The designer's own panel offers an action that returns a single text value, or rows with a text
 * field. Rows are read by their `Id` (see `DYNAMIC_ASSIGNEE_FIELD`), so a row type that is known and
 * has no `Id` cannot assign anyone. A row type the catalog does not describe is not judged.
 */
function dynamicAssigneeShapeRefusal(def: IActionDefinitionDto, actionName: string): string | undefined {
  const returnsOneValue: boolean = def.returnTypeName === "String" && def.returnCardinality === ECardinality.One;
  if (returnsOneValue) return undefined;
  const fields: IKTypeField[] = def.returnTypeModel?.elements ?? [];
  if (fields.length === 0) return undefined;
  const hasId: boolean = fields.some(
    (field: IKTypeField): boolean =>
      field.isList !== true && field.name.toLowerCase() === DYNAMIC_ASSIGNEE_FIELD.toLowerCase()
  );
  if (hasId) return undefined;
  return (
    `ASSIGN_DYNAMIC refused: '${actionName}' returns rows without an \`${DYNAMIC_ASSIGNEE_FIELD}\` field ` +
    `(it returns: ${fields.map((field: IKTypeField): string => field.name).join(", ")}). Each row's ` +
    `${DYNAMIC_ASSIGNEE_FIELD} is who gets the task — a person's user id, or a role's name — so the action ` +
    "has to return it."
  );
}

/**
 * KOA-18593 — `ASSIGN_DYNAMIC <task> "<action>" returns:<User|Role>`: the user task is assigned by an
 * action that returns who gets it, run by the engine when the task is created.
 *
 * Written exactly as the designer's assignment panel writes it — assignment type Dynamic, the action
 * as an attached instance with its parameters, what it returns, and the row field to read — and on
 * the task itself (`useLaneAssignment: false`), so a lane cannot overwrite it. The script could only
 * assign a task to a ROLE before (`LANE` / `LANE_ASSIGN`), and a dynamic assignment without an action
 * fails design validation, so every "the requester's manager" task was built as a role lane.
 * Re-issuing it for the same action keeps the inputs already bound.
 */
export function workflowSetDynamicAssignmentAction(
  sdInstance: ScreenDesignInstance,
  input: IDynamicAssignmentInput
): IScriptStepResult {
  if (!isUserTaskNode(input.stepId)) {
    return {
      error:
        `ASSIGN_DYNAMIC applies to a USER task — the step a person performs; '${input.stepId}' is not one. ` +
        "A system step runs its action with STEP_ACTION."
    };
  }
  const step: IWFStep | undefined = forceSyncAndFindNode(sdInstance, input.stepId);
  if (!step) return { error: unverifiedStepRefusal(input.actionName, String(input.stepId), "dynamic assignment") };

  const resolved = resolveWorkflowAction(sdInstance, input.actionName);
  if (!resolved.ok) return { error: resolved.error };
  const shapeRefusal: string | undefined = dynamicAssigneeShapeRefusal(resolved.def, input.actionName);
  if (shapeRefusal !== undefined) return { error: shapeRefusal };

  const held: IAction | undefined = step.assignment?.dynamicAssignmentAction;
  const reused: boolean = held !== undefined && String(held.actionDefinitionId) === String(resolved.def.id);
  const action: IAction = reused ? (held as IAction) : (buildStepAction(resolved.def).action as unknown as IAction);
  const returnsOneValue: boolean =
    resolved.def.returnTypeName === "String" && resolved.def.returnCardinality === ECardinality.One;

  const assignment: IWFNodeAssignment = {
    assignmentType: EWFAssignmentType.dynamic,
    assignmentId: "Dynamic",
    dynamicAssignmentAction: action,
    dynamicAssignmentActionReturnType: input.returns,
    dynamicAssignmentActionField: returnsOneValue
      ? undefined
      : ({
          category: ESymbolCategory.fixed,
          fixedValue: DYNAMIC_ASSIGNEE_FIELD,
          label: DYNAMIC_ASSIGNEE_FIELD
        } as ISymbol),
    roleAssignmentParameter: undefined
  };
  step.assignment = assignment;
  step.useLaneAssignment = false;

  const wp = getWp(sdInstance);
  if (wp && wp.updateWfStep) wp.updateWfStep(step);
  adoptCanvasIntoModel(wp);

  const inputs: string[] = (Array.isArray(action.parameters) ? action.parameters : [])
    .map((param: IActionParameter): string => String(param?.name ?? ""))
    .filter((name: string): boolean => name.length > 0);
  const bindHint: string =
    inputs.length > 0 ? ` Bind its inputs with ASSIGN_INPUT: ${inputs.join(", ")}.` : " It takes no inputs.";
  return {
    success: true,
    message:
      `Task '${step.name || input.stepId}' is assigned by '${resolved.def.name}' (returns ${input.returns}` +
      `${returnsOneValue ? "" : `, each row's ${DYNAMIC_ASSIGNEE_FIELD}`}).${reused ? " Existing inputs kept." : ""}${bindHint}`
  };
}

/**
 * KOA-18593 — `ASSIGN_INPUT <task> <input> [=] <source>`: bind one input of the task's dynamic
 * assignment action, by the same resolution `MAP_INPUT` uses for a system step's action.
 */
export function workflowMapDynamicAssignmentInputAction(
  sdInstance: ScreenDesignInstance,
  input: IDynamicAssignmentInputBinding
): IScriptStepResult {
  const step: IWFStep | undefined = forceSyncAndFindNode(sdInstance, input.stepId);
  if (!step) return { error: "Task not found." };
  const action: IAction | undefined = step.assignment?.dynamicAssignmentAction;
  if (action === undefined) {
    return {
      error:
        `Task '${step.name || input.stepId}' has no dynamic assignment action. Set it with ASSIGN_DYNAMIC first, ` +
        "then ASSIGN_INPUT its inputs."
    };
  }
  const params: IActionParameter[] = Array.isArray(action.parameters) ? action.parameters : [];
  const wanted: string = String(input.parameterName).toLowerCase();
  const target: IActionParameter | undefined = params.find(
    (param: IActionParameter): boolean => String(param?.name ?? "").toLowerCase() === wanted
  );
  if (target === undefined) {
    const available: string = params
      .map((param: IActionParameter): string => String(param?.name ?? ""))
      .filter((name: string): boolean => name.length > 0)
      .join(", ");
    return {
      error: `Input '${input.parameterName}' is not an input of the task's assignment action. Available: ${available || "(none)"}.`
    };
  }
  const binding: IActionInputBinding = bindActionInputSource(
    sdInstance,
    target as unknown as IBindableActionInput,
    String(input.source),
    input.isLiteral === true,
    "ASSIGN_INPUT"
  );
  if (!binding.success) return { error: binding.error };

  const wp = getWp(sdInstance);
  if (wp && wp.updateWfStep) wp.updateWfStep(step);
  adoptCanvasIntoModel(wp);
  return { success: true, message: binding.message };
}

/** KOA-18593 — the fields of an action input the source binding reads and writes. */
interface IBindableActionInput {
  name?: string;
  symbol?: ISymbol;
  Typename?: string;
  dataType?: string;
}

/** KOA-18593 — a bound input: what was bound, or why nothing was. */
interface IActionInputBinding {
  success: boolean;
  /** Set when `success`. */
  message?: string;
  /** Set when not `success`. */
  error?: string;
}

/**
 * The binding half of `MAP_INPUT`, shared with `ASSIGN_INPUT` (KOA-18593): resolve a script source
 * — a workflow parameter by name, a process-instance keyword, a platform default, or a quoted
 * literal — into the symbol an action input carries, and write it onto `targetParam`. One reading
 * for both, so an assignee action's inputs bind exactly the way a step action's inputs do.
 */
function bindActionInputSource(
  sdInstance: ScreenDesignInstance,
  targetParam: IBindableActionInput,
  source: string,
  isLiteral: boolean,
  command: string
): IActionInputBinding {
  // Resolve the workflow parameter from a UNION of every live reference — same
  // script-race MAP_INPUT hits as CONDITION/MAP_OUTPUT (a PARAM created earlier in
  // THIS script may live only on a not-yet-synced provider copy of selectedWf).
  const allParams: IMapInputParamRef[] = collectWorkflowParamsUnion(sdInstance, getValues(sdInstance));

  const resolution = resolveMapInputSource(String(source), isLiteral, allParams);

  if (resolution.kind === "error") {
    return {
      success: false,
      error:
        `Source '${String(source)}' is a bare parameter reference but no workflow parameter ` +
        `with that name exists. Available parameters: ${resolution.available.join(", ") || "(none)"}. ` +
        `Either add it first with \`PARAM INTERNAL ${String(source)} <Type>\` in this same script, ` +
        `reuse one of the available parameters, or — if you meant a constant value — quote it: ` +
        `\`${command} <step> "${targetParam.name}" "<literal value>"\`.`
    };
  }

  let resultMessage: string;
  if (resolution.kind === "parameter") {
    const wfParam = resolution.param;
    targetParam.symbol = buildWorkflowParamRefSymbol({
      name: wfParam.name,
      id: String(wfParam.id ?? ""),
      dataType: String(wfParam.dataType ?? "")
    });
    // Keep Typename === the action input's OWN declared type (target side) so the
    // action panel won't reset our symbol; only fall back to the source type when
    // the input has no declared type. (Mirrors the LINKED_CONFIG input-binding note.)
    targetParam.Typename = targetParam.Typename || targetParam.dataType || wfParam.dataType;
    resultMessage = `Mapped input '${targetParam.name}' to workflow parameter '${wfParam.name}'.`;
  } else if (resolution.kind === "instance") {
    // Process-INSTANCE parameter (e.g. the initiator's e-mail/username) — the
    // symbol picker's "Common" leaf. Built by the canonical factory so the
    // binding is field-for-field what a hand-pick produces; in particular it
    // carries `fieldName`, which `SymbolModel.IsEmpty()` keys on for category 25
    // (omitting it made validation raise KV1170 on a correctly-generated binding).
    targetParam.symbol = buildProcessInstanceSymbol({
      inputName: resolution.inputName,
      label: resolution.label,
      type: resolution.type,
      targetType: targetParam.Typename || targetParam.dataType || resolution.type
    });
    targetParam.Typename = targetParam.Typename || targetParam.dataType || resolution.type;
    resultMessage = `Mapped input '${targetParam.name}' to process instance value '${resolution.label}'.`;
  } else if (resolution.kind === "default") {
    // Platform-generated/default value (NewGuid / Now / Empty / CurrentUserid) —
    // same `ESymbolCategory.default` shape the screen Binder uses for PK→NewGuid.
    targetParam.symbol = buildPlatformDefaultSymbol(
      resolution.defaultName,
      targetParam.Typename || targetParam.dataType
    );
    resultMessage = `Mapped input '${targetParam.name}' to platform default '${resolution.defaultName}'.`;
  } else {
    targetParam.symbol = buildFixedValueSymbol(resolution.value);
    resultMessage = `Mapped input '${targetParam.name}' to fixed value "${resolution.value}".`;
  }

  return { success: true, message: resultMessage };
}

export function workflowUpdatePathNameAction(sdInstance: ScreenDesignInstance, input: any) {
  const { modeling, elementRegistry } = getBpmnModules();
  const element = elementRegistry.get(input.pathId);
  if (!element) return { error: `Path ${input.pathId} not found.` };
  modeling.updateProperties(element, { name: input.newName });
  return { success: true, message: `Updated path name to ${input.newName}` };
}

/**
 * KOA-18136 — the locale-agnostic step↔screen name fold moved to
 * `workflow-step-screen-mapping.ts`, next to the pairing decision it exists to serve
 * (that module documents why the fold is spelled out per Turkish letter instead of
 * using a locale-aware lowercase). Re-exported here so existing importers — including
 * `__tests__/assign-screen-name-fold.test.ts` — are unaffected.
 */
export { foldForScreenNameMatch } from "./workflow-step-screen-mapping";

/**
 * KOA-17362 §7.6 Eksen 1 — the parameter names a user task itself WRITES: its mapped
 * OUTPUT parameters (`MAP_OUTPUT`). Everything else on the managed task form is the
 * request as it arrived, which an assignee reviews rather than edits. Used to derive
 * the deterministic readonly default for the generated screen.
 */
function resolveTaskEditableParamNames(sdInstance: ScreenDesignInstance, sd: any, stepId: string): string[] {
  try {
    const workflows: any[] = sd?.workFlowList || [];
    let step: any;
    for (const wf of workflows) {
      const steps: any[] = sd.findAllStepsRecursively ? sd.findAllStepsRecursively(wf, () => true) : [];
      const found = steps.find((s: any) => s?.id === stepId);
      if (found) {
        step = found;
        break;
      }
    }
    const outputs: any[] = Array.isArray(step?.outputParameters) ? step.outputParameters : [];
    const outputIds: Set<string> = new Set<string>(
      outputs.map((p: any): string => String(p?.id ?? "")).filter((id: string): boolean => id.length > 0)
    );
    if (outputIds.size === 0) return [];
    const allParams: any[] = collectWorkflowParamsUnion(sdInstance, sd);
    return allParams
      .filter((p: any): boolean => outputIds.has(String(p?.id)))
      .map((p: any): string => String(p?.name ?? ""))
      .filter((name: string): boolean => name.length > 0);
  } catch {
    // A step we cannot inspect simply gets no editable set → the default freezes
    // every field, which is the safe shape for a review task.
    return [];
  }
}

/** One assigned step's DSL-authored task-screen configuration. */
interface WorkflowStepFieldConfigInput {
  stepId: string;
  fields: readonly WorkflowFormFieldOverride[];
  /** Titled sections to ensure exist (KOA-17362 §7.6 slice 3). */
  sections?: readonly WorkflowFormSectionOverride[];
  /** Layout of the default (untitled) section. */
  form?: WorkflowFormLayoutOverride;
}

/**
 * KOA-17362 §7.6 slice 4 — where the ResumeProcess / decision buttons sit on EVERY
 * managed task screen (`SCREEN_BUTTONS`).
 *
 * This is deliberately NOT a per-step knob: the setting lives on the PROJECT
 * (`project.workflowScreensSettings.buttonLocation`, the wizard's whole Settings tab)
 * and `WorkflowScreenService` reads it per screen at generate/update time. Exposing it
 * per step would be a lie the model would then write per step. Pure resolution of the
 * author's word → enum, exported for testing.
 */
export function resolveButtonLocation(value: string): EButtonLocation | undefined {
  const folded: string = (value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s\-_]/g, "");
  if (folded === "under" || folded === "underform" || folded === "form") return EButtonLocation.UnderForm;
  if (folded === "top" || folded === "header") return EButtonLocation.Top;
  if (folded === "bottom" || folded === "footer") return EButtonLocation.Bottom;
  return undefined;
}

/**
 * Set the project-wide button location for managed workflow task screens. Mirrors the
 * wizard's Settings tab (`onClickSaveSettingsTab`): write
 * `project.workflowScreensSettings` and persist via `updateProject`. Must run BEFORE
 * ASSIGN_SCREEN in the same script — the backend reads the persisted project JSON when
 * it generates or updates each screen.
 */
export async function workflowSetScreenButtonLocationAction(sdInstance: ScreenDesignInstance, input: any) {
  const sd = getValues(sdInstance) as any;
  const project = sd?.project;
  if (!project) return { error: "Project not found." };

  const location: EButtonLocation | undefined = resolveButtonLocation(String(input?.location ?? ""));
  if (location === undefined) {
    return { error: `Unknown button location '${input?.location}'. Use one of: under, top, bottom.` };
  }

  project.workflowScreensSettings = { ...(project.workflowScreensSettings || {}), buttonLocation: location };

  if (typeof sd.updateProject === "function") {
    await sd.updateProject(project);
  } else if (typeof sd.setProject === "function") {
    sd.setProject(project);
  } else {
    return { error: "Project could not be updated." };
  }

  return {
    success: true,
    message:
      `Task-screen buttons will render ${EButtonLocation[location]} on EVERY managed workflow screen ` +
      `(project-wide setting, not per step). Screens that already exist pick this up the next time they are ` +
      `generated or re-configured — put an ASSIGN_SCREEN in this same script to apply it now.`
  };
}

/** The DSL-authored screen config for one step, if this call carries any. */
function screenConfigForStep(input: any, stepId: string): WorkflowStepFieldConfigInput | undefined {
  const configs: WorkflowStepFieldConfigInput[] = Array.isArray(input.fieldConfigs)
    ? (input.fieldConfigs as WorkflowStepFieldConfigInput[])
    : [];
  return configs.find((c: WorkflowStepFieldConfigInput): boolean => c.stepId === stepId);
}

export async function workflowAssignScreenToUserTaskAction(sdInstance: ScreenDesignInstance, input: any) {
  const sd = getValues(sdInstance) as any;
  const project = sd.project;

  if (!project) return { error: "Project not found." };

  const rawStepIds = input.stepIds || input.stepId;
  if (!rawStepIds) return { error: "stepIds (or stepId) is required." };

  const stepIdsArray = Array.isArray(rawStepIds) ? rawStepIds : [rawStepIds];
  if (stepIdsArray.length === 0) return { error: "No valid step IDs provided." };

  const workflows = sd.workFlowList || [];
  const stepsToProcess: { processId: string; stepId: string }[] = [];
  const stepsToReconfigure: { processId: string; stepId: string; screenId: string }[] = [];

  for (const sId of stepIdsArray) {
    let pId = input.processId;
    let foundWf = workflows.find((wf: any) => String(wf.id) === String(pId));

    if (!foundWf) {
      for (const wf of workflows) {
        const allSteps = sd.findAllStepsRecursively ? sd.findAllStepsRecursively(wf, () => true) : [];
        if (allSteps.some((s: any) => s.id === sId)) {
          foundWf = wf;
          pId = wf.id;
          break;
        }
      }
    }

    if (pId && foundWf) {
      const managedEntry = project.processStepScreenIds?.find(
        (ps: any) => ps.processId === pId && ps.stepId === sId && ps.isManaged
      );

      if (!managedEntry) {
        stepsToProcess.push({ processId: pId, stepId: sId });
      } else if (screenConfigForStep(input, sId) !== undefined && managedEntry.screenId) {
        // KOA-17362 §7.6 slice 4 — the step's managed screen already exists AND this
        // script carries config for it: RE-CONFIGURE it instead of reporting the config
        // as dropped. The backend already supports this (`UpdateWorkflowScreenForStep`
        // regenerates the form DI from the metadata we send, preserving element ids via
        // `CurrentDiSnapshot`); only the designer side used to skip these steps.
        stepsToReconfigure.push({ processId: pId, stepId: sId, screenId: String(managedEntry.screenId) });
      }
    }
  }

  if (stepsToProcess.length === 0 && stepsToReconfigure.length === 0) {
    const hadFieldConfig: boolean = Array.isArray(input.fieldConfigs) && input.fieldConfigs.length > 0;
    return {
      success: true,
      message:
        "All requested screens are already managed/assigned or invalid steps." +
        (hadFieldConfig ? " No config was applied: the requested steps could not be resolved to a managed screen." : "")
    };
  }

  try {
    // Persist the canvas BEFORE reading any metadata: a re-configure reads the EXISTING
    // screen's metadata from the server, so unsaved edits on the open screen would
    // otherwise be read back stale (and then overwritten by what we send).
    if (typeof sd.saveScreen === "function") {
      await sd.saveScreen(true);
    }

    const metadataPromises = stepsToProcess.map(async (item) => {
      const response = await FormService.GetFormMetadataFromWorkflowBo({
        processId: item.processId,
        stepId: item.stepId
      });
      if (response.status === 200 && response.data) {
        return { processId: item.processId, stepId: item.stepId, formMetadata: response.data };
      }
      return null;
    });

    // Re-configure path: start from the screen's CURRENT metadata (never a fresh
    // default), so a form the user hand-edited in the wizard keeps those edits and only
    // the requested knobs change.
    const reconfigurePromises = stepsToReconfigure.map(async (item) => {
      const response = await WorkflowScreenService.GetFormMetadataOfScreen(item.screenId, {
        processId: item.processId,
        stepId: item.stepId
      });
      if (response && response.status === 200 && response.data) {
        return { processId: item.processId, stepId: item.stepId, formMetadata: response.data, isReconfigure: true };
      }
      return null;
    });

    const formMetadataBySteps = (await Promise.all([...metadataPromises, ...reconfigurePromises])).filter(
      Boolean
    ) as any[];

    if (formMetadataBySteps.length === 0) {
      return { error: "Failed to get form metadata for the requested steps." };
    }

    // KOA-17362 §7.6 Eksen 1 — shape the generated form BEFORE it is created.
    // The backend's default metadata renders EVERY business-object parameter as an
    // editable input, which is the wrong shape for a task screen: the assignee
    // should review the request read-only and only edit what the task decides. Two
    // layers, deterministic first: (1) freeze every param the task does not itself
    // output, (2) apply the script's SCREEN_FIELD config on top. Layer 1 alone makes
    // the screen correct even when the model authors no field config at all (the
    // on-prem floor), and layer 2 lets the author opt any field back to editable.
    // `SyncFormMetadataWithWorkflow` preserves these knobs and `GetFormDiCustom`
    // renders them, so nothing else in the chain has to change.
    const fieldConfigInputs: WorkflowStepFieldConfigInput[] = Array.isArray(input.fieldConfigs)
      ? (input.fieldConfigs as WorkflowStepFieldConfigInput[])
      : [];
    const configWarnings: string[] = [];
    for (const entry of formMetadataBySteps) {
      const boParamNames: string[] = ((entry.formMetadata?.sections ?? []) as any[])
        .flatMap((section: any): any[] => (Array.isArray(section?.items) ? section.items : []))
        .map((field: any): string => String(field?.linkedWorkflowBoParameterName ?? field?.name ?? ""))
        .filter((name: string): boolean => name.length > 0);
      if (boParamNames.length === 0) continue;

      const editableParamNames: string[] = resolveTaskEditableParamNames(sdInstance, sd, entry.stepId);
      // On a RE-CONFIGURE the readonly default is deliberately skipped: it is a
      // creation-time shape, and re-imposing it would silently revert a field somebody
      // made editable in the wizard. Only what the script asks for changes.
      const deterministicDefault: WorkflowFormOverrides = entry.isReconfigure
        ? { fields: [] }
        : deriveTaskFormReadonlyDefault(boParamNames, editableParamNames);
      const stepConfig: WorkflowStepFieldConfigInput | undefined = fieldConfigInputs.find(
        (c: WorkflowStepFieldConfigInput): boolean => c.stepId === entry.stepId
      );
      const explicit: WorkflowFormOverrides = {
        fields: stepConfig?.fields ?? [],
        ...(stepConfig?.sections ? { sections: stepConfig.sections } : {}),
        ...(stepConfig?.form ? { form: stepConfig.form } : {})
      };
      const overrides: WorkflowFormOverrides = mergeWorkflowFormOverrides(deterministicDefault, explicit);
      const application: WorkflowFormOverrideApplication = applyWorkflowFormOverrides(entry.formMetadata, overrides);

      if (application.unknownParams.length > 0) {
        // Fixable error, raised BEFORE any screen is created so a re-run is clean.
        // List what would actually have worked — the BO parameter name AND, where
        // it differs and is unambiguous, the visible label (which is what an
        // author reading the screen can see; a label is accepted as an alias).
        const referenceNames: string[] = describeFieldReferenceNames(entry.formMetadata);
        const available: string = referenceNames.length > 0 ? referenceNames.join(", ") : boParamNames.join(", ");
        return {
          error:
            `SCREEN_FIELD references parameter(s) that are not on this task's form: ` +
            `${application.unknownParams.join(", ")}. Addressable fields for step ${entry.stepId}: ` +
            `${available}. Use the \`PARAM BO\` name or the field's visible label.`
        };
      }
      if (application.error) {
        return { error: `${application.error} (step ${entry.stepId})` };
      }
      entry.formMetadata = application.metadata;
      configWarnings.push(...application.warnings);
    }

    // Config aimed at a step whose screen already exists was not applied — say so
    // rather than reporting a clean success (re-configuring an existing managed
    // screen is a separate operation).
    const skippedConfigSteps: string[] = fieldConfigInputs
      .map((c: WorkflowStepFieldConfigInput): string => c.stepId)
      .filter((stepId: string): boolean => !formMetadataBySteps.some((e: any): boolean => e.stepId === stepId));
    if (skippedConfigSteps.length > 0) {
      configWarnings.push(
        `Config for step(s) ${skippedConfigSteps.join(", ")} was NOT applied — their managed screen could not be read.`
      );
    }

    const oldScreens = sd.screenList ? [...sd.screenList] : [];

    // `onlyProvidedSteps` SCOPES the apply to the steps this call actually asked for.
    // Without it the platform ALSO reprocesses every already-managed step in the project,
    // which is the single root behind three separate defect families this branch had to
    // compensate for: (a) the call outgrew the 90s tool ceiling on a project with a few
    // managed screens, and the "no answer yet" was read as "it did not land", triggering a
    // SECOND full generation and a duplicate task screen (§38); (b) the response came back
    // as a SUPERSET of our request, so pairing new screens to steps had to be filtered by
    // hand (see the guard below); (c) unrelated managed screens were rebuilt — and briefly
    // unavailable — on every assignment. The Forms tab sets the same flag for the same
    // reason. The per-step filter below is KEPT as a belt-and-braces guard: an older
    // backend that ignores the flag must still not corrupt the mapping.
    const updateResponse = await WorkflowScreenService.UpdateWorkflowScreensWithProgress(
      { formMetadataBySteps, onlyProvidedSteps: true },
      () => {}
    );

    // KOA-18136 — a PARTIAL failure used to be invisible. `failedSteps` fed nothing but a
    // subtraction in the success message, so a run where the backend refused one of two
    // user tasks (`"Step not found."` — its step is missing from the PERSISTED workflow)
    // reported `success: true` / "Successfully assigned screens for 1 user task(s)." and
    // threw the reason away. The orchestrator then had only a count to go on, and the user
    // was told a task screen was missing with no way to learn why. Every failed step is now
    // carried out with its own reason, on the same result the caller already reads.
    const failedSteps = updateResponse?.formMetadataBySteps?.filter((res: any) => res && !res.isSuccess) || [];
    const failedAssignments: IAssignmentFailure[] = failedSteps.map((res: any): IAssignmentFailure => {
      const message: string = String(res?.message ?? "Unknown error.");
      return {
        processId: String(res?.processId ?? input.processId ?? ""),
        stepId: String(res?.stepId ?? ""),
        message,
        kind: classifyAssignmentFailure(message)
      };
    });
    const failureSummary: IAssignmentFailureSummary = summariseAssignmentFailures(failedAssignments);

    // A total failure is only an ERROR when at least one refusal is a real one. When every
    // step was merely HELD OPEN, the process is intact and the remedy is the user's, not a
    // rebuild — so this returns a normal result carrying the remedy. Raising `error` here is
    // what taught the caller to treat a busy screen as "the screen does not exist".
    if (failedSteps.length > 0 && failedSteps.length === formMetadataBySteps.length) {
      if (failureSummary.refused.length > 0) {
        return { error: `Failed to assign all screens. First error: ${failureSummary.refused[0].message}` };
      }
      return {
        success: true,
        message: `No screen was generated on this run.${failureSummary.note}`,
        assignedScreens: [],
        failedAssignments
      };
    }

    if (sd.setScreenList) await sd.setScreenList();

    // KOA-17362 — capture the post-update screen list AUTHORITATIVELY, straight
    // from the service. The previous implementation read `sd.screenList` for both
    // the before AND after snapshots, but `sd` is a one-shot snapshot taken at the
    // top of this function: `setScreenList()`'s setState never reaches it, so the
    // before/after diff was the SAME array compared to itself and `newlyCreated`
    // came back EMPTY. With no newly-created screens detected, `assignedScreens`
    // stayed empty and the orchestrator's enrichment phase ran zero passes — the
    // managed approval screens existed but were never decorated/wired. Re-fetching
    // the list directly removes the React-state dependency entirely.
    let newScreens: { id: string; name: string }[] = sd.screenList ? [...sd.screenList] : [];
    try {
      const sdId: string = sd.sdId || KMainFunctions.getSdIdFromUrl();
      if (sdId) {
        const listRes = await ScreenService.GetProjectSdListBySdId(sdId);
        if (listRes && listRes.status === 200) {
          newScreens = KMainFunctions.convertScreenListDTOToInfoInterface(listRes.data);
        }
      }
    } catch {
      const reread = getValues(sdInstance) as any;
      if (reread.screenList) newScreens = [...reread.screenList];
    }

    const newlyCreatedScreens = newScreens.filter((ns: any) => !oldScreens.some((os) => os.id === ns.id));

    // The steps whose managed screen was actually created (skip failures); fall
    // back to this call's own request set if the response did not echo per-step results.
    const succeededSteps: { processId: string; stepId: string }[] = (updateResponse?.formMetadataBySteps ?? [])
      .filter((r: any) => r.isSuccess)
      .map((r: any) => ({ processId: r.processId, stepId: r.stepId }));
    // KOA-18136 — ONLY A STEP THIS CALL ASKED TO CREATE MAY CLAIM ONE OF ITS SCREENS.
    //
    // The response is not guaranteed to be an answer about the steps we sent. Unscoped,
    // the platform re-processes EVERY already-managed step of the project and echoes them
    // all with `isSuccess` (which defaults to true), so `succeededSteps` becomes a SUPERSET
    // of this call's work while `newlyCreatedScreens` holds only the screen(s) this call
    // created. Pairing across that gap is what the name-fold FALLBACK below turns into
    // corruption: an already-mapped step whose name matches nothing here took the new
    // screen and overwrote its own correct mapping.
    //
    // The call above now sends `onlyProvidedSteps`, so a current backend answers about our
    // steps alone. This filter stays because the guarantee is the SERVER's, not ours: an
    // older backend silently ignores the flag, and a silently-ignored scope must degrade
    // into a slow-but-correct run, never into a corrupted mapping.
    //
    // Live 2026-08-17 (session f0d6f51c), four user tasks assigned one call per task:
    // from the second call on, each earlier task was re-pointed at the later task's
    // screen. Two consequences, both reported by the user — (a) the completeness read
    // saw 1 of 4 tasks carrying a screen, so the Designer's style + function passes ran
    // for a single screen and the rest were never touched; (b) the platform then found
    // no form for that step on the screen it was pointed at and PREPENDED a second full
    // form, duplicating every wizard parameter field on the screen.
    //
    // A re-configured step is excluded by construction: it lives in
    // `stepsToReconfigure`, never in `stepsToProcess`, and it creates no screen.
    const requestedStepIds: Set<string> = new Set<string>(
      stepsToProcess.map((item: { stepId: string }): string => item.stepId)
    );
    const stepsForMapping: { processId: string; stepId: string }[] = (
      succeededSteps.length > 0 ? succeededSteps : stepsToProcess
    ).filter((item: { stepId: string }): boolean => requestedStepIds.has(item.stepId));

    // KOA-17362 — collect the managed task screens created here so the
    // orchestrator can run a follow-up enrichment design pass on each (it gets
    // only this action's result, so the screen↔step mapping must travel back in
    // the return value — there is no other handoff).
    // `pairedBy` is EVIDENCE, and it travels because the receiver cannot re-derive it:
    // "name" means this screen's name is the one the platform generates from that step's
    // name, "order" means nothing about the screen says it is that step's and the pair is
    // positional. A consumer that builds ONE STEP'S requirement onto a screen must refuse
    // the second kind — live 379e6653 built the workshop step's read-out and inputs onto
    // the department-manager's screen because the pair was believed rather than shown.
    const assignedScreens: {
      screenId: string;
      screenName: string;
      processId: string;
      stepId: string;
      stepName: string;
      pairedBy: StepScreenPairingEvidence;
    }[] = [];

    if (newlyCreatedScreens.length > 0) {
      const newProcessStepScreenIds = [...(project.processStepScreenIds || [])];

      // The step→screen decision itself is pure and lives in
      // `workflow-step-screen-mapping.ts`, with its own tests: EVERY name match first
      // (the name the platform itself generates from the step name, not a fold of the
      // step name), then a positional fallback that may never REPLACE a mapping a step
      // already carries — and that reports itself as positional.
      const stepsAwaitingScreen: StepAwaitingScreen[] = stepsForMapping.map(
        (processedItem: { processId: string; stepId: string }): StepAwaitingScreen => {
          const wf = workflows.find((w: any) => String(w.id) === String(processedItem.processId));
          const allSteps = wf && sd.findAllStepsRecursively ? sd.findAllStepsRecursively(wf, () => true) : [];
          return {
            processId: processedItem.processId,
            stepId: processedItem.stepId,
            stepName: allSteps.find((s: any) => s.id === processedItem.stepId)?.name || processedItem.stepId
          };
        }
      );

      for (const pairing of pairCreatedScreensWithSteps(
        stepsAwaitingScreen,
        newlyCreatedScreens as CreatedScreenLike[],
        newProcessStepScreenIds as StepScreenLinkLike[]
      )) {
        const existingIdx = newProcessStepScreenIds.findIndex(
          (ps: any) => ps.processId === pairing.processId && ps.stepId === pairing.stepId
        );

        if (existingIdx !== -1) {
          newProcessStepScreenIds[existingIdx] = {
            ...newProcessStepScreenIds[existingIdx],
            screenId: pairing.screenId,
            isManaged: true
          };
        } else {
          newProcessStepScreenIds.push({
            projectId: project.id,
            processId: pairing.processId,
            stepId: pairing.stepId,
            screenId: pairing.screenId,
            isManaged: true
          });
        }

        assignedScreens.push({
          screenId: pairing.screenId,
          screenName: pairing.screenName,
          processId: pairing.processId,
          stepId: pairing.stepId,
          stepName: pairing.stepName,
          pairedBy: pairing.pairedBy
        });
      }

      project.processStepScreenIds = newProcessStepScreenIds;

      if (typeof sd.updateProject === "function") {
        await sd.updateProject(project);
      } else if (typeof sd.setProject === "function") {
        sd.setProject(project);
      }
    }

    const wp = getWp(sdInstance);
    adoptCanvasIntoModel(wp);
    const lastTouchedStepId: string | undefined =
      stepsToProcess.length > 0
        ? stepsToProcess[stepsToProcess.length - 1].stepId
        : stepsToReconfigure[stepsToReconfigure.length - 1]?.stepId;
    if (wp && typeof wp.setSelectedWfStep === "function" && lastTouchedStepId) {
      wp.setSelectedWfStep(null);
      setTimeout(() => wp.setSelectedWfStep(lastTouchedStepId), 50);
    }

    // REMOVED (KOA-18136): a 1500 ms delayed `clearSelectedWf()` + `loadScreen()` used to
    // fire here. It was a time bomb — `clearSelectedWf` deselects the workflow AND calls
    // `resetModeler()`, so ~1.5 s after ASSIGN_SCREEN returned the BPMN canvas was torn
    // down underneath whatever the script was still doing. ASSIGN_SCREEN is normally the
    // TAIL of a build, so the commands it detonated on were LINT and LAYOUT — the exact
    // "lint errors left behind / autolayout never ran" symptom — and the teardown is
    // unrecoverable: `ensureModelerBuilt` bails on `!selectedWf`, so nothing rebuilds the
    // modeler and every later element click stops updating the right panel too.
    // Refreshing the screen list is the caller's business, not a fuse lit inside a
    // workflow step handler.

    const configNote: string = configWarnings.length > 0 ? ` Field config notes: ${configWarnings.join(" ")}` : "";
    const reconfigureNote: string =
      stepsToReconfigure.length > 0
        ? ` Re-configured the existing managed screen(s) of ${stepsToReconfigure.length} user task(s) ` +
          `(their other form settings were preserved).`
        : "";
    const failedStepIds: Set<string> = new Set<string>(
      failedAssignments.map((entry: { stepId: string }): string => entry.stepId)
    );
    const createdCount: number = stepsToProcess.filter(
      (item: { stepId: string }): boolean => !failedStepIds.has(item.stepId)
    ).length;
    const createdNote: string =
      createdCount > 0
        ? `Successfully assigned screens for ${createdCount} user task(s). Each generated form shows the task's ` +
          `request data read-only and keeps only the task's own output parameters editable.`
        : "No new screen needed to be generated.";
    // Named, with the backend's own reason — this is the ONLY place that still holds it.
    // Split by KIND: a screen that is merely open elsewhere is not a failure to repair, and
    // saying so is what keeps the caller from rebuilding a process that is already correct.
    const failedNote: string = failureSummary.note;

    return {
      success: true,
      message: `${createdNote}${failedNote}${reconfigureNote}${configNote}`,
      assignedScreens,
      failedAssignments
    };
  } catch (error: any) {
    return { error: `Exception: ${error?.message || error}` };
  }
}

/** The pool band the phase columns must tile, in canvas coordinates. */
interface IPhaseLayoutPoolRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** One declared phase as the MODEL carries it at a given instant. */
interface IPhaseSnapshotEntry {
  id: string;
  name: string;
  order: number;
}

/** One phase column as the CANVAS carries it at a given instant. */
interface IPhaseGroupSnapshot {
  elementId: string;
  phaseId: string;
  label: string;
  x: number;
  width: number;
}

/** Both sides of the phase story at one instant — what the model says and what the canvas shows. */
interface IPhaseSnapshot {
  declared: IPhaseSnapshotEntry[];
  groups: IPhaseGroupSnapshot[];
  /** Step id → declared phase id, for every step that carries one. */
  stepPhases: { stepId: string; stepName: string; phaseId: string; x: number; width: number }[];
}

/**
 * Read the phase state without touching anything — the observation point for
 * "who owns the phase order".
 *
 * Two questions cannot be answered from the end state alone, and both decide whether the
 * retile below can work at all: (a) is the MODEL's declared order still the author's, or has
 * the canvas already written over it during this layout, and (b) do the canvas columns exist
 * and in what order. So this is called twice — before layout touches the diagram and after
 * the retile — and the two are compared.
 */
function readPhaseSnapshot(sdInstance: ScreenDesignInstance, modeler: any): IPhaseSnapshot {
  const empty: IPhaseSnapshot = { declared: [], groups: [], stepPhases: [] };
  try {
    const sd = getValues(sdInstance) as any;
    const wf = sd?.selectedWf;

    const declared: IPhaseSnapshotEntry[] = (Array.isArray(wf?.happyPathDefinition) ? wf.happyPathDefinition : [])
      .map((phase: any, index: number): IPhaseSnapshotEntry => ({
        id: String(phase?.id ?? ""),
        name: String(phase?.happyPathPhase ?? ""),
        order: typeof phase?.phaseOrder === "number" ? phase.phaseOrder : index + 1
      }))
      .sort((a: IPhaseSnapshotEntry, b: IPhaseSnapshotEntry): number => a.order - b.order);

    const elementRegistry = modeler?.get?.("elementRegistry");
    const all: unknown[] = typeof elementRegistry?.getAll === "function" ? elementRegistry.getAll() : [];
    const groups: IPhaseGroupSnapshot[] = [];
    for (const raw of all) {
      const element = raw as {
        id?: string;
        x?: number;
        width?: number;
        labelTarget?: unknown;
        businessObject?: {
          isHappyPathPhase?: boolean;
          phaseId?: string;
          name?: string;
          categoryValueRef?: { value?: string };
        };
      };
      if (element?.labelTarget) continue;
      const bo = element?.businessObject;
      if (bo?.isHappyPathPhase !== true) continue;
      groups.push({
        elementId: String(element.id ?? ""),
        phaseId: String(bo.phaseId ?? ""),
        // The rendered label lives on the category value; `name` is only the in-memory mirror.
        label: String(bo.categoryValueRef?.value ?? bo.name ?? ""),
        x: Math.round(Number(element.x ?? 0)),
        width: Math.round(Number(element.width ?? 0))
      });
    }
    groups.sort((a: IPhaseGroupSnapshot, b: IPhaseGroupSnapshot): number => a.x - b.x);

    const stepPhases: IPhaseSnapshot["stepPhases"] = [];
    const allSteps: any[] = wf && sd.findAllStepsRecursively ? sd.findAllStepsRecursively(wf, () => true) : [];
    for (const step of allSteps) {
      const hit = Array.isArray(step?.extraParameters)
        ? step.extraParameters.find((p: any): boolean => p?.key === "happyPath")
        : undefined;
      const phaseId: string = hit && typeof hit.value === "string" ? hit.value : "";
      if (phaseId.length === 0) continue;
      const shape = elementRegistry?.get?.(String(step.id));
      stepPhases.push({
        stepId: String(step.id),
        stepName: String(step.name ?? ""),
        phaseId,
        x: Math.round(Number(shape?.x ?? -1)),
        width: Math.round(Number(shape?.width ?? 0))
      });
    }
    stepPhases.sort((a, b): number => a.x - b.x);

    return { declared, groups, stepPhases };
  } catch {
    return empty;
  }
}

/** Human-readable phase order, so a scrambled one is obvious at a glance. */
function describePhaseOrder(snapshot: IPhaseSnapshot): string {
  const model: string = snapshot.declared.map((p: IPhaseSnapshotEntry): string => `${p.order}:${p.name}`).join(" | ");
  const canvas: string = snapshot.groups
    .map((g: IPhaseGroupSnapshot): string => `${g.label}@x${g.x}(w${g.width})`)
    .join(" | ");
  return `model[${model || "(none)"}]  canvas[${canvas || "(no columns on canvas)"}]`;
}

/** Log one side of the story with a stage tag. */
function logPhaseSnapshot(stage: string, snapshot: IPhaseSnapshot): void {
  console.log(`[LAYOUT-PHASES:${stage}] ${describePhaseOrder(snapshot)}`);
  console.log(
    `[LAYOUT-PHASES:${stage}] declared=${snapshot.declared.length} canvasColumns=${snapshot.groups.length} ` +
      `stepsWithPhase=${snapshot.stepPhases.length}`
  );
  const nameById: Map<string, string> = new Map<string, string>(
    snapshot.declared.map((p: IPhaseSnapshotEntry): [string, string] => [p.id, p.name])
  );
  for (const step of snapshot.stepPhases) {
    console.log(
      `[LAYOUT-PHASES:${stage}]   step "${step.stepName}" (${step.stepId}) x=${step.x} w=${step.width} ` +
        `→ phase "${nameById.get(step.phaseId) ?? "(unknown id " + step.phaseId + ")"}"`
    );
  }
}

/**
 * Make the Happy Path COLUMNS say what the model DECLARED, right after auto-layout.
 *
 * A phase column is a `bpmn:Group` marked by the moddle extension attrs the phase
 * interceptor writes (`isHappyPathPhase` / `phaseId`). Geometry is the authority for a
 * step's phase — the interceptor derives it from the column containing the step's centre —
 * so a DSL-declared assignment (`PHASE_ASSIGN`) only survives if the step is INSIDE its own
 * column when that derivation next runs. Layout is the one moment both can be settled at
 * once, because it is re-deciding the step positions anyway.
 *
 * The decision itself is pure and tested (`planPhaseColumns`); this function only reads the
 * canvas and applies it. Everything is best-effort: a diagram without phases, an
 * un-mounted modeler, or a pool too narrow to carry the columns leaves the canvas exactly
 * as layout left it. Auto-layout must never be able to DESTROY phases — the empty plan is
 * "leave them alone", never "erase them".
 */
function applyPhaseColumnLayout(
  sdInstance: ScreenDesignInstance,
  modeler: any,
  pool: IPhaseLayoutPoolRect,
  before?: IPhaseSnapshot
): void {
  try {
    console.log(
      `[LAYOUT-PHASES] pool band handed to the retile: x=${Math.round(pool.x)} w=${Math.round(pool.width)} ` +
        `y=${Math.round(pool.y)} h=${Math.round(pool.height)}`
    );
    const sd = getValues(sdInstance) as any;
    const wf = sd?.selectedWf;

    // The decisive comparison: is the order I am about to treat as "the declaration" still
    // the author's, or did this very layout already let the canvas write over it?
    if (before) {
      const now: IPhaseSnapshot = readPhaseSnapshot(sdInstance, modeler);
      const beforeOrder: string = before.declared.map((p: IPhaseSnapshotEntry): string => p.name).join(" > ");
      const nowOrder: string = now.declared.map((p: IPhaseSnapshotEntry): string => p.name).join(" > ");
      logPhaseSnapshot("after-layout", now);
      if (beforeOrder !== nowOrder) {
        console.warn(
          `[LAYOUT-PHASES] ⚠ THE MODEL ORDER CHANGED DURING LAYOUT — before: [${beforeOrder}]  after: [${nowOrder}]. ` +
            `Everything below therefore reproduces the CANVAS's order, not the author's.`
        );
      } else {
        console.log(`[LAYOUT-PHASES] model order unchanged during layout: [${nowOrder}]`);
      }
    }
    const declared: IPhaseDeclaration[] = (Array.isArray(wf?.happyPathDefinition) ? wf.happyPathDefinition : [])
      .filter((phase: any): boolean => typeof phase?.id === "string" && phase.id.length > 0)
      .map((phase: any, index: number): IPhaseDeclaration => ({
        id: String(phase.id),
        name: String(phase.happyPathPhase ?? ""),
        order: typeof phase.phaseOrder === "number" ? phase.phaseOrder : index + 1
      }));
    if (declared.length === 0) return;

    const elementRegistry = modeler.get("elementRegistry");
    const modeling = modeler.get("modeling");

    // The phase a step DECLARES, by step id. Read recursively so a step inside a
    // subprocess carries its phase exactly like PHASE_ASSIGN writes it.
    const phaseByStepId: Map<string, string> = new Map<string, string>();
    const allSteps: any[] = wf && sd.findAllStepsRecursively ? sd.findAllStepsRecursively(wf, () => true) : [];
    for (const step of allSteps) {
      const hit = Array.isArray(step?.extraParameters)
        ? step.extraParameters.find((p: any): boolean => p?.key === "happyPath")
        : undefined;
      if (hit && typeof hit.value === "string" && hit.value.length > 0) {
        phaseByStepId.set(String(step.id), String(hit.value));
      }
    }

    const laidOutSteps: IPhaseLayoutStep[] = elementRegistry
      .getAll()
      .filter((element: any): boolean => typeof element?.id === "string" && phaseByStepId.has(String(element.id)))
      .map((element: any): IPhaseLayoutStep => ({
        id: String(element.id),
        phaseId: phaseByStepId.get(String(element.id)),
        x: Number(element.x ?? 0),
        width: Number(element.width ?? 0)
      }));

    const plan: IPhaseLayoutPlan = planPhaseColumns(declared, laidOutSteps, { x: pool.x, width: pool.width });
    const declaredNameById: Map<string, string> = new Map<string, string>(
      declared.map((p: IPhaseDeclaration): [string, string] => [p.id, p.name])
    );
    console.log(
      `[LAYOUT-PHASES] plan: ${plan.columns.length} column(s), ${plan.nudges.length} nudge(s) ` +
        `from ${declared.length} declared phase(s) and ${laidOutSteps.length} phased step(s)`
    );
    for (const column of plan.columns) {
      console.log(
        `[LAYOUT-PHASES]   column "${declaredNameById.get(column.phaseId) ?? column.phaseId}" ` +
          `x=${column.x} w=${column.width}`
      );
    }
    for (const nudge of plan.nudges) {
      // Reported, never applied — see the note at the apply site below.
      console.log(
        `[LAYOUT-PHASES]   NOTE ${nudge.stepId} sits outside its declared column ` +
          `(would be x=${nudge.x}); left where layout put it`
      );
    }
    if (plan.columns.length === 0) {
      console.warn("[LAYOUT-PHASES] empty plan — nothing to retile (no phases, or the pool is too narrow).");
      return;
    }

    // STEPS ARE NEVER MOVED HERE. The first version nudged a step whose declared phase did not
    // match where layout put it — and that ran AFTER the layout's own collision pass, with no
    // collision logic of its own. Live evidence: three steps landed on the same x=1039, producing
    // two node overlaps and an edge crossing a node, on a diagram the layout had just laid out
    // cleanly. The columns follow the diagram; the diagram is not bent to fit the columns.
    //
    // A step that contradicts its own declaration therefore stays where layout put it and simply
    // falls in whichever column contains it — visible, and correctable by hand, rather than
    // silently shoved and overlapping.

    // The columns, LEFT TO RIGHT. The interceptor treats a resize as a move of the SHARED
    // boundary and lets the right-hand neighbour give or take the difference, so applying in
    // order lets each column settle its own left edge and leaves the last one covering the
    // remainder — which is exactly the tiling the plan describes.
    //
    // A GROUP'S EXTERNAL LABEL IS A SEPARATE ELEMENT THAT SHARES THE GROUP'S BUSINESS OBJECT, so
    // it answers `isHappyPathPhase` too and, coming later in the registry, used to overwrite the
    // column in this map. Every resize below then landed on the label: the log read
    // `resize "Muhasebe İncelemesinde" from x=855 w=71` while the column was 1481 wide, and the
    // canvas came out of the "retile" byte-identical. `labelTarget` is the discriminator.
    const groupsByPhaseId: Map<string, any> = new Map<string, any>();
    for (const element of elementRegistry.getAll()) {
      if (element?.labelTarget) continue;
      const businessObject: any = element?.businessObject;
      if (businessObject?.isHappyPathPhase !== true) continue;
      const phaseId: unknown = businessObject?.phaseId;
      if (typeof phaseId === "string" && phaseId.length > 0) groupsByPhaseId.set(phaseId, element);
    }
    console.log(
      `[LAYOUT-PHASES] canvas carries ${groupsByPhaseId.size} phase column(s) matchable by id; ` +
        `plan wants ${plan.columns.length}`
    );
    // COUNT WHAT WAS APPLIED, not what was planned. The previous line reported the plan, so a
    // pass that found no columns still announced a successful retile — the log lied about the
    // one thing it existed to report.
    let resized: number = 0;
    const missing: string[] = [];
    for (const column of plan.columns) {
      const group = groupsByPhaseId.get(column.phaseId);
      if (!group) {
        missing.push(declaredNameById.get(column.phaseId) ?? column.phaseId);
        continue;
      }
      const currentX: number = Math.round(Number(group.x ?? 0));
      const currentWidth: number = Math.round(Number(group.width ?? 0));
      // Every write here reaches the interceptor, which re-tiles and PERSISTS. The layout's own
      // node moves already flood that path (the run that exposed this produced a burst of
      // save conflicts), so a column that is already where the plan wants it costs nothing.
      if (currentX === column.x && currentWidth === column.width) {
        console.log(
          `[LAYOUT-PHASES]   keep "${declaredNameById.get(column.phaseId) ?? column.phaseId}" ` +
            `already at x=${currentX} w=${currentWidth}`
        );
        continue;
      }
      console.log(
        `[LAYOUT-PHASES]   resize "${declaredNameById.get(column.phaseId) ?? column.phaseId}" ` +
          `from x=${currentX} w=${currentWidth} to x=${column.x} w=${column.width}`
      );
      modeling.resizeShape(group, { x: column.x, y: pool.y, width: column.width, height: pool.height });
      resized += 1;
    }
    if (missing.length > 0) {
      console.warn(
        `[LAYOUT-PHASES] ⚠ ${missing.length} declared phase(s) have NO column on canvas — not retiled: ` +
          missing.join(", ")
      );
    }
    console.log(
      `[LAYOUT-PHASES] APPLIED: resized ${resized}/${plan.columns.length} column(s); ` +
        `${plan.nudges.length} step(s) noted as outside their column, 0 moved`
    );
    // What the canvas ACTUALLY ended up with — the interceptor re-tiles on every resize, so the
    // final state is its reaction to the commands above, not necessarily the plan.
    logPhaseSnapshot("after-retile", readPhaseSnapshot(sdInstance, modeler));
  } catch (error: any) {
    // Best-effort by design: a phase-column hiccup must not fail the layout that already
    // succeeded, and the columns are re-derivable from the model on the next diagram load.
    console.warn(`[LAYOUT-PHASES] skipped: ${error?.message || error}`);
  }
}

export async function workflowAutoLayoutAction(sdInstance: ScreenDesignInstance, input: any) {
  const modeler = (window as any).bpmnModeler;
  if (!modeler) {
    console.log("[DEBUG] BPMN Modeler is not ready.");
    return { error: "BPMN Modeler is not ready." };
  }

  try {
    console.log("[DEBUG] --- AUTO LAYOUT START ---");
    const elk = new ELK();
    const elementRegistry = modeler.get("elementRegistry");
    const modeling = modeler.get("modeling");
    const eventBus = modeler.get("eventBus");

    // Taken BEFORE anything on the diagram is touched. This is the only moment the model's
    // phase order is still guaranteed to be the AUTHOR's: from here on every pool/lane resize
    // and every step move fires the phase interceptor, which treats the canvas as the source
    // of truth and writes the order it derives back into the model.
    const phasesBefore: IPhaseSnapshot = readPhaseSnapshot(sdInstance, modeler);
    logPhaseSnapshot("before-layout", phasesBefore);

    // ── LAYOUT INSTRUMENTATION ──────────────────────────────────────────
    // Snapshot all SequenceFlows before layout
    const preFlows = elementRegistry.filter((e: any) => e.type === "bpmn:SequenceFlow") as {
      id: string;
      source?: { id: string };
      target?: { id: string };
      businessObject?: { name?: string };
    }[];
    const preFlowMap = new Map<string, { source?: string; target?: string; name?: string }>();
    for (const f of preFlows) {
      preFlowMap.set(f.id, {
        source: f.source?.id,
        target: f.target?.id,
        name: f.businessObject?.name
      });
    }
    console.log(`[LAYOUT-INSTR] PRE-LAYOUT flow count: ${preFlows.length}`);

    // Listen for connection-delete events during layout
    const deleteLog: { id: string; stage: string }[] = [];
    let currentStage = "initialization";
    const onConnectionDelete = (event: { element?: { id?: string; type?: string } }) => {
      if (event.element?.type === "bpmn:SequenceFlow" && event.element.id) {
        const info = preFlowMap.get(event.element.id);
        console.error(
          `[LAYOUT-INSTR] 🔥 Connection DELETED during "${currentStage}": ${event.element.id}` +
            (info ? ` (${info.source} -> ${info.target}, name="${info.name || ""}")` : "")
        );
        deleteLog.push({ id: event.element.id, stage: currentStage });
      }
    };
    eventBus.on("commandStack.connection.delete.postExecute", onConnectionDelete);

    // Listen for reconnect (source/target change) events
    const onReconnect = (event: {
      context?: { connection?: { id?: string; source?: { id?: string }; target?: { id?: string } } };
    }) => {
      const conn = event.context?.connection;
      if (conn?.id) {
        const info = preFlowMap.get(conn.id);
        console.error(
          `[LAYOUT-INSTR] 🔀 Connection RECONNECTED during "${currentStage}": ${conn.id}` +
            (info ? ` (was ${info.source} -> ${info.target})` : "") +
            ` now ${conn.source?.id} -> ${conn.target?.id}`
        );
      }
    };
    eventBus.on("commandStack.connection.reconnect.postExecute", onReconnect);
    eventBus.on("commandStack.connection.reconnectStart.postExecute", onReconnect);
    eventBus.on("commandStack.connection.reconnectEnd.postExecute", onReconnect);

    // Listen for new connection creation (to see if one replaces another)
    const onConnectionCreate = (event: {
      context?: { connection?: { id?: string; source?: { id?: string }; target?: { id?: string } } };
    }) => {
      const conn = event.context?.connection;
      if (conn?.id) {
        console.log(
          `[LAYOUT-INSTR] ✨ Connection CREATED during "${currentStage}": ${conn.id} (${conn.source?.id} -> ${conn.target?.id})`
        );
      }
    };
    eventBus.on("commandStack.connection.create.postExecute", onConnectionCreate);

    // ── FIX: disable DropOnFlowBehavior during LAYOUT ──────────────────
    // bpmn-js's DropOnFlowBehavior sets context.targetFlow in preExecute
    // when a moved shape's mid intersects an existing flow, then splits
    // that flow in postExecuted. During auto-layout this corrupts the
    // diagram (flows get rewired to gateways they visually cross over).
    // Neutralize by clearing context.targetFlow in postExecute (fires
    // between preExecute and postExecuted).
    const suppressDropOnFlow = (event: { context?: { targetFlow?: unknown } }) => {
      if (event.context && event.context.targetFlow) {
        event.context.targetFlow = undefined;
      }
    };
    eventBus.on("commandStack.elements.move.postExecute", 2000, suppressDropOnFlow);
    eventBus.on("commandStack.shape.create.postExecute", 2000, suppressDropOnFlow);

    const allElements = elementRegistry.getAll();
    console.log("[DEBUG] allElements count:", allElements.length);

    // `bpmn:Group` is EXCLUDED like the pool and lanes are. A Happy Path phase column is a
    // group: a visual artifact with no sequence flow, whose geometry is a strict TILING of
    // the pool (full height, no gaps). Feeding one to ELK as if it were a flow node laid it
    // out like a task — the tiling broke, and because a step's phase is derived from the
    // column containing its centre, every step's phase was then re-derived from scrambled
    // geometry. The columns are re-tiled from the DECLARED phases after layout instead
    // (see `planPhaseColumns` below).
    const nodes = allElements.filter(
      (e: any) =>
        e.type !== "label" &&
        e.type !== "bpmn:Process" &&
        e.type !== "bpmn:Participant" &&
        e.type !== "bpmn:Lane" &&
        e.type !== "bpmn:Group" &&
        e.type !== "bpmn:BoundaryEvent" &&
        e.parent !== undefined &&
        !e.waypoints &&
        e.id &&
        e.width !== undefined &&
        e.height !== undefined
    );
    console.log("[DEBUG] filtered nodes count:", nodes.length);
    // Does the exclusion actually hold at runtime? A phase column reaching ELK is the
    // difference between "layout scrambled the columns" and "they were already wrong".
    const groupsOnCanvas: number = allElements.filter((e: any): boolean => e?.type === "bpmn:Group").length;
    const groupsHandedToElk: number = nodes.filter((e: any): boolean => e?.type === "bpmn:Group").length;
    console.log(
      `[LAYOUT-PHASES] bpmn:Group on canvas=${groupsOnCanvas}, handed to the layout engine=${groupsHandedToElk} ` +
        `(must be 0 — a column laid out as a flow node loses its tiling)`
    );

    const edges = allElements.filter(
      (e: any) =>
        e.waypoints &&
        e.type === "bpmn:SequenceFlow" &&
        e.source &&
        e.target &&
        nodes.some((n) => n.id === e.source.id) &&
        nodes.some((n) => n.id === e.target.id)
    );
    console.log("[DEBUG] filtered edges count:", edges.length);

    const laneShapes = allElements.filter((e: any) => e.type === "bpmn:Lane");
    const lanesList = [...laneShapes].sort((a: any, b: any) => a.y - b.y);
    console.log("[DEBUG] lanesList count:", lanesList.length);

    const nodeToPartition = new Map();
    nodes.forEach((n: any) => {
      let laneIndex = lanesList.findIndex((l: any) => {
        const refs = l.businessObject?.get ? l.businessObject.get("flowNodeRef") : l.businessObject?.flowNodeRef;
        return Array.isArray(refs) && refs.some((ref: any) => ref.id === n.id);
      });
      if (laneIndex === -1 && lanesList.length > 0) {
        const centerY = n.y + n.height / 2;
        laneIndex = lanesList.findIndex((l: any) => centerY >= l.y && centerY <= l.y + l.height);
        if (laneIndex === -1) laneIndex = centerY < lanesList[0].y ? 0 : lanesList.length - 1;
      }
      const finalIdx = Math.max(0, laneIndex);
      nodeToPartition.set(n.id, finalIdx);
      console.log(`[DEBUG] NodeToPartition mapping: ${n.id} -> Lane ${finalIdx}`);
    });

    // ELK lays the flow out by sequence (direction:RIGHT). We keep ELK's X
    // (flow order) and override each node's Y to its LANE BAND (move-nodes pass)
    // so every node stays in its actor lane — the user's hard rule. NOTE: the
    // ELK-hierarchy (lanes-as-containers) experiment was reverted — elkjs
    // `hierarchyHandling:INCLUDE_CHILDREN` FLATTENS the hierarchy (it laid the
    // whole flow top-to-bottom and the lane boxes became garbage bboxes), so
    // nodes ended up outside their lanes. This flat layout keeps nodes IN lanes.
    const multiLane: boolean = lanesList.length > 1;
    const graph: any = {
      id: "root",
      layoutOptions: {
        "elk.algorithm": "layered",
        "elk.direction": "RIGHT",
        "elk.edgeRouting": "ORTHOGONAL",
        "elk.spacing.nodeNode": "120",
        // Wider column gap (was 150) so the channel router has room for the
        // vertical hops in/out of each node's column plus per-edge stagger
        // without two parallel edges sharing a line (KOA-17362).
        "elk.layered.spacing.nodeNodeBetweenLayers": "180"
      },
      children: nodes.map((n: any) => ({
        id: n.id,
        width: n.width,
        height: n.height
      })),
      edges: edges.map((e: any) => ({ id: e.id, sources: [e.source.id], targets: [e.target.id] }))
    };

    // Boundary events are attached to a host (not standalone ELK nodes), so
    // their outgoing flows are excluded from `edges`. Add a synthetic
    // host→target edge so ELK reserves space and places the escalation
    // target near its host. The real boundary→target connection isn't in
    // `edges`, so it's rerouted by bpmn.js in the non-ELK-edge pass below.
    const boundaryShapesForLayout = allElements.filter((e: any) => e.type === "bpmn:BoundaryEvent");
    for (const be of boundaryShapesForLayout) {
      const hostId = be.host?.id || be.businessObject?.attachedToRef?.id;
      if (!hostId || !nodes.some((n: any) => n.id === hostId)) continue;
      for (const out of be.outgoing || []) {
        if (out.type !== "bpmn:SequenceFlow" || !out.target) continue;
        if (!nodes.some((n: any) => n.id === out.target.id)) continue;
        graph.edges.push({ id: `bnd_${out.id}`, sources: [hostId], targets: [out.target.id] });
      }
    }

    // Race ELK against a 20s wall clock. Pathological graphs (orphan nodes,
    // cycles left behind by a broken LLM script) can keep ELK spinning past
    // the 30s ui_workflow_dsl tool timeout — which nukes the entire response
    // including successful commands that ran before LAYOUT. Bailing early
    // lets the rest of the script's results return on time with a clean
    // error the LLM can act on (fix lint errors first, re-layout after).
    const ELK_TIMEOUT_MS = 20_000;
    console.log("[DEBUG] Invoking elk.layout...");
    interface ElkLayoutNode {
      id: string;
      x: number;
      y: number;
      width: number;
      height: number;
      layoutOptions?: Record<string, string>;
      children?: ElkLayoutNode[];
    }
    let layoutedGraph: {
      children?: ElkLayoutNode[];
      edges?: {
        id: string;
        sections?: {
          startPoint?: { x: number; y: number };
          endPoint?: { x: number; y: number };
          bendPoints?: { x: number; y: number }[];
        }[];
      }[];
    };
    try {
      layoutedGraph = (await Promise.race([
        elk.layout(graph),
        new Promise((_, reject) => setTimeout(() => reject(new Error("ELK_TIMEOUT")), ELK_TIMEOUT_MS))
      ])) as typeof layoutedGraph;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg === "ELK_TIMEOUT") {
        console.warn(`[DEBUG] elk.layout timed out after ${ELK_TIMEOUT_MS}ms — skipping layout`);
        return {
          error:
            `LAYOUT timed out after ${ELK_TIMEOUT_MS / 1000}s. ` +
            `Fix any lint errors first (orphan nodes or missing conditions can make layout spin), then retry LAYOUT alone in a fresh script.`
        };
      }
      throw err;
    }
    // ── ELK output → absolute leaf positions. Flat layout: children ARE the
    // leaf flow-nodes (absolute coords). Lane membership comes from
    // nodeToPartition (the lane's flowNodeRef), NOT from ELK — the move-nodes
    // pass pins each node's Y to that lane's band. ──
    interface ElkLeaf {
      id: string;
      x: number;
      y: number;
      width: number;
      height: number;
      laneIdx: number;
    }
    const leafNodes: ElkLeaf[] = [];
    const topChildren: ElkLayoutNode[] = layoutedGraph.children || [];
    topChildren.forEach((leaf: ElkLayoutNode) => {
      if (typeof leaf.id === "string" && leaf.id.startsWith("dummy_")) return;
      leafNodes.push({
        id: leaf.id,
        x: leaf.x,
        y: leaf.y,
        width: leaf.width,
        height: leaf.height,
        laneIdx: nodeToPartition.get(leaf.id) ?? 0
      });
    });
    console.log(`[DEBUG] elk.layout finished. leaf nodes: ${leafNodes.length}`);

    // Bounds over leaves, lane boxes, and edge route points (so the pool/offset
    // include everything ELK drew).
    let diagramMinX = Infinity,
      diagramMinY = Infinity,
      diagramMaxX = -Infinity,
      diagramMaxY = -Infinity;
    const accBounds = (x: number, y: number, w: number, h: number): void => {
      diagramMinX = Math.min(diagramMinX, x);
      diagramMinY = Math.min(diagramMinY, y);
      diagramMaxX = Math.max(diagramMaxX, x + w);
      diagramMaxY = Math.max(diagramMaxY, y + h);
    };
    leafNodes.forEach((n: ElkLeaf) => accBounds(n.x, n.y, n.width, n.height));
    (layoutedGraph.edges || []).forEach((ee) => {
      (ee.sections || []).forEach((s) => {
        if (s.startPoint) accBounds(s.startPoint.x, s.startPoint.y, 0, 0);
        if (s.endPoint) accBounds(s.endPoint.x, s.endPoint.y, 0, 0);
        (s.bendPoints || []).forEach((b) => accBounds(b.x, b.y, 0, 0));
      });
    });
    if (!Number.isFinite(diagramMinX)) {
      diagramMinX = 0;
      diagramMinY = 0;
      diagramMaxX = 0;
      diagramMaxY = 0;
    }
    console.log(
      `[DEBUG] Diagram bounds: MinX=${diagramMinX}, MinY=${diagramMinY}, MaxX=${diagramMaxX}, MaxY=${diagramMaxY}`
    );

    const PADDING = 80,
      POOL_X = 150,
      POOL_Y = 150,
      POOL_HEADER_W = 30;
    // ONE uniform ELK→canvas transform for nodes AND edges — this is what keeps
    // ELK's orthogonal routes aligned with the nodes (no arrow-over-node).
    const OFFSET_X = POOL_X + POOL_HEADER_W + PADDING - diagramMinX;
    const OFFSET_Y = POOL_Y + PADDING - diagramMinY;
    const tx = (x: number): number => x + OFFSET_X;
    const ty = (y: number): number => y + OFFSET_Y;
    const finalPoolWidth = Math.max(diagramMaxX - diagramMinX + 2 * PADDING + POOL_HEADER_W, 1200);
    const finalPoolHeight = Math.max(diagramMaxY - diagramMinY + 2 * PADDING, 200);

    // ── Lane bands. multi-lane: each band is sized to its tallest node PLUS a
    // clear routing corridor above and below that node row (LANE_CORRIDOR), so
    // the channel router can run edges between node rows and around columns
    // without ever crossing a node (KOA-17362 — the old even-split floored
    // every band to a cramped 120px ≈ node+20, leaving NO corridor, so arrows
    // cut straight through nodes). Bands tile the pool body contiguously and
    // the pool grows DOWN to fit them (the user's "lane'i dikey büyüt" — extra
    // vertical room is exactly what gives the router clean channels).
    // single/no-lane: one band spanning the whole ELK-sized pool (node Y comes
    // from ELK there, not from a band). ──
    const MIN_LANE_BAND_HEIGHT = 120;
    const LANE_CORRIDOR = 70;
    const poolTopY = POOL_Y;
    const laneMaxNodeH = new Map<number, number>();
    leafNodes.forEach((n: ElkLeaf) => {
      const cur: number = laneMaxNodeH.get(n.laneIdx) ?? 0;
      if (n.height > cur) laneMaxNodeH.set(n.laneIdx, n.height);
    });
    const bandHeightFor = (laneIdx: number): number => {
      const nodeH: number = laneMaxNodeH.get(laneIdx) ?? 80;
      return Math.max(MIN_LANE_BAND_HEIGHT, nodeH + 2 * LANE_CORRIDOR);
    };
    const laneBoundaryY: number[] = [poolTopY];
    let poolBottomY: number;
    if (multiLane) {
      for (let i = 0; i < lanesList.length; i++) {
        laneBoundaryY.push(laneBoundaryY[i] + bandHeightFor(i));
      }
      poolBottomY = laneBoundaryY[laneBoundaryY.length - 1];
    } else {
      poolBottomY = POOL_Y + finalPoolHeight;
      laneBoundaryY.push(poolBottomY);
    }
    const finalPoolHeightAdjusted = poolBodyHeight(multiLane, poolBottomY - POOL_Y, finalPoolHeight);
    console.log(`[DEBUG] finalPoolWidth: ${finalPoolWidth}, finalPoolHeight: ${finalPoolHeightAdjusted}`);

    const pool = allElements.find((e: any) => e.type === "bpmn:Participant");
    currentStage = "resize-pool";
    if (pool) {
      modeling.resizeShape(pool, { x: POOL_X, y: POOL_Y, width: finalPoolWidth, height: finalPoolHeightAdjusted });
    }

    lanesList.forEach((lane: any, i: number) => {
      const yTop = laneBoundaryY[i];
      const h = (laneBoundaryY[i + 1] ?? yTop + MIN_LANE_BAND_HEIGHT) - yTop;
      console.log(`[DEBUG] Resizing lane ${i} to Y: ${Math.round(yTop)}, Height: ${Math.round(h)}`);
      currentStage = `resize-lane-${i}`;
      modeling.resizeShape(lane, {
        x: POOL_X + POOL_HEADER_W,
        y: yTop,
        width: finalPoolWidth - POOL_HEADER_W,
        height: h
      });
    });

    // ── Move nodes: X from ELK (flow order); for multi-lane, Y is PINNED to the
    // node's lane band (the user's hard rule — every node in its lane), and
    // same-lane nodes ELK left at ≈same X are spread apart (resolveLaneRowCollisions)
    // so no two overlap. Single-/no-lane keeps the uniform ELK Y. ──
    currentStage = "move-nodes";
    const laneRowX: Map<string, number> = multiLane
      ? resolveLaneRowCollisions(
          leafNodes.map((n: ElkLeaf) => ({ id: n.id, x: tx(n.x), width: n.width, lane: n.laneIdx })),
          80
        )
      : new Map<string, number>();
    leafNodes.forEach((n: ElkLeaf) => {
      const shape = elementRegistry.get(n.id);
      if (!shape) {
        console.log(`[DEBUG] ERROR: Shape not found in registry for ${n.id}`);
        return;
      }
      const targetX: number = multiLane ? (laneRowX.get(n.id) ?? tx(n.x)) : tx(n.x);
      let targetY: number;
      if (multiLane) {
        const bandTop: number = laneBoundaryY[n.laneIdx] ?? POOL_Y;
        const bandBottom: number = laneBoundaryY[n.laneIdx + 1] ?? bandTop + MIN_LANE_BAND_HEIGHT;
        targetY = bandTop + Math.max(10, (bandBottom - bandTop - (shape.height || 80)) / 2);
      } else {
        targetY = ty(n.y);
      }
      currentStage = `move-${n.id}`;
      modeling.moveElements([shape], { x: targetX - shape.x, y: targetY - shape.y }, shape.parent);
    });

    // ── Happy Path phase columns. Layout has just decided where every step sits, which is
    // also the moment the DECLARED phases (`happyPathDefinition` + each step's `happyPath`)
    // and the DERIVED ones (the column containing a step's centre) can be made to agree.
    // Re-tiled here rather than left to the interceptor: the interceptor derives FROM
    // geometry, so after a layout it would happily read back a phase nobody declared. ──
    currentStage = "retile-phases";
    applyPhaseColumnLayout(
      sdInstance,
      modeler,
      {
        x: POOL_X + POOL_HEADER_W,
        width: finalPoolWidth - POOL_HEADER_W,
        y: POOL_Y,
        height: finalPoolHeightAdjusted
      },
      phasesBefore
    );

    // Diagnostics: final node rects + node-overlap detection.
    const finalRects: { id: string; lane: number; x: number; y: number; w: number; h: number }[] = [];
    leafNodes.forEach((n: ElkLeaf) => {
      const s = elementRegistry.get(n.id);
      if (!s) return;
      finalRects.push({ id: n.id, lane: n.laneIdx, x: s.x, y: s.y, w: s.width || 0, h: s.height || 0 });
      console.log(
        `[LAYOUT-NODE] ${n.id} lane=${n.laneIdx} x=${Math.round(s.x)} y=${Math.round(s.y)} w=${s.width} h=${s.height}`
      );
    });
    let nodeOverlapCount = 0;
    for (let a = 0; a < finalRects.length; a++) {
      for (let b = a + 1; b < finalRects.length; b++) {
        const ra = finalRects[a];
        const rb = finalRects[b];
        if (ra.x < rb.x + rb.w && ra.x + ra.w > rb.x && ra.y < rb.y + rb.h && ra.y + ra.h > rb.y) {
          nodeOverlapCount++;
          console.log(`[LAYOUT-OVERLAP] ${ra.id} (lane ${ra.lane}) <-> ${rb.id} (lane ${rb.lane})`);
        }
      }
    }
    console.log(`[LAYOUT-INSTR] node overlaps after placement: ${nodeOverlapCount}`);

    // ── Route edges. multi-lane: nodes were pinned to lane-band Y (off ELK's
    // Y), so ELK's section waypoints no longer line up — route each edge
    // (forward AND back-edge/loop) through the empty channels the taller bands
    // opened up (`routeChannelEdge`: exit RIGHT → clear column gap → clear
    // inter-band corridor → clear column gap → enter LEFT), guaranteed
    // node-clean; bpmn layoutConnection only when no clean channel exists.
    // single-/no-lane: reuse ELK's collision-aware sections verbatim. ──
    console.log("[DEBUG] Applying ELK edge routes...");
    currentStage = "edge-routing";
    const elkEdgeById = new Map<
      string,
      {
        sections?: {
          startPoint?: { x: number; y: number };
          endPoint?: { x: number; y: number };
          bendPoints?: { x: number; y: number }[];
        }[];
      }
    >();
    for (const ee of layoutedGraph.edges || []) {
      elkEdgeById.set(ee.id, ee);
    }
    const dedupeWaypoints = (pts: { x: number; y: number }[]): { x: number; y: number }[] => {
      const out: { x: number; y: number }[] = [];
      for (const p of pts) {
        const last = out[out.length - 1];
        if (!last || Math.abs(last.x - p.x) > 0.5 || Math.abs(last.y - p.y) > 0.5) out.push(p);
      }
      return out;
    };
    const elkRoutedIds = new Set<string>();
    edges.forEach((e: any, edgeIdx: number) => {
      const conn = elementRegistry.get(e.id);
      if (!conn) return;
      currentStage = `edge-${e.id}`;
      if (multiLane) {
        const s = conn.source;
        const t = conn.target;
        let routed = false;
        if (s && t && typeof s.x === "number" && typeof t.x === "number") {
          const srcRect: LaneEdgeRect = { x: s.x, y: s.y, width: s.width || 0, height: s.height || 0 };
          const tgtRect: LaneEdgeRect = { x: t.x, y: t.y, width: t.width || 0, height: t.height || 0 };
          // Every OTHER node is an obstacle the route must avoid; the route may
          // dock into its own source/target, so they're excluded.
          const obstacles: LaneEdgeRect[] = finalRects
            .filter((r) => r.id !== s.id && r.id !== t.id)
            .map((r) => ({ x: r.x, y: r.y, width: r.w, height: r.h }));
          const edgeMidY: number = (srcRect.y + srcRect.height / 2 + (tgtRect.y + tgtRect.height / 2)) / 2;
          // Per-edge stagger keeps parallel edges off the same corridor/column
          // line (so arrows don't overlap). |stagger| < LANE_CORRIDOR, so a
          // staggered corridor line stays inside the inter-band gap (clean).
          const stagger: number = ((edgeIdx % 5) - 2) * 16; // -32..+32
          const corridorYs: number[] = laneBoundaryY
            .map((b: number) => b + stagger)
            .sort((a: number, b2: number) => Math.abs(a - edgeMidY) - Math.abs(b2 - edgeMidY));
          const xJitter: number = (edgeIdx % 3) * 8;
          const exitXs: number[] = [25, 40, 55, 70, 90].map((d: number) => srcRect.x + srcRect.width + d + xJitter);
          const entryXs: number[] = [25, 40, 55, 70, 90].map((d: number) => tgtRect.x - d - xJitter);
          const wp: Point2D[] | null = routeChannelEdge(srcRect, tgtRect, obstacles, corridorYs, exitXs, entryXs);
          if (wp) {
            const clean = dedupeWaypoints(wp);
            if (clean.length >= 2) {
              try {
                modeling.updateWaypoints(conn, clean);
                routed = true;
              } catch (err) {
                console.warn(`[DEBUG] lane edge updateWaypoints failed for ${e.id}:`, err);
              }
            }
          }
        }
        if (!routed) {
          try {
            modeling.layoutConnection(conn);
          } catch (err) {
            console.warn(`[DEBUG] layoutConnection failed for ${e.id}:`, err);
          }
        }
        elkRoutedIds.add(e.id);
        return;
      }
      const ee = elkEdgeById.get(e.id);
      const pts: { x: number; y: number }[] = [];
      for (const s of ee?.sections || []) {
        if (s.startPoint) pts.push({ x: tx(s.startPoint.x), y: ty(s.startPoint.y) });
        for (const b of s.bendPoints || []) pts.push({ x: tx(b.x), y: ty(b.y) });
        if (s.endPoint) pts.push({ x: tx(s.endPoint.x), y: ty(s.endPoint.y) });
      }
      const clean = dedupeWaypoints(pts);
      if (clean.length >= 2) {
        modeling.updateWaypoints(conn, clean);
        elkRoutedIds.add(e.id);
      } else {
        try {
          modeling.layoutConnection(conn);
        } catch (err) {
          console.warn(`[DEBUG] layoutConnection fallback failed for ${e.id}:`, err);
        }
      }
    });

    // Edges ELK didn't route — boundary-event outgoing flows (the boundary is
    // attached to its host, not an ELK node) plus any stragglers. Let bpmn.js
    // compute a clean route from the now-final endpoint positions.
    currentStage = "non-elk-edge-routing";
    const nonElkFlows = elementRegistry.filter((e: any) => e.type === "bpmn:SequenceFlow" && e.source && e.target);

    // Route a boundary-event outgoing flow AROUND its host instead of letting
    // bpmn.js take the shortest path — which, for a bottom-attached timer
    // whose target sits at host level, cuts straight up through the host body
    // (the reported overlap). Kuika always attaches timer boundaries at the
    // host's bottom edge, so exit downward, run horizontally clear of the
    // host, then dock vertically into the target. Returns false (→ fall back
    // to layoutConnection) for any non-bottom attachment.
    const BND_GAP = 45;
    const routeBoundaryFlow = (conn: any): boolean => {
      const be = conn.source;
      const host = be?.host;
      const tgt = conn.target;
      if (!host || !tgt || be.x === undefined || host.x === undefined || tgt.x === undefined) return false;
      const hostBottom = host.y + host.height;
      const beCenterY = be.y + be.height / 2;
      // Only handle bottom-attached boundaries (center at/below the host bottom).
      if (beCenterY < hostBottom - be.height / 2) return false;
      const bcx = be.x + be.width / 2;
      const exitY = hostBottom + BND_GAP;
      const tcx = tgt.x + tgt.width / 2;
      const tgtCenterY = tgt.y + tgt.height / 2;
      // Dock into the target's bottom if it sits above the exit line, else its top.
      const dockY = tgtCenterY <= exitY ? tgt.y + tgt.height : tgt.y;
      const wp = dedupeWaypoints([
        { x: bcx, y: hostBottom },
        { x: bcx, y: exitY },
        { x: tcx, y: exitY },
        { x: tcx, y: dockY }
      ]);
      if (wp.length < 2) return false;
      modeling.updateWaypoints(conn, wp);
      return true;
    };

    nonElkFlows.forEach((conn: any) => {
      if (elkRoutedIds.has(conn.id)) return;
      currentStage = `non-elk-edge-${conn.id}`;
      if (conn.source?.type === "bpmn:BoundaryEvent" && routeBoundaryFlow(conn)) return;
      try {
        modeling.layoutConnection(conn);
      } catch (err) {
        console.warn(`[DEBUG] layoutConnection failed for ${conn.id}:`, err);
      }
    });

    // ── LAYOUT INSTRUMENTATION — edge-vs-node crossings (rule 3) ─────────
    // Node placement guarantees 0 node-node overlaps; this quantifies the
    // remaining "an arrow crosses an element" problem (KOA-17362) so a
    // routing fix can be validated against `edges crossing nodes: 0` instead
    // of eyeballing a screenshot. Reads each flow's FINAL waypoints, tests
    // every segment against every node box (Liang–Barsky, 2px inset), and
    // skips the flow's own source/target so docking into an endpoint never
    // counts as a crossing.
    currentStage = "edge-crossing-snapshot";
    const obstacleRects: { id: string; rect: LaneEdgeRect }[] = finalRects.map((r) => ({
      id: r.id,
      rect: { x: r.x, y: r.y, width: r.w, height: r.h }
    }));
    const allSeqFlows = elementRegistry.filter((e: any) => e.type === "bpmn:SequenceFlow") as {
      id: string;
      source?: { id: string };
      target?: { id: string };
      waypoints?: { x: number; y: number }[];
    }[];
    let edgeCrossingCount = 0;
    for (const flow of allSeqFlows) {
      const pts: { x: number; y: number }[] = flow.waypoints || [];
      if (pts.length < 2) continue;
      const srcId: string | undefined = flow.source?.id;
      const tgtId: string | undefined = flow.target?.id;
      const crossed: Set<string> = new Set<string>();
      for (let i = 0; i + 1 < pts.length; i++) {
        for (const ob of obstacleRects) {
          if (ob.id === srcId || ob.id === tgtId || crossed.has(ob.id)) continue;
          if (segmentIntersectsRect(pts[i], pts[i + 1], ob.rect, 2)) {
            crossed.add(ob.id);
            console.log(`[LAYOUT-EDGE-OVERLAP] flow ${flow.id} crosses node ${ob.id}`);
          }
        }
      }
      if (crossed.size > 0) edgeCrossingCount++;
    }
    console.log(`[LAYOUT-INSTR] edges crossing nodes: ${edgeCrossingCount}`);

    // ── Reposition sequence-flow LABELS to their edge midpoint ──────────
    // `updateWaypoints`/`layoutConnection` re-route the edge but never move its
    // external label shape, so a labeled flow keeps its STALE label position —
    // which overlapped the END node here (KOA-17362: gateway→END "Diğer
    // Durumlar" default label on top of END). Center each label at its
    // polyline midpoint, lifted LABEL_LIFT px above the line. Labels are not
    // obstacles, so this never affects node placement or edge routing.
    currentStage = "label-reposition";
    const LABEL_LIFT = 14;
    const labeledFlows = elementRegistry.filter((e: any) => e.type === "bpmn:SequenceFlow") as {
      id: string;
      waypoints?: { x: number; y: number }[];
      label?: { x: number; y: number; width: number; height: number };
    }[];
    for (const flow of labeledFlows) {
      const label = flow.label;
      if (!label || typeof label.x !== "number" || typeof label.width !== "number") continue;
      const pts: { x: number; y: number }[] = flow.waypoints || [];
      if (pts.length < 2) continue;
      const mid = polylineMidpoint(pts);
      const dx: number = mid.x - label.width / 2 - label.x;
      const dy: number = mid.y - LABEL_LIFT - label.height / 2 - label.y;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;
      currentStage = `label-${flow.id}`;
      try {
        modeling.moveShape(label, { x: dx, y: dy });
        console.log(`[LAYOUT-LABEL] flow ${flow.id} label → mid (${Math.round(mid.x)},${Math.round(mid.y)})`);
      } catch (err) {
        console.warn(`[DEBUG] label move failed for ${flow.id}:`, err);
      }
    }

    currentStage = "adoptCanvasIntoModel";
    const wp = (window as any).getWp?.(sdInstance);
    adoptCanvasIntoModel(wp);

    // ── FORCE CANVAS REPAINT ────────────────────────────────────────────
    // On large diagrams bpmn.js doesn't always repaint after a batch of
    // moveElements/resizeShape calls — the user has to switch screens and
    // come back to see the new layout. Force a redraw by:
    // 1. Firing canvas.resized so the canvas recomputes its viewbox
    // 2. Re-setting the viewbox to itself (no-op value change that triggers
    //    the SVG re-render pipeline)
    // 3. Firing elements.changed for all moved nodes so each shape repaints
    currentStage = "force-repaint";
    try {
      const canvas = modeler.get("canvas") as {
        viewbox: (vb?: { x: number; y: number; width: number; height: number }) => {
          x: number;
          y: number;
          width: number;
          height: number;
        };
        zoom: (level?: number | string) => number;
      };
      eventBus.fire("canvas.resized");
      const vb = canvas.viewbox();
      canvas.viewbox({ x: vb.x, y: vb.y, width: vb.width, height: vb.height });
      // elements.changed forces bpmn.js to re-render each shape/connection
      const allChanged = elementRegistry
        .getAll()
        .filter((e: { type?: string }) => e.type !== "bpmn:Process" && e.type !== "bpmn:Collaboration");
      eventBus.fire("elements.changed", { elements: allChanged });
      console.log("[DEBUG] Canvas repaint forced.");
    } catch (repaintErr) {
      console.warn("[DEBUG] Force-repaint step failed (non-fatal):", repaintErr);
    }

    // ── LAYOUT INSTRUMENTATION — final summary ─────────────────────────
    currentStage = "post-layout-snapshot";
    const postFlows = elementRegistry.filter((e: any) => e.type === "bpmn:SequenceFlow") as {
      id: string;
    }[];
    const postIds = new Set(postFlows.map((f) => f.id));
    const missingIds: string[] = [];
    for (const id of preFlowMap.keys()) {
      if (!postIds.has(id)) missingIds.push(id);
    }
    if (missingIds.length > 0) {
      console.error(`[LAYOUT-INSTR] Post-layout: ${missingIds.length} flow(s) missing from pre-layout snapshot:`);
      for (const id of missingIds) {
        const info = preFlowMap.get(id);
        const deleted = deleteLog.find((d) => d.id === id);
        console.error(
          `  - ${id} (${info?.source} -> ${info?.target}, name="${info?.name || ""}")` +
            (deleted ? ` — deleted during stage "${deleted.stage}"` : " — NEVER caught by delete listener")
        );
      }
    } else {
      console.log(`[LAYOUT-INSTR] Post-layout: all ${preFlows.length} flows still present ✓`);
    }
    eventBus.off("commandStack.connection.delete.postExecute", onConnectionDelete);
    eventBus.off("commandStack.connection.reconnect.postExecute", onReconnect);
    eventBus.off("commandStack.connection.reconnectStart.postExecute", onReconnect);
    eventBus.off("commandStack.connection.reconnectEnd.postExecute", onReconnect);
    eventBus.off("commandStack.connection.create.postExecute", onConnectionCreate);
    eventBus.off("commandStack.elements.move.postExecute", suppressDropOnFlow);
    eventBus.off("commandStack.shape.create.postExecute", suppressDropOnFlow);

    console.log("[DEBUG] --- AUTO LAYOUT END ---");
    return { success: true };
  } catch (e: any) {
    console.error("[DEBUG] FATAL EXCEPTION:", e);
    return { error: e.message };
  }
}

(window as any).testAutoLayout = workflowAutoLayoutAction;

/**
 * What the diagram already says about the gateway a flagged flow belongs to
 * (KOA-18136 §35). Without this the remediation hint is FLOW-SCOPED and can
 * contradict itself across siblings — see `buildGatewayConditionHint`.
 */
export interface GatewayBranchFacts {
  /** Flow already marked as this gateway's default, if any. */
  defaultFlowId?: string;
  /** Logical parameter names a condition can name, best candidates first. */
  conditionParams: string[];
}

/**
 * KOA-18136 §35 — the remediation hint must know the GATEWAY, not just the flow.
 *
 * A gateway can hold exactly ONE default branch. The old hint was written per
 * flow and offered "mark this one DEFAULT" to every uncovered sibling, so on a
 * two-branch gateway the two hints CANCELLED EACH OTHER: mark A and B loses its
 * cover; mark B and A loses its. Live session d36d78e2: the builder followed
 * that advice literally for 21 turns, moving the same default back and forth
 * across four gateways, never once trying a condition — the lint never cleared,
 * the process was never accepted, and no task screen was ever generated.
 *
 * Two laws:
 *  1. If the gateway ALREADY has a default branch, "mark this DEFAULT" is not an
 *     option at all — it would steal the cover from the sibling. Only a condition
 *     can fix this flow, so only a condition is offered.
 *  2. The condition option is a READY command, not a template. The workflow's own
 *     logical parameters are in hand; naming one is strictly better than asking
 *     the author to go find one (`<existingParam>` is what pushed the weaker
 *     model toward the copy-pasteable DEFAULT every single time).
 */
export function buildGatewayConditionHint(
  flowId: string,
  gatewayRef: string,
  gatewayKind: string,
  facts: GatewayBranchFacts
): string {
  const param: string = facts.conditionParams[0] ?? "";
  const conditionCmd: string = param
    ? `CONDITION ${flowId} WHEN ${param} == true`
    : `CONDITION ${flowId} WHEN <existingParam> <op> <value>`;
  const paramNote: string = param
    ? ` This workflow's decision parameters: ${facts.conditionParams.slice(0, 6).join(", ")} — use the one this branch actually depends on.`
    : ` The parameter must already exist; if it does not, create it FIRST with \`PARAM INTERNAL <name> Boolean = false\` in the same script.`;

  if (facts.defaultFlowId && facts.defaultFlowId !== flowId) {
    return (
      `SequenceFlow ${flowId} is a non-default path from an ${gatewayKind} Gateway (source: ${gatewayRef}) and needs a WHEN condition.\n` +
      `  \`${conditionCmd}\`${paramNote}\n` +
      `Do NOT mark this flow DEFAULT: ${gatewayRef} already has its default branch (${facts.defaultFlowId}), a gateway can only have ONE, ` +
      `and moving it here would leave ${facts.defaultFlowId} uncovered — the same error on a different flow. A condition is the only fix for this one.`
    );
  }

  return (
    `SequenceFlow ${flowId} is a non-default path from an ${gatewayKind} Gateway (source: ${gatewayRef}) and needs either a WHEN condition OR the default marker. ` +
    `${gatewayRef} has NO default branch yet, so exactly ONE of its flows may take it — pick per branch meaning:\n` +
    `  A) This branch runs on a condition: \`${conditionCmd}\`${paramNote}\n` +
    `  B) This branch is the fallback/else: \`DEFAULT ${gatewayRef} ${flowId}\` — USE THIS EXACT GATEWAY ID, do not substitute another. ` +
    `Only one flow of this gateway may be the fallback; every other flow needs its own condition.`
  );
}

/**
 * Map a lint rule + element context to a concrete remediation DSL snippet
 * the LLM can apply. Without this, LLMs tend to escalate trivial fixes
 * (e.g. rebuilding a gateway subgraph instead of adding one missing CONNECT).
 */
function getLintRemediationHint(
  ruleName: string,
  element: { id: string; type?: string; sourceId?: string; targetId?: string } | undefined,
  message: string,
  gatewayFacts?: GatewayBranchFacts
): string | undefined {
  const type = element?.type || "";
  const id = element?.id || "<elementId>";

  if (ruleName.includes("missing-incoming-or-outgoing")) {
    if (type === "bpmn:StartEvent") {
      return `Add \`CONNECT ${id} -> <firstTaskId>\` where <firstTaskId> is the task that should follow Start. Do NOT rebuild other parts of the diagram; the START flow is the only missing piece.`;
    }
    if (type === "bpmn:EndEvent") {
      return `Add \`CONNECT <lastTaskId> -> ${id}\` where <lastTaskId> is the final task in the flow.`;
    }
    if (type.includes("Gateway")) {
      return `Gateway ${id} needs ≥1 incoming + ≥1 outgoing. Add \`CONNECT <upstreamNode> -> ${id}\` for incoming and \`PATH ${id} -> <target> [WHEN ...|DEFAULT]\` for outgoing.`;
    }
    return `Task ${id} needs both incoming and outgoing. Use CONNECT to wire it into the main flow, or DELETE it if obsolete.`;
  }

  if (ruleName.includes("no-isolated-flow-nodes")) {
    return `Element ${id} is disconnected. Either CONNECT/PATH it into the main flow, or DELETE ${id} if it's obsolete. Most common cause: a node was created but never linked.`;
  }

  if (ruleName.includes("gateway-conditions")) {
    // Offer two concrete options and tell the LLM how to pick.
    // If the workflow has NO parameters yet, CONDITION will fail — so the
    // DEFAULT option is safer as a fallback.
    // CRITICAL: substitute the real parent gateway ID, not a <placeholder>.
    // LLMs sometimes hallucinate gateway IDs when told "<gatewayId>".
    const gatewayRef = element?.sourceId || "<gatewayId>";
    // Infer gateway kind from the lint message wording so the hint matches
    // the actual gateway type (Exclusive vs Inclusive).
    const gatewayKind = message.includes("Inclusive") ? "Inclusive" : "Exclusive";
    // §35 — gateway-scoped, so two siblings never get advice that cancels out.
    return buildGatewayConditionHint(id, gatewayRef, gatewayKind, gatewayFacts ?? { conditionParams: [] });
  }

  if (ruleName.includes("gateway-join")) {
    return `Parallel/Inclusive gateways come in pairs. Add a matching PJOIN/IJOIN gateway to close the fork.`;
  }

  if (ruleName.includes("gateway-min-outgoing") || message.includes("single outgoing")) {
    return `Gateway ${id} has only one outgoing path. PREFERRED FIX: add the MISSING branch — a decision needs ≥2 outgoing PATHs (e.g. an approval gateway needs both the approve AND the reject branch: \`PATH ${id} -> <otherTarget> "<label>" WHEN ... | DEFAULT\`). If the gateway is mid-build and its second branch just isn't wired yet, WIRE IT — do NOT delete it. Only DELETE ${id} (and CONNECT its predecessor straight to its single target) when the gateway is genuinely redundant — i.e. there is truly no second outcome. Never delete a NAMED decision gateway (e.g. "...Onayı Sonucu", "...Uygun mu?", "Karar") to clear this warning — that erases a decision the process needs.`;
  }

  if (ruleName.includes("single-outgoing-non-gateway")) {
    const subject = type === "bpmn:StartEvent" ? `Start event ${id}` : `${id}`;
    return `${subject} has more than one outgoing flow — only a Gateway may branch. Keep exactly ONE outgoing: remove the extra outgoing flow(s) with \`DELETE <flowId>\` (LIST to find the duplicate flow ids), OR if a real branch is intended, route through a Gateway (insert an XGATEWAY and PATH from it) instead of forking directly from ${id}.`;
  }

  if (ruleName.includes("gateway-duplicate-condition")) {
    return `Flow ${id} repeats a condition already used by another branch of the same gateway — on an exclusive gateway the second one never runs. \`DELETE ${id}\` (you likely modeled the same branch twice — e.g. once to a task and once straight to End), or give it a DISTINCT condition via \`CONDITION ${id} WHEN <param> <op> <different value>\`.`;
  }

  if (ruleName.includes("start-end-required")) {
    return `The diagram needs both a StartEvent and an EndEvent. Do not DELETE StartEvent_1 or EndEvent_1.`;
  }

  if (ruleName.includes("steps-required")) {
    return `Workflow must have at least one Task between Start and End.`;
  }

  if (ruleName.includes("label-required")) {
    return `Element ${id} needs a non-empty name. Use \`RENAME ${id} "<new name>"\`.`;
  }

  if (ruleName.includes("mi-instance-config")) {
    return `Multi-instance node ${id} has incomplete MI configuration. Use \`MI ${id} <PMI|SMI> [count:<N>] [collection:<Manual|Assignment>]\`.`;
  }

  if (ruleName.includes("step-output-unbound")) {
    return `System step ${id} maps output parameter(s) with no VALUE SOURCE, so they generate as an empty string and every later step keyed on them reads nothing. Re-issue the mapping with the source: \`MAP_OUTPUT ${id} "<paramName>" FROM <actionName>.<resultField>\` — <actionName> is the action attached to this step (e.g. the insert whose new row id you need) and <resultField> is the field of its result (e.g. \`Id\`). Do NOT delete the parameter to clear this; the process needs the value.`;
  }

  if (ruleName.includes("step-action-input-unbound")) {
    // KOA-18448 — the DSL form lives HERE, not in the rule's message: the message is what the
    // human reads in the Designer's lint panel, where a script command means nothing.
    return `System step ${id} runs an action whose required input(s) have NO value, so every run writes those columns empty (live \`44125266\`: 59 of them). Give each one a source: \`MAP_INPUT ${id} "<inputName>" <workflowParamName>\` for a value this process carries, or \`MAP_INPUT ${id} "<inputName>" "<literal>"\` for a genuine constant. If the value does not exist yet, declare it FIRST in the same script — \`PARAM BO <name> <Type> INPUT\` when the initiator supplies it, \`PARAM INTERNAL <name> <Type>\` when an earlier step produces it (then MAP_OUTPUT it on that step). Do NOT delete the input or the step to clear this: the action's own signature declares it, so removing it here only hides that the column ships empty.`;
  }

  if (ruleName.includes("system-step-has-action")) {
    // KOA-17362 §24 — the wrong exit must be named: the old hint read as
    // "attach something (or else delete)", and under that pressure a live build
    // attached `ChargePaymentMethod` — a Stripe CARD CHARGE — to a step meant to
    // REIMBURSE an employee, purely to clear the error. A semantically wrong
    // action is worse than no step: it runs.
    //
    // KOA-18136 C — the OTHER exit turned out to be just as available. Live
    // smoke 21 deleted the process's four persistence steps because the actions
    // it needed did not resolve (a stale browser catalog, not missing actions),
    // and the build then passed LINT and self-reported complete while inserting
    // nothing. So DELETE is no longer offered as an equal option: it is for a
    // step nobody asked for. A step that WAS asked for and has no action must be
    // left standing and reported — an unattached step is visible, a deleted one
    // is not. `workflowUnresolvedStepActionsFromRuns` reports the gap either way;
    // this text only stops teaching the delete.
    return `System step ${id} has no action, so it does nothing at runtime. Attach the action this step is FOR: \`STEP_ACTION ${id} "<actionName>"\` (then bind its inputs with MAP_INPUT). If the name you expect does not resolve, list the catalog again before concluding it is absent — an action created earlier in this same turn is real. NEVER attach a "close enough" action just to clear this error: an action whose description does not match what this step must do will EXECUTE at runtime and do the wrong thing (charging a card on a step meant to issue a refund, etc.). If NO catalog action genuinely performs this operation, LEAVE THE STEP IN PLACE, keep this warning, and say so in your summary — the user then sees which step needs an action. \`DELETE ${id}\` is only for a step the process does not need at all; deleting a step that was asked for silently removes the work and the build looks finished.`;
  }

  return undefined;
}

/**
 * Diagram elements that are NOT business flow nodes: the containers, the
 * pool/lane furniture, the annotations, and the Start/End pair every blank
 * template ships with. What remains — tasks, gateways, sub-processes,
 * intermediate/boundary events — is the process the author actually modelled.
 */
const NON_BUSINESS_ELEMENT_TYPES: ReadonlySet<string> = new Set<string>([
  "bpmn:Definitions",
  "bpmn:Process",
  "bpmn:Collaboration",
  "bpmn:Participant",
  "bpmn:Lane",
  "bpmn:LaneSet",
  "bpmn:StartEvent",
  "bpmn:EndEvent",
  "bpmn:TextAnnotation",
  "bpmn:Group",
  "bpmn:DataObjectReference",
  "bpmn:DataStoreReference",
  "bpmn:DataInput",
  "bpmn:DataOutput",
  "label"
]);

interface DiagramElementShape {
  readonly id?: string;
  readonly type?: string;
  readonly waypoints?: readonly unknown[];
  readonly labelTarget?: unknown;
}
interface ElementRegistryShape {
  getAll?: () => readonly DiagramElementShape[];
}

/**
 * Count the business flow nodes on the diagram. Used to tell an EMPTY process
 * apart from a valid one — lint alone cannot, because a graph with nothing in
 * it violates no rule (KOA-18136 §33).
 */
export function countBusinessFlowNodes(elementRegistry: ElementRegistryShape | undefined): number {
  const all: readonly DiagramElementShape[] =
    typeof elementRegistry?.getAll === "function" ? elementRegistry.getAll() : [];
  let count: number = 0;
  for (const element of all) {
    const type: string = typeof element.type === "string" ? element.type : "";
    if (!type.startsWith("bpmn:")) continue;
    // Connections carry waypoints; labels point at their owner. Neither is a node.
    if (Array.isArray(element.waypoints)) continue;
    if (element.labelTarget !== undefined && element.labelTarget !== null) continue;
    if (NON_BUSINESS_ELEMENT_TYPES.has(type)) continue;
    count++;
  }
  return count;
}

interface GatewayShape {
  readonly businessObject?: { default?: { id?: string } };
}

/**
 * Read the diagram + workflow for the facts a gateway-scoped hint needs
 * (KOA-18136 §35): which flow already holds this gateway's default, and which
 * logical parameters a condition could name.
 *
 * Decision parameters come first — a non-business-object Boolean is exactly the
 * approve/reject flag a branch condition tests — then any other Boolean.
 */
function readGatewayBranchFacts(
  elementRegistry: ElementRegistryShape | undefined,
  workflowParams: readonly IWFParameter[],
  gatewayId: string
): GatewayBranchFacts {
  let defaultFlowId: string | undefined;
  if (gatewayId && typeof elementRegistry?.getAll === "function") {
    for (const element of elementRegistry.getAll()) {
      if (element.id !== gatewayId) continue;
      const shape: GatewayShape = element as GatewayShape;
      const flowId: string | undefined = shape.businessObject?.default?.id;
      if (typeof flowId === "string" && flowId.length > 0) defaultFlowId = flowId;
      break;
    }
  }
  const params: readonly IWFParameter[] = workflowParams;
  const isBoolean = (p: IWFParameter): boolean => String(p.dataType).toLowerCase() === "boolean";
  const decisionFlags: string[] = params.filter((p) => isBoolean(p) && p.isBusinessObject !== true).map((p) => p.name);
  const otherBooleans: string[] = params.filter((p) => isBoolean(p) && p.isBusinessObject === true).map((p) => p.name);
  return {
    ...(defaultFlowId !== undefined ? { defaultFlowId } : {}),
    conditionParams: [...decisionFlags, ...otherBooleans]
  };
}

export async function workflowGetLintIssuesAction(sdInstance: ScreenDesignInstance, input: any = {}) {
  const modeler = (window as any).bpmnModeler;
  if (!modeler) {
    return { error: "BPMN Modeler is not ready." };
  }
  try {
    const linting = modeler.get("linting");
    const elementRegistry = modeler.get("elementRegistry");
    if (!linting) {
      return { error: "Linting module is not active or found." };
    }
    // §35 — read once: a gateway-flow hint names a real decision parameter
    // instead of a `<placeholder>` the weaker model will not go looking for.
    const lintWorkflowParams: readonly IWFParameter[] = collectWorkflowParamsUnion(
      sdInstance,
      getValues(sdInstance)
    ) as IWFParameter[];
    // bpmnlint's lint() returns results keyed by RULE NAME (e.g.
    // "kuika/no-isolated-flow-nodes"), with each value being an array of
    // reports: [{ id, message, category }, ...] where `id` is the actual
    // BPMN element ID. Group by real element ID so the LLM/user can see
    // exactly which element has which issue.
    const issuesMap = await linting.lint();
    interface LintIssueEntry {
      category: string;
      message: string;
      rule: string;
      hint?: string;
    }
    interface ElementIssueGroup {
      elementId: string;
      elementName: string;
      elementType: string;
      issues: LintIssueEntry[];
    }
    const byElement = new Map<string, ElementIssueGroup>();
    let totalErrors = 0;
    let totalWarnings = 0;

    for (const [ruleName, reports] of Object.entries(issuesMap || {})) {
      if (ruleName.includes("screen-assigned")) continue;
      for (const report of (reports as { id?: string; message?: string; category?: string; rule?: string }[]) || []) {
        if (report.rule === "screen-assigned" || (report.message && report.message.includes("screen assigned"))) {
          continue;
        }
        if (report.category === "error") totalErrors++;
        if (report.category === "warn") totalWarnings++;

        const realElementId = report.id || "__diagram__";
        let group = byElement.get(realElementId);
        if (!group) {
          const element = realElementId === "__diagram__" ? undefined : elementRegistry.get(realElementId);
          group = {
            elementId: realElementId,
            elementName: element?.businessObject?.name || realElementId,
            elementType: element?.type || (realElementId === "__diagram__" ? "Diagram" : "Unknown"),
            issues: []
          };
          byElement.set(realElementId, group);
        }
        let elementForHint: { id: string; type: string; sourceId?: string; targetId?: string } | undefined;
        if (realElementId !== "__diagram__") {
          const el = elementRegistry.get(realElementId) as
            | { source?: { id?: string }; target?: { id?: string } }
            | undefined;
          elementForHint = {
            id: realElementId,
            type: group.elementType,
            sourceId: el?.source?.id,
            targetId: el?.target?.id
          };
        }
        // §35 — a gateway-flow hint reads the gateway, not just the flow.
        const gatewayFacts: GatewayBranchFacts | undefined =
          elementForHint?.sourceId !== undefined
            ? readGatewayBranchFacts(elementRegistry, lintWorkflowParams, elementForHint.sourceId)
            : undefined;
        const hint = getLintRemediationHint(ruleName, elementForHint, report.message || "", gatewayFacts);
        const issue: LintIssueEntry = {
          category: report.category || "error",
          message: report.message || "",
          rule: ruleName
        };
        if (hint) issue.hint = hint;
        group.issues.push(issue);
      }
    }
    const formattedIssues = Array.from(byElement.values());
    // KOA-18136 §33 — an EMPTY process breaks no rule, so lint used to call it
    // "perfectly valid" and the builder read that as a finished build (live
    // session 947ce0d8: the script was rejected wholesale, the process stayed
    // empty, lint said clean, the turn ended). Zero flow nodes is the single
    // worst outcome there is; it must never be reported as success.
    const flowNodeCount: number = countBusinessFlowNodes(elementRegistry);
    const emptyGraph: boolean = flowNodeCount === 0;
    let message: string;
    if (emptyGraph) {
      message =
        "THE PROCESS IS EMPTY — 0 flow nodes on the diagram. Nothing has been built yet, " +
        "so there is nothing for lint to complain about. This is a FAILED build, not a clean one: " +
        "create the process (TASK / SYSTEM / XGATEWAY + their connections) before reporting completion.";
    } else if (totalErrors === 0 && totalWarnings === 0) {
      message = "No linting issues found. The diagram is perfectly valid.";
    } else {
      message = `Found ${totalErrors} errors and ${totalWarnings} warnings in the diagram.`;
    }
    return {
      success: true,
      totalErrors,
      totalWarnings,
      flowNodeCount,
      emptyGraph,
      elementsWithIssues: formattedIssues,
      message
    };
  } catch (error: any) {
    return { error: `Failed to retrieve lint issues: ${error?.message || error}` };
  }
}
